



import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { logoutUser } from "../authService";
import React from "react";
import TournamentsSection, {
  getTournamentStatusKeyForCard,
  getTournamentStatusVisualDefinition,
  tournamentStatusDotBaseStyle,
} from "../components/TournamentsSection";
import TopHeader from "../components/TopHeader";
import TopBar from "../components/TopBar";
import GamePage from "./GamePage";
import BottomBar from "../components/BottomBar";
import CopyrightFooter from "../components/CopyrightFooter";
import { httpsCallable } from "firebase/functions";
import { buildReportV2Request } from "../utils/reportingV2";
import {
  ADMIN_MESSAGE_BODY_MAX_LENGTH,
  ADMIN_MESSAGE_MAX_RECIPIENTS,
  ADMIN_MESSAGE_TITLE_MAX_LENGTH,
} from "../utils/adminMessageLimits";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import ShopPage from "../pages/ShopPage";
import VouchersPage from "./VouchersPage";
import {
  CoinIcon,
  CommunityIcon,
  ConflictIcon,
  CrownIcon,
  EveningCupIcon,
  MessageIcon,
  NightlyCupIcon,
  ProfileIcon,
  QuestsIcon,
  SinglesIcon,
  SpecialCreatorCupIcon,
  TrophyIcon,
  TrustIcon,
} from "../components/icons";
import ownTournamentsIconUrl from "../assets/icons/owntournaments.svg";
import { ReactComponent as CalendarQuestIcon } from "../assets/icons/calender.svg";
import { ReactComponent as FireQuestIcon } from "../assets/icons/fire.svg";
import AgbPage from "../pages/AgbPage";
import DatenschutzPage from "../pages/DatenschutzPage";
import ImpressumPage from "../pages/ImpressumPage";
import FaqPage from "../pages/FaqPage";
import CompetitionTermsPage from "../pages/CompetitionTermsPage";
import CompetitionRulesView from "../components/CompetitionRulesView";
import TournamentResultModal from "../components/TournamentResultModal";
import SurveyModal from "../components/SurveyModal";
import {
  FaBan,
  FaClock,
  FaChevronRight,
  FaClipboardList,
  FaCloudUploadAlt,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle,
  FaHistory,
  FaInstagram,
  FaLock,
  FaRegUser,
  FaTimes,
  FaTiktok,
  FaTrashAlt,
  FaTrophy,
  FaUserFriends,
  FaUserPlus,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { auth, db, functions as firebaseFunctions } from "../firebase";
import { useLanguage } from "../i18n/LanguageProvider";
import { resetPassword } from "../authService";
import {
  isBiometricLoginEnabledForUser,
  isIosBiometricAuthSupportedPlatform,
  setBiometricLoginEnabledForUser,
} from "../services/biometricAuth";
import { hasAdminRole, isCreator } from "../utils/roles";
import { canOpenSurveyPrompt, isSurveyBlockedByMatchState } from "../utils/surveyPromptLifecycle";
import {
  getBracketSize,
  getTournamentTotalRounds,
  isSpecialCupTournament,
  isThirdPlaceEnabledTournament,
} from "../utils/tournamentBracket";
import { shouldShowPriority1Notification } from "../utils/notificationPriority";
import {
  getPresencePriorityCountdown,
  getPresencePriorityPopupStatus,
  selectPriorityNotificationPopup,
} from "../utils/priorityNotificationPopupEligibility";
import useAppActiveState from "../hooks/useAppActiveState";
import { getAccountProviderCapabilities } from "../utils/authProviders";
import { isNativeIosAppleAuthAvailable } from "../services/appleAuth";
import { deleteAccountWithProviderConfirmation } from "../services/providerAwareAccountDeletion";
import { requestProviderAwareDataExport } from "../services/providerAwareDataExport";
import { prefetchUsernames, seedUsername, seedUsernames } from "../services/usernameCache";
import { hasRequiredPlayerProfileFields } from "../utils/playerProfileRequirements";
import { buildTournamentRulesViewModel } from "../utils/competitionRules";
import {
  getDashboardLobbyMaxPlayers,
  getDashboardMaxPlayers,
} from "../utils/dashboardTournamentCapacity";
import {
  buildQuestRewards,
  shouldShowQuestRewardPrompt,
} from "../utils/questRewards";
import {
  areQuestRewardPromptSourcesReady,
  createQuestRewardPromptGuard,
  getQuestRewardPromptDisplay,
  getQuestRewardPromptName,
  isQuestRewardPromptBlockedByLocalGuard,
} from "../utils/questRewardPromptLifecycle";
import { classifyDailyBonusClaimResult } from "../utils/dailyBonusClaimResult";
import { consumePendingPushNavigation } from "../utils/pushNavigation";
import {
  closeMyPrizesNavigation,
  openMyPrizesNavigation,
} from "../utils/myPrizesNavigation";
import { selectCurrentTournamentMatch } from "../utils/tournamentCurrentMatch";
import { getTournamentSeriesState } from "../utils/tournamentSeriesState";
import {
  VAULTQUEST_AGE_GROUPS,
  canGrantAiConflictAnalysisConsent,
  hasValidAiConflictAnalysisConsent,
  shouldShowAiConflictAnalysisConsentPrompt,
} from "../utils/aiConflictConsent";
import {
  APP_REVIEW_MISSING_APP_STORE_ID,
  claimAppReviewPromptIfEligible,
  getCompletedMatchCountForAppReview,
  isNativeIosAppReviewAvailable,
  openVaultQuestAppStoreReviewPage,
  requestNativeAppReviewIfAvailable,
} from "../services/appReview";

import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  orderBy,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  setDoc,
  where,
  getDocs,
  limit,
} from "firebase/firestore";

const AdminPanel = React.lazy(() => import("../components/AdminPanel"));
const TournamentRunningView = React.lazy(() => import("../components/TournamentRunningView"));
const SingleGamesPage = React.lazy(() => import("./SingleGamesPage"));
const CreatorTournamentsPage = React.lazy(() => import("./CreatorTournamentsPage"));
const ProfilePage = React.lazy(() => import("./ProfilePage"));
const FairPlayCommunityPage = React.lazy(() => import("./FairPlayCommunityPage"));
const CommunityPage = React.lazy(() => import("./CommunityPage"));
const LeaderboardsPage = React.lazy(() => import("./LeaderboardsPage"));
const FriendsPage = React.lazy(() => import("./FriendsPage"));
const ReportBlockUserPage = React.lazy(() => import("./ReportBlockUserPage"));
const RewardsPage = React.lazy(() => import("./RewardsPage"));
const MyPrizesPage = React.lazy(() => import("./MyPrizesPage"));
const claimDailyBonus = httpsCallable(firebaseFunctions, "claimDailyBonus");

function sortTournamentMatches(items = []) {
  return [...items].sort((a, b) => {
    const roundA = Number(a?.roundNumber || 0);
    const roundB = Number(b?.roundNumber || 0);
    if (roundA !== roundB) return roundA - roundB;

    const indexA = Number(a?.matchIndex || 0);
    const indexB = Number(b?.matchIndex || 0);
    return indexA - indexB;
  });
}

const DASHBOARD_ACTIVE_MATCH_STATUSES = new Set([
  "check_in_open",
  "pending",
  "reported",
  "disputed",
  "resume_pending",
]);

const DASHBOARD_TERMINAL_TOURNAMENT_STATUSES = new Set([
  "finished",
  "completed",
  "cancelled",
  "canceled",
  "void",
  "deleted",
  "invalid",
  "not_started",
  "startet nicht",
]);

function renderQuestRewardPromptIcon(iconType, size = 48) {
  if (iconType === "single") return <SinglesIcon size={size} />;
  if (iconType === "trophy") return <TrophyIcon size={size} />;
  if (iconType === "fire") return <FireQuestIcon width={size} height={size} aria-hidden="true" focusable="false" />;
  if (iconType === "community") return <CommunityIcon size={size} />;
  if (iconType === "coin") return <CoinIcon size={size} />;
  if (iconType === "calendar") return <CalendarQuestIcon width={size} height={size} aria-hidden="true" focusable="false" />;
  if (iconType === "crown") return <CrownIcon size={size} />;
  return <QuestsIcon size={size} />;
}

function getNotificationPresentation(notification = {}) {
  const type = String(notification?.type || "").toLowerCase();
  if (type.includes("upload") || type.includes("deadline") || type.includes("expiring") || type.includes("timeout")) {
    return { tone: "warning", icon: <FaClock aria-hidden="true" /> };
  }
  if (type.includes("friend") || type.includes("community")) {
    return { tone: "info", icon: <CommunityIcon size="100%" /> };
  }
  if (type.includes("message") || type.includes("admin_message")) {
    return { tone: "info", icon: <MessageIcon size="100%" /> };
  }
  if (type.includes("quest")) {
    return { tone: "success", icon: <QuestsIcon size="100%" /> };
  }
  if (type.includes("coin") || type.includes("reward")) {
    return { tone: "success", icon: <CoinIcon size="100%" /> };
  }
  if (type.includes("win") || type.includes("tournament") || type.includes("qualified")) {
    return { tone: "success", icon: <TrophyIcon size="100%" /> };
  }
  if (type.includes("conflict") || type.includes("repeat") || type.includes("no_show") || type.includes("report")) {
    return { tone: "warning", icon: <ConflictIcon size="100%" /> };
  }
  if (type.includes("single") || type.includes("opponent") || type.includes("match")) {
    return { tone: "info", icon: <SinglesIcon size="100%" /> };
  }
  return { tone: "info", icon: <MessageIcon size="100%" /> };
}


const HOME_UPCOMING_CUP_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const HOME_UPCOMING_CUP_LIMIT = 3;
const HOME_UPCOMING_DAILY_PRIZE_DAYS = 9;
const VAULTQUEST_TIME_ZONE = "Europe/Berlin";

function getVaultQuestDateKeyFromMillis(millis = Date.now()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: VAULTQUEST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(millis));
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

function getDashboardDailyPrizeDateKeys(now = Date.now()) {
  return Array.from({ length: HOME_UPCOMING_DAILY_PRIZE_DAYS }, (_, index) =>
    getVaultQuestDateKeyFromMillis(now + index * 24 * 60 * 60 * 1000)
  );
}

function isActiveDashboardDailyPrizeSpecial(special) {
  if (!special || typeof special !== "object" || Array.isArray(special)) return false;
  if (Object.keys(special).length === 0) return false;
  if (special.enabled === false || special.active === false || special.disabled === true || special.deleted === true) {
    return false;
  }

  const status = String(special.status || "open").toLowerCase();
  return !DASHBOARD_TERMINAL_TOURNAMENT_STATUSES.has(status) &&
    !special.deletedAt &&
    !special.tournamentDeletedAt &&
    !special.removedAt;
}

function buildDashboardSpecialCupFromDailyPrize(dateKey, special) {
  if (!dateKey || !isActiveDashboardDailyPrizeSpecial(special)) return null;

  return {
    ...special,
    id: `${dateKey}_special`,
    joinDocId: special.joinDocId || `${dateKey}_special`,
    tournamentId: special.tournamentId || "special",
    type: "special",
    dateKey,
    name: special.name || special.title || "Special Cup",
    time: special.time || special.startTime || special.requestedStartTime || "20:00",
    maxPlayers: special.maxPlayers || 64,
    entryCoins: special.entryCoins || 500,
    status: special.status || "open",
    specialCup: true,
    additionalRulesType: special.additionalRulesType || "tournament",
  };
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  return 0;
}

function normalizeDashboardId(value) {
  return String(value || "").trim();
}

function getDashboardTournamentId(tournament = {}) {
  return normalizeDashboardId(
    tournament.id ||
      tournament.tournamentId ||
      tournament.requestId ||
      tournament.uid ||
      ""
  );
}

function getDashboardTournamentStartMillis(tournament = {}) {
  const direct =
    timestampToMillis(tournament.startAt) ||
    timestampToMillis(tournament.scheduledStartAt) ||
    timestampToMillis(tournament.startsAt) ||
    timestampToMillis(tournament.startDateTime);
  if (direct) return direct;

  const dateKey = String(
    tournament.dateKey ||
      tournament.date ||
      tournament.startDate ||
      getDashboardTournamentId(tournament).split("_")[0] ||
      ""
  ).trim();
  const startTime = String(tournament.startTime || tournament.time || "").trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateKey) && /^\d{1,2}:\d{2}/.test(startTime)) {
    const [hours, minutes] = startTime.split(":").map((part) => Number(part));
    const date = new Date(dateKey);
    date.setHours(Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, 0, 0);
    return date.getTime();
  }

  return 0;
}

function tournamentIncludesUid(tournament = {}, uid = "") {
  const target = String(uid || "");
  if (!target) return false;

  const fromArray = (items) =>
    Array.isArray(items) &&
    items.some((item) => {
      if (typeof item === "string") return item === target;
      if (!item || typeof item !== "object") return false;
      return [item.uid, item.userId, item.id, item.playerUid].map(String).includes(target);
    });

  const fromMap = (map) =>
    map &&
    typeof map === "object" &&
    !Array.isArray(map) &&
    Object.keys(map).includes(target);

  return (
    fromArray(tournament.players) ||
    fromArray(tournament.playerUids) ||
    fromArray(tournament.participants) ||
    fromArray(tournament.participantUids) ||
    fromArray(tournament.registeredPlayerUids) ||
    fromMap(tournament.participants) ||
    fromMap(tournament.playerMap)
  );
}

function isDashboardActiveMatch(match = {}, uid = "") {
  const status = String(match.status || "").toLowerCase();
  return (
    DASHBOARD_ACTIVE_MATCH_STATUSES.has(status) &&
    (match.player1Uid === uid || match.player2Uid === uid)
  );
}

function isDashboardUpcomingTournament(tournament = {}, now = Date.now()) {
  const status = String(tournament.status || "open").toLowerCase();
  const startMillis = getDashboardTournamentStartMillis(tournament);
  return startMillis > now && !DASHBOARD_TERMINAL_TOURNAMENT_STATUSES.has(status);
}

function isDashboardSpecialOrCreatorTournament(tournament = {}) {
  const type = String(
    tournament.tournamentType ||
      tournament.type ||
      tournament.category ||
      tournament.kind ||
      getDashboardTournamentId(tournament)
  ).toLowerCase();
  return (
    isSpecialCupTournament(tournament) ||
    tournament.creatorUid ||
    tournament.creatorId ||
    tournament.isCreatorCup ||
    type.includes("special") ||
    type.includes("creator") ||
    type.includes("community")
  );
}

function getDashboardTournamentTypeFlags(tournament = {}) {
  const id = getDashboardTournamentId(tournament).toLowerCase();
  const type = String(
    tournament.tournamentType ||
      tournament.type ||
      tournament.category ||
      tournament.kind ||
      ""
  ).toLowerCase();

  const isEvening =
    type === "evening" ||
    id === "evening" ||
    id.endsWith("_evening");
  const isNightly =
    type === "nightly" ||
    id === "nightly" ||
    id.endsWith("_nightly");
  const isSpecialCreator =
    id.endsWith("_special") ||
    type === "special" ||
    type === "community" ||
    type === "creator" ||
    type === "creator_cup" ||
    type === "creatorcup" ||
    tournament.specialCup === true ||
    tournament.isSpecialCup === true ||
    isDashboardSpecialOrCreatorTournament(tournament);

  return { isEvening, isNightly, isSpecialCreator };
}

function getDashboardTournamentIcon(tournament = {}) {
  const { isEvening, isNightly, isSpecialCreator } = getDashboardTournamentTypeFlags(tournament);
  if (isSpecialCreator) return SpecialCreatorCupIcon;
  if (isNightly) return NightlyCupIcon;
  if (isEvening) return EveningCupIcon;
  return TrophyIcon;
}

function countDashboardParticipantCollection(value) {
  if (Array.isArray(value)) return value.length;
  if (!value || typeof value !== "object") return 0;
  return Object.keys(value).length;
}

function hasDashboardParticipantCollection(value) {
  return Array.isArray(value) || Boolean(value && typeof value === "object");
}

function getDashboardParticipantCount(tournament = {}) {
  const participantCollections = [
    tournament.participants,
    tournament.participantUids,
    tournament.players,
    tournament.playerUids,
    tournament.registeredPlayerUids,
    tournament.playerMap,
  ];

  if (participantCollections.some(hasDashboardParticipantCollection)) {
    return Math.max(...participantCollections.map(countDashboardParticipantCollection));
  }

  const explicit = [
    tournament.participantCount,
    tournament.currentPlayers,
    tournament.currentParticipants,
    tournament.registeredCount,
    tournament.playersCount,
  ].find((value) => Number.isFinite(Number(value)) && Number(value) >= 0);

  if (explicit !== undefined) return Number(explicit);
  return 0;
}

const DashboardPage = () => {
  const { language, setLanguage, t } = useLanguage();
  // ----------------------------
  // Global / Profile / Lists
  // ----------------------------
const [selectedDayKey, setSelectedDayKey] = useState("today");
const [profile, setProfile] = useState(null);
const [loadingProfile, setLoadingProfile] = useState(true);
const [accountConsoleType, setAccountConsoleType] = useState("");
const [accountConsoleId, setAccountConsoleId] = useState("");
const [accountEaId, setAccountEaId] = useState("");
const [accountAgeGroup, setAccountAgeGroup] = useState("");
const [accountSaveMessage, setAccountSaveMessage] = useState("");
const [savingAccountProfile, setSavingAccountProfile] = useState(false);
const [accountAgeMessage, setAccountAgeMessage] = useState("");
const [savingAccountAgeGroup, setSavingAccountAgeGroup] = useState(false);
const accountSaveMessageTimerRef = useRef(null);
const accountKeyboardFocusTimersRef = useRef([]);
const [aiConsentPromptOpen, setAiConsentPromptOpen] = useState(false);
const [savingAiConsent, setSavingAiConsent] = useState(false);
const [aiConsentMessage, setAiConsentMessage] = useState("");
const [aiConsentPromptHandledLocalUid, setAiConsentPromptHandledLocalUid] = useState(null);
const [leaveModalOpen, setLeaveModalOpen] = useState(false);
const [selectedLeaveTournament, setSelectedLeaveTournament] = useState(null);
const [tournaments, setTournaments] = useState([]);
const [joinMessage, setJoinMessage] = useState("");
const [dailyBonusOpen, setDailyBonusOpen] = useState(false);
const [dailyBonusData, setDailyBonusData] = useState(null);
const [dailyBonusLoading, setDailyBonusLoading] = useState(false);
const [questRewardPromptOpen, setQuestRewardPromptOpen] = useState(false);
const [questRewardPromptRewards, setQuestRewardPromptRewards] = useState([]);
const [questRewardPromptCheckTick, setQuestRewardPromptCheckTick] = useState(0);
const [dashboardFriendCount, setDashboardFriendCount] = useState(0);
const [dashboardFriendsInitialized, setDashboardFriendsInitialized] = useState(false);
const [lobbyPlayers, setLobbyPlayers] = useState([]);
const [dashboardTournamentMatches, setDashboardTournamentMatches] = useState([]);
const [dashboardActiveSingleMatch, setDashboardActiveSingleMatch] = useState(null);
const [dashboardNow, setDashboardNow] = useState(() => Date.now());
const [dueSurvey, setDueSurvey] = useState(null);
const [surveyAvailability, setSurveyAvailability] = useState({ survey: null, dueSurveyCount: 0 });
const [surveyBusy, setSurveyBusy] = useState(false);
const [surveyCheckTick, setSurveyCheckTick] = useState(0);
const surveyCheckInFlightRef = useRef(false);
const surveyOpenInFlightRef = useRef(false);
const surveySessionGuardRef = useRef(false);
const [dashboardDailySpecialCups, setDashboardDailySpecialCups] = useState([]);
const currentUid = auth.currentUser?.uid || null;
const isAppActive = useAppActiveState();
const getPriorityNotificationPopupStates = useMemo(
  () => httpsCallable(firebaseFunctions, "getPriorityNotificationPopupStatesSecure"),
  []
);
const getDueSurvey = useMemo(
  () => httpsCallable(firebaseFunctions, "getDueSurveySecure"),
  []
);
const markSurveyShown = useMemo(
  () => httpsCallable(firebaseFunctions, "markSurveyShownSecure"),
  []
);
const deferSurvey = useMemo(
  () => httpsCallable(firebaseFunctions, "deferSurveySecure"),
  []
);
const submitSurvey = useMemo(
  () => httpsCallable(firebaseFunctions, "submitSurveySecure"),
  []
);
const aiConflictAnalysisConsentAvailable = canGrantAiConflictAnalysisConsent(profile);
const aiConflictAnalysisConsentValid = hasValidAiConflictAnalysisConsent(profile);
const accountProviderCapabilities = getAccountProviderCapabilities(auth.currentUser);
const nativeAppleReauthAvailable = isNativeIosAppleAuthAvailable();
const nativeIosAppReviewAvailable = isNativeIosAppReviewAvailable();
const dataExportUsesApple =
  accountProviderCapabilities.hasApple && nativeAppleReauthAvailable;
const appleOnlyDataExportUnavailable =
  accountProviderCapabilities.state === "APPLE_ONLY" && !nativeAppleReauthAvailable;
const deleteAccountUsesApple =
  accountProviderCapabilities.hasApple && nativeAppleReauthAvailable;
const appleLinkedDeleteUnavailable =
  accountProviderCapabilities.hasApple && !nativeAppleReauthAvailable;
const dataExportRecentAuthReasons = new Set([
  "MISSING_OR_INVALID_AUTH_TIME",
  "INVALID_AUTH_AGE",
  "AUTH_TOO_OLD",
]);

const renderInlineHighlight = (text, highlight, highlightStyle) => {
  if (!highlight || !text.includes(highlight)) return text;
  const [before, after] = text.split(highlight);
  return (
    <>
      {before}
      <strong style={highlightStyle}>{highlight}</strong>
      {after}
    </>
  );
};
const isDataExportRecentAuthError = (error) =>
  dataExportRecentAuthReasons.has(String(error?.details?.reason || ""));
const communityQuestSummary = useMemo(() => {
  if (!profile) return null;

  const claimedRewardIds = Array.isArray(profile.claimedRewardIds)
    ? profile.claimedRewardIds
    : [];
  const rewards = buildQuestRewards(profile, { friendCount: dashboardFriendCount });
  const completed = rewards.filter((reward) => reward.progress >= reward.target).length;
  const claimable = rewards.filter((reward) => {
    const claimId = reward.claimId || reward.id;
    return reward.progress >= reward.target && !claimedRewardIds.includes(claimId);
  }).length;

  return {
    completed,
    total: rewards.length,
    claimable,
  };
}, [profile, dashboardFriendCount]);
const questRewardPromptDisplay = getQuestRewardPromptDisplay(
  questRewardPromptRewards,
  3
);
const functions = firebaseFunctions;
const leaveTournamentSecure = httpsCallable(functions, "leaveTournamentSecure");
const deleteMyAccountSecure = httpsCallable(functions, "deleteMyAccountSecure");
const sendFriendRequestSecure = httpsCallable(functions, "sendFriendRequestSecure");
const submitSupportRequest = httpsCallable(functions, "submitSupportRequest");
const requestMyDataExportSecure = httpsCallable(functions, "requestMyDataExportSecure");
const syncMyAccountEmailSecure = httpsCallable(functions, "syncMyAccountEmailSecure");
const requestAccountEmailChangeSecure = httpsCallable(functions, "requestAccountEmailChangeSecure");
const updateAiConflictAnalysisConsentSecure = httpsCallable(
  functions,
  "updateAiConflictAnalysisConsentSecure"
);
const updateAccountAgeGroupSecure = httpsCallable(
  functions,
  "updateAccountAgeGroupSecure"
);
const unlockPrivateTournamentByCodeSecure = httpsCallable(
  functions,
  "unlockPrivateTournamentByCodeSecure"
);
const navigate = useNavigate();
const respondToSingleFriendChallenge = httpsCallable(
  functions,
  "respondToSingleFriendChallenge"
);
const [lobbyMaxPlayers, setLobbyMaxPlayers] = useState(0);
const [tournamentsSectionKey, setTournamentsSectionKey] = useState(0);
const [singleStep, setSingleStep] = useState("stake");
const [view, setView] = useState("LIST"); // "LIST" | "DETAIL"
const [countdownText, setCountdownText] = useState("00:00:00");
const [selectedTournamentId, setSelectedTournamentId] = useState(null);
const [selectedTournament, setSelectedTournament] = useState(null);
const lobbyTournamentFallbackRef = useRef(null);
const pendingPushNavigationConsumedRef = useRef(false);
const [homeSection, setHomeSection] = useState(null);
const [myPrizesBackSection, setMyPrizesBackSection] = useState(null);
const [viewedProfileUid, setViewedProfileUid] = useState(null);
const [viewedProfileConnectionDetails, setViewedProfileConnectionDetails] = useState(null);
const [profileBackTarget, setProfileBackTarget] = useState(null);
const [reportBlockInitial, setReportBlockInitial] = useState({ mode: "", username: "", locked: false });
const [reportBlockBackTarget, setReportBlockBackTarget] = useState(null);
const [openPlayerMenuUid, setOpenPlayerMenuUid] = useState(null);
const [playerMenuPosition, setPlayerMenuPosition] = useState(null);
const playerMenuRef = useRef(null);
const dashboardScrollRef = useRef(null);
const [fairplaySubscreen, setFairplaySubscreen] = useState("MENU");
const [infoModalOpen, setInfoModalOpen] = useState(false);
const [infoModalTitle, setInfoModalTitle] = useState("");
const [infoModalText, setInfoModalText] = useState("");
const [infoModalAccountAction, setInfoModalAccountAction] = useState(false);
const alert = (message) => {
  const cleanMessage = String(message || "").trim();
  setInfoModalTitle("VaultQuest");
  setInfoModalText(cleanMessage || t("dashboard.match.saveError", { code: "", message: "" }));
  setInfoModalAccountAction(false);
  setInfoModalOpen(true);
};

const isPlayerProfileComplete = hasRequiredPlayerProfileFields(profile || {});

const openAccountSettings = () => {
  setMenuPage("account");
  setHomeSection(null);
  setMenuOpen(true);
};

const closeInfoModal = () => {
  setInfoModalOpen(false);
  setInfoModalAccountAction(false);
};

const showProfileCompletionNotice = () => {
  setInfoModalTitle(t("profileCompletion.title"));
  setInfoModalText(`${t("profileCompletion.text")}\n\n${t("profileCompletion.path")}`);
  setInfoModalAccountAction(true);
  setInfoModalOpen(true);
};

const showOtherPlayerProfileIncompleteNotice = () => {
  setInfoModalTitle(t("profileCompletion.otherPlayerTitle"));
  setInfoModalText(t("profileCompletion.otherPlayerText"));
  setInfoModalAccountAction(false);
  setInfoModalOpen(true);
};

const openReportBlockForUser = (uid, mode = "report", usernameOverride = "", backTarget = null) => {
  const username = String(usernameOverride || usernamesByUid?.[uid] || "").trim();
  setReportBlockInitial({ mode, username, locked: Boolean(username) });
  setReportBlockBackTarget(backTarget);
  setView("LIST");
  setHomeSection("COMMUNITY_REPORT_BLOCK");
};
const [rulesModalOpen, setRulesModalOpen] = useState(false);
const [rulesCompetitionTermsOpen, setRulesCompetitionTermsOpen] = useState(false);
const [notificationsOpen, setNotificationsOpen] = useState(false);
const [selectedNotification, setSelectedNotification] = useState(null);
const [notificationDetailOpen, setNotificationDetailOpen] = useState(false);
const [surveyNotificationUnavailableId, setSurveyNotificationUnavailableId] = useState(null);
const [surveyNotificationBlockedId, setSurveyNotificationBlockedId] = useState(null);
const [notificationsMobileLayout, setNotificationsMobileLayout] = useState(false);
const [dashboardTabletLayout, setDashboardTabletLayout] = useState(false);
const [dashboardShellTabletLayout, setDashboardShellTabletLayout] = useState(false);
const [priorityNotification, setPriorityNotification] = useState(null);
const [priorityNotificationActionLoading, setPriorityNotificationActionLoading] = useState(false);
const [priorityNotificationActionError, setPriorityNotificationActionError] = useState("");
const [priorityPresenceNowMs, setPriorityPresenceNowMs] = useState(() => Date.now());
const [priorityPresenceActionUnavailable, setPriorityPresenceActionUnavailable] = useState(false);
const [notifications, setNotifications] = useState([]);
const [challengeResponseLoading, setChallengeResponseLoading] = useState(false);
const [challengeResponseDone, setChallengeResponseDone] = useState(false);
const [friendRequestButtonPressed, setFriendRequestButtonPressed] = useState(null);
const [friendRequestResponseLoading, setFriendRequestResponseLoading] = useState(false);
const [challengeCountdownText, setChallengeCountdownText] = useState("");
const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
const [deletingAccount, setDeletingAccount] = useState(false);
const [deleteAccountPassword, setDeleteAccountPassword] = useState("");
const [deleteAccountMessage, setDeleteAccountMessage] = useState("");
const [deleteAccountPasswordVisible, setDeleteAccountPasswordVisible] = useState(false);
const [deleteAccountConfirmed, setDeleteAccountConfirmed] = useState(false);
const [adminRecipientUsernames, setAdminRecipientUsernames] = useState("");
const [adminNotificationTitle, setAdminNotificationTitle] = useState("");
const [adminNotificationMessage, setAdminNotificationMessage] = useState("");
const adminNotificationActionRef = useRef({ fingerprint: "", actionId: "" });
const [sendingAdminNotification, setSendingAdminNotification] = useState(false);
const [adminPanelBackHandler, setAdminPanelBackHandler] = useState(null);
const [supportModalOpen, setSupportModalOpen] = useState(false);
const [supportTitle, setSupportTitle] = useState("");
const [supportMessage, setSupportMessage] = useState("");
const [sendingSupport, setSendingSupport] = useState(false);
const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
const [emailChangeModalOpen, setEmailChangeModalOpen] = useState(false);
const [emailChangePassword, setEmailChangePassword] = useState("");
const [emailChangeNewEmail, setEmailChangeNewEmail] = useState("");
const [emailChangeConfirmEmail, setEmailChangeConfirmEmail] = useState("");
const [emailChangeMessage, setEmailChangeMessage] = useState("");
const [requestingEmailChange, setRequestingEmailChange] = useState(false);
const [dataExportModalOpen, setDataExportModalOpen] = useState(false);
const [dataExportPassword, setDataExportPassword] = useState("");
const [requestingDataExport, setRequestingDataExport] = useState(false);
const [dataExportMessage, setDataExportMessage] = useState("");
const [latestDataExportRequest, setLatestDataExportRequest] = useState(null);
const [biometricLoginEnabled, setBiometricLoginEnabled] = useState(false);
const hasAdminAccess = hasAdminRole(profile);
const hasCreatorTournamentAccess = hasAdminAccess || isCreator(profile);
// ----------------------------
// Result Modal (Eliminated / Winner)
// ----------------------------
const [resultModalOpen, setResultModalOpen] = useState(false);
const [resultModalVariant, setResultModalVariant] = useState("eliminated");
const resultNextActionRef = useRef(null); // function to run after OK

const shownResultForMatchRef = useRef({ matchId: null, type: null });
const processedPriorityNotificationIdsByUidRef = useRef(new Map());
const initialQuestPromptEvaluatedRef = useRef(false);
const questRewardPromptLocalGuardRef = useRef(null);
const appReviewPromptInFlightRef = useRef(false);

const otherBlockingDashboardModalOpen =
  leaveModalOpen ||
  infoModalOpen ||
  rulesModalOpen ||
  notificationsOpen ||
  supportModalOpen ||
  passwordResetModalOpen ||
  dataExportModalOpen ||
  deleteAccountModalOpen ||
  resultModalOpen ||
  aiConsentPromptOpen ||
  questRewardPromptOpen ||
  dailyBonusOpen;

const blockingDashboardModalOpen = otherBlockingDashboardModalOpen || Boolean(dueSurvey);

useEffect(() => {
  const uid = auth.currentUser?.uid;
  setBiometricLoginEnabled(Boolean(uid && isBiometricLoginEnabledForUser(uid)));
}, [profile?.uid]);

useEffect(() => {
  if (aiConsentPromptHandledLocalUid && aiConsentPromptHandledLocalUid !== currentUid) {
    setAiConsentPromptHandledLocalUid(null);
  }
}, [aiConsentPromptHandledLocalUid, currentUid]);

useEffect(() => {
  if (!currentUid) {
    setDashboardFriendCount(0);
    setDashboardFriendsInitialized(false);
    return undefined;
  }

  setDashboardFriendsInitialized(false);
  const friendsRef = collection(db, "users", currentUid, "friends");
  const unsubscribe = onSnapshot(
    friendsRef,
    (snapshot) => {
      setDashboardFriendCount(snapshot.size);
      setDashboardFriendsInitialized(true);
    },
    (error) => {
      console.error("reward prompt friends listener failed:", error?.code || error?.message || "unknown");
      setDashboardFriendCount(0);
      setDashboardFriendsInitialized(false);
    }
  );

  return () => unsubscribe();
}, [currentUid]);

useEffect(() => {
  initialQuestPromptEvaluatedRef.current = false;
  questRewardPromptLocalGuardRef.current = null;
}, [currentUid]);

useEffect(() => {
  const requestCheck = () => setQuestRewardPromptCheckTick((value) => value + 1);

  const handleVisibilityChange = () => {
    if (!document.hidden) requestCheck();
  };

  window.addEventListener("focus", requestCheck);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    window.removeEventListener("focus", requestCheck);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}, []);

useEffect(() => {
  if (
    !areQuestRewardPromptSourcesReady({
      currentUid,
      profile,
      loadingProfile,
      friendsInitialized: dashboardFriendsInitialized,
    }) ||
    questRewardPromptOpen
  ) return;
  if (blockingDashboardModalOpen) return;

  if (!initialQuestPromptEvaluatedRef.current) {
    initialQuestPromptEvaluatedRef.current = true;
  }
  const nowMs = Date.now();
  const result = shouldShowQuestRewardPrompt(profile, {
    friendCount: dashboardFriendCount,
    nowMs,
  });

  if (!result.shouldShow || !result.signature) return;
  if (
    isQuestRewardPromptBlockedByLocalGuard({
      rewards: result.rewards,
      guard: questRewardPromptLocalGuardRef.current,
      nowMs,
    })
  ) return;

  questRewardPromptLocalGuardRef.current = createQuestRewardPromptGuard(
    result.rewards,
    nowMs
  );
  setQuestRewardPromptRewards(result.rewards);
  setQuestRewardPromptOpen(true);

  updateDoc(doc(db, "users", currentUid), {
    "engagementPrompts.questRewards.lastShownAt": serverTimestamp(),
    "engagementPrompts.questRewards.lastSignature": result.signature,
    "engagementPrompts.questRewards.lastCount": result.rewards.length,
    "engagementPrompts.questRewards.updatedAt": serverTimestamp(),
  }).catch((error) => {
    console.error("reward prompt marker failed:", error?.code || error?.message || "unknown");
  });
}, [
  currentUid,
  profile,
  loadingProfile,
  dashboardFriendCount,
  dashboardFriendsInitialized,
  questRewardPromptCheckTick,
  questRewardPromptOpen,
  blockingDashboardModalOpen,
]);

useEffect(() => {
  const updateNotificationsLayout = () => {
    const useNativeMobileLayout = Capacitor.isNativePlatform();
    setNotificationsMobileLayout(useNativeMobileLayout || window.innerWidth <= 720);
    setDashboardTabletLayout(
      !useNativeMobileLayout && window.innerWidth > 720 && window.innerWidth <= 1180
    );
    setDashboardShellTabletLayout(
      !useNativeMobileLayout && window.innerWidth > 720 && window.innerWidth <= 1023
    );
  };

  updateNotificationsLayout();
  window.addEventListener("resize", updateNotificationsLayout);
  return () => window.removeEventListener("resize", updateNotificationsLayout);
}, []);

useEffect(() => {
  if (notificationsOpen) {
    setNotificationDetailOpen(false);
  }
}, [notificationsOpen]);


const playerMenuButtonRef = useRef(null);
const getLobbyPlayerMenuPosition = (buttonEl, uid) => {
  if (typeof window === "undefined" || !buttonEl) {
    return { top: 0, left: 0, width: 304 };
  }

  const rect = buttonEl.getBoundingClientRect();
  const viewportWidth = window.innerWidth || 390;
  const viewportHeight = window.innerHeight || 800;
  const edgePadding = 12;
  const bottomSafeArea = viewportWidth <= 720 ? 128 : 28;
  const menuWidth = Math.min(304, Math.max(232, viewportWidth - edgePadding * 2));
  const menuHeight = uid === currentUid ? 72 : 264;
  const preferredLeft = rect.right - menuWidth;
  const preferredTop = rect.top + rect.height / 2 - menuHeight / 2;

  return {
    top: Math.max(
      edgePadding,
      Math.min(viewportHeight - bottomSafeArea - menuHeight, preferredTop)
    ),
    left: Math.max(
      edgePadding,
      Math.min(viewportWidth - menuWidth - edgePadding, preferredLeft)
    ),
    width: menuWidth,
  };
};
const [tournamentTab, setTournamentTab] = useState("match");
// "match" | "tournament" | "rules"

useEffect(() => {
  const resetDashboardScroll = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    if (typeof document !== "undefined") {
      document.documentElement.scrollTop = 0;
      document.documentElement.scrollLeft = 0;
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
    }

    const container = dashboardScrollRef.current;
    if (container) {
      container.scrollTop = 0;
      container.scrollLeft = 0;
    }
  };

  resetDashboardScroll();
  const frame = window.requestAnimationFrame(resetDashboardScroll);
  return () => window.cancelAnimationFrame(frame);
}, [homeSection, view, selectedTournamentId, viewedProfileUid, singleStep, fairplaySubscreen]);



// null | "TOURNAMENTS" | "PROFILE" | "GAME" | "SINGLES" | "FAIRPLAY"

const [menuOpen, setMenuOpen] = useState(false);
const [menuPage, setMenuPage] = useState(null);
const [menuNotice, setMenuNotice] = useState("");
const [menuReturnContext, setMenuReturnContext] = useState(null);

useEffect(() => {
  return () => {
    if (accountSaveMessageTimerRef.current) {
      clearTimeout(accountSaveMessageTimerRef.current);
      accountSaveMessageTimerRef.current = null;
    }
  };
}, []);

const appReviewQuietMoment =
  nativeIosAppReviewAvailable &&
  view === "LIST" &&
  homeSection === null &&
  !selectedTournamentId &&
  !dashboardActiveSingleMatch &&
  !blockingDashboardModalOpen &&
  !menuOpen;

useEffect(() => {
  if (!currentUid) {
    setAiConsentPromptOpen(false);
    setAiConsentMessage("");
    return;
  }

  if (
    aiConsentPromptHandledLocalUid !== currentUid &&
    shouldShowAiConflictAnalysisConsentPrompt({
      profile,
      currentUid,
      loadingProfile,
      isPlayerProfileComplete,
      view,
      homeSection,
      selectedTournamentId,
      dashboardActiveSingleMatch,
      blockingDashboardModalOpen,
      menuOpen,
    })
  ) {
    setAiConsentPromptOpen(true);
  }
}, [
  currentUid,
  profile,
  aiConsentPromptHandledLocalUid,
  loadingProfile,
  isPlayerProfileComplete,
  view,
  homeSection,
  selectedTournamentId,
  dashboardActiveSingleMatch,
  blockingDashboardModalOpen,
  menuOpen,
]);

useEffect(() => {
  if (
    !currentUid ||
    loadingProfile ||
    !profile ||
    !appReviewQuietMoment ||
    appReviewPromptInFlightRef.current
  ) {
    return;
  }

  const completedMatches = getCompletedMatchCountForAppReview(profile);
  if (completedMatches < 0) return;

  appReviewPromptInFlightRef.current = true;
  claimAppReviewPromptIfEligible({
    firestoreDb: db,
    userRef: doc(db, "users", currentUid),
  })
    .then((shouldRequestReview) => {
      if (!shouldRequestReview) return null;
      return requestNativeAppReviewIfAvailable();
    })
    .catch((error) => {
      console.error("app review eligibility check failed:", error?.code || error?.message || "unknown");
    })
    .finally(() => {
      appReviewPromptInFlightRef.current = false;
    });
}, [currentUid, loadingProfile, profile, appReviewQuietMoment]);

const resetDashboardHorizontalOffset = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  window.requestAnimationFrame(() => {
    window.scrollTo({ top: window.scrollY, left: 0, behavior: "auto" });
    document.documentElement.scrollLeft = 0;
    document.body.scrollLeft = 0;
    document.getElementById("root")?.scrollTo?.({ left: 0, behavior: "auto" });

    const container = dashboardScrollRef.current;
    if (container) {
      container.scrollLeft = 0;
    }
  });
};

const clearAccountKeyboardFocusTimers = () => {
  accountKeyboardFocusTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
  accountKeyboardFocusTimersRef.current = [];
};

const keepFocusedAccountFieldVisible = (target) => {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !target ||
    document.activeElement !== target
  ) {
    return;
  }

  resetDashboardHorizontalOffset();

  const visualViewport = window.visualViewport;
  const viewportTop = visualViewport?.offsetTop || 0;
  const viewportHeight = visualViewport?.height || window.innerHeight || 0;
  if (!viewportHeight) return;

  const visibleTop = viewportTop + 18;
  const visibleBottom = viewportTop + viewportHeight - 28;
  const rect = target.getBoundingClientRect();

  if (rect.top < visibleTop || rect.bottom > visibleBottom) {
    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }
};

const handleAccountKeyboardAwareFocus = (event) => {
  const target = event?.currentTarget;
  resetDashboardHorizontalOffset();

  if (
    !Capacitor.isNativePlatform() ||
    Capacitor.getPlatform() !== "ios" ||
    typeof window === "undefined"
  ) {
    return;
  }

  clearAccountKeyboardFocusTimers();
  [80, 220, 420, 650].forEach((delay) => {
    const timerId = window.setTimeout(() => keepFocusedAccountFieldVisible(target), delay);
    accountKeyboardFocusTimersRef.current.push(timerId);
  });
};

useEffect(() => {
  return () => {
    accountKeyboardFocusTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    accountKeyboardFocusTimersRef.current = [];
  };
}, []);

const releaseFocusedFieldAndResetHorizontalOffset = () => {
  if (typeof document !== "undefined") {
    const activeElement = document.activeElement;
    if (
      activeElement &&
      typeof activeElement.blur === "function" &&
      ["INPUT", "TEXTAREA", "SELECT"].includes(activeElement.tagName)
    ) {
      activeElement.blur();
    }
  }

  resetDashboardHorizontalOffset();
};

const closeMenu = () => {
  releaseFocusedFieldAndResetHorizontalOffset();
  setMenuOpen(false);
  setMenuPage("root");
  setMenuNotice("");


};

const handleRateVaultQuest = async () => {
  setMenuNotice("");
  try {
    const result = await openVaultQuestAppStoreReviewPage();
    if (result?.reason === APP_REVIEW_MISSING_APP_STORE_ID) {
      setMenuNotice(t("menu.rateVaultQuestMissingAppStoreId"));
    }
  } catch (error) {
    console.error("app store review page could not be opened:", error?.code || error?.message || "unknown");
    setMenuNotice(t("menu.rateVaultQuestOpenFailed"));
  }
};

const openMenuHomeSection = (section) => {
  releaseFocusedFieldAndResetHorizontalOffset();
  setMenuReturnContext({
    homeSection,
    menuPage: menuPage || "root",
  });
  setMenuOpen(false);
  setHomeSection(section);
};

const handleMenuHomeSectionBack = () => {
  if (!menuReturnContext) {
    setHomeSection(null);
    return;
  }

  setHomeSection(menuReturnContext.homeSection || null);
  setMenuPage(menuReturnContext.menuPage || "root");
  setMenuNotice("");
  setMenuOpen(true);
  setMenuReturnContext(null);
};

const openMyPrizes = (backSection = null) => {
  const navigation = openMyPrizesNavigation(backSection);
  setMyPrizesBackSection(navigation.backSection);
  setHomeSection(navigation.homeSection);
};

const handleMyPrizesBack = () => {
  const navigation = closeMyPrizesNavigation(myPrizesBackSection);
  setMyPrizesBackSection(navigation.backSection);
  setHomeSection(navigation.homeSection);
};



const leaveTournamentFirestore = async (tournamentJoinDocId) => {
  try {
    await leaveTournamentSecure({ tournamentId: tournamentJoinDocId });
    return true;
  } catch (e) {
    console.error("Leave failed:", e?.code || e?.message || "unknown");

    const msg = String(e?.message || "");
    const code = String(e?.code || "");
    const reason = String(e?.details?.reason || "");

    if (msg.includes("LEAVE_LOCKED_10_MIN") || reason === "LEAVE_LOCKED_10_MIN") {
      setInfoModalTitle(t("dashboard.leaveTournament.lockedTitle"));
setInfoModalText(t("dashboard.leaveTournament.lockedText"));
    } else if (msg.includes("Tournament already started")) {
      setInfoModalTitle(t("dashboard.leaveTournament.startedTitle"));
setInfoModalText(t("dashboard.leaveTournament.startedText"));
    } else if (code.includes("unauthenticated")) {
      setInfoModalTitle(t("dashboard.leaveTournament.notLoggedInTitle"));
setInfoModalText(t("dashboard.leaveTournament.notLoggedInText"));
    } else {
      setInfoModalTitle(t("common.error"));
setInfoModalText(t("dashboard.leaveTournament.genericError"));
    }

    setInfoModalOpen(true);
    return false; // kein throw, kein alert
  }
};

const handleRequestLeave = (payload) => {
  // payload kann String (id) oder Objekt sein
  const obj = typeof payload === "string" ? { id: payload } : (payload || {});
  const idToLeave = obj.joinDocId || obj.id;

  setSelectedLeaveTournament({
    id: idToLeave,
    time: obj.time || "18:00",
    maxPlayers: obj.maxPlayers || 0,
  });

  setLeaveModalOpen(true);
};

const markNotificationAsRead = async (notificationItem) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    if (!notificationItem?.id) return;
    if (notificationItem?.isRead === true) return;

    const markRead = httpsCallable(firebaseFunctions, "markNotificationReadSecure");
    await markRead({ notificationId: notificationItem.id });
  } catch (error) {
    console.error("markNotificationAsRead failed:", error?.code || error?.message || "unknown");
  }
};


const handleSendAdminNotification = async () => {
  try {
    const rawUsernames = adminRecipientUsernames
  .split(",")
  .map((username) => username.trim().toLowerCase())
  .filter(Boolean);

if (rawUsernames.length === 0) {
  alert(t("dashboard.admin.enterRecipient"));
  return;
}
    if (rawUsernames.length > ADMIN_MESSAGE_MAX_RECIPIENTS) {
      alert(`Maximal ${ADMIN_MESSAGE_MAX_RECIPIENTS} Empfänger sind erlaubt.`);
      return;
    }

    if (!adminNotificationTitle.trim()) {
      alert(t("dashboard.admin.enterTitle"));
      return;
    }

    if (!adminNotificationMessage.trim()) {
      alert(t("dashboard.admin.enterMessage"));
      return;
    }
    if (adminNotificationTitle.trim().length > ADMIN_MESSAGE_TITLE_MAX_LENGTH || adminNotificationMessage.trim().length > ADMIN_MESSAGE_BODY_MAX_LENGTH) {
      alert(`Titel maximal ${ADMIN_MESSAGE_TITLE_MAX_LENGTH}, Nachricht maximal ${ADMIN_MESSAGE_BODY_MAX_LENGTH} Zeichen.`);
      return;
    }

    const fingerprint = JSON.stringify({
      usernames: [...new Set(rawUsernames)].sort(),
      title: adminNotificationTitle.trim(),
      message: adminNotificationMessage.trim(),
    });
    if (adminNotificationActionRef.current.fingerprint !== fingerprint) {
      adminNotificationActionRef.current = {
        fingerprint,
        actionId: `admin_message_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`,
      };
    }

    setSendingAdminNotification(true);

    const sendNotification = httpsCallable(functions, "adminSendNotification");

    const result = await sendNotification({
  recipientUsernames: rawUsernames,
  title: adminNotificationTitle.trim(),
  message: adminNotificationMessage.trim(),
  matchId: null,
  conflictId: null,
  actionId: adminNotificationActionRef.current.actionId,
});

    console.log("adminSendNotification success");

    const delivery = result?.data?.notificationDelivery;
    if (delivery?.outcome === "ACTION_SUCCESS_PUSH_WARNING") {
      alert(t("dashboard.admin.sentPushWarning"));
    } else if (delivery?.outcome === "ACTION_SUCCESS_NO_PUSH_TARGET") {
      alert(t("dashboard.admin.sentNoPushTarget"));
    } else {
      alert(t("dashboard.admin.sentSuccess"));
    }

    setAdminRecipientUsernames("");
    setAdminNotificationTitle("");
    setAdminNotificationMessage("");
    adminNotificationActionRef.current = { fingerprint: "", actionId: "" };
  } catch (error) {
    console.error("adminSendNotification error:", error?.code || error?.message || "unknown");
   alert(t("dashboard.admin.sentError"));
  } finally {
    setSendingAdminNotification(false);
  }
};




const handleSubmitSupportRequest = async () => {
  try {
    const cleanTitle = supportTitle.trim();
    const cleanMessage = supportMessage.trim();

    if (!cleanMessage) {
     setInfoModalTitle(t("dashboard.support.missingMessageTitle"));
setInfoModalText(t("dashboard.support.missingMessageText"));
      setInfoModalOpen(true);
      return;
    }

    setSendingSupport(true);

    await submitSupportRequest({
      title: cleanTitle,
      message: cleanMessage,
    });

    setSupportTitle("");
    setSupportMessage("");
    setSupportModalOpen(false);
closeMenu();


    setInfoModalTitle(t("dashboard.support.sentTitle"));
setInfoModalText(t("dashboard.support.sentText"));
    setInfoModalOpen(true);
  } catch (error) {
    console.error("submitSupportRequest failed:", error?.code || error?.message || "unknown");

    setInfoModalTitle(t("common.error"));
setInfoModalText(t("dashboard.support.errorText"));
    setInfoModalOpen(true);
  } finally {
    setSendingSupport(false);
  }
};


const handleClaimDailyBonus = async () => {
  try {
    setDailyBonusLoading(true);

    const res = await claimDailyBonus({});
    const result = res?.data || null;
    const outcome = classifyDailyBonusClaimResult(result);

    console.log("claimDailyBonus final result:", result?.reason || "unknown");
    setDailyBonusData(result);

    if (outcome === "awarded") {
      setDailyBonusOpen(false);
      return;
    }

    setDailyBonusOpen(false);
    setInfoModalTitle(t("dashboard.dailyBonus.title"));
    if (outcome === "already_claimed") {
      setInfoModalText(t("dashboard.dailyBonus.alreadyClaimed"));
    } else {
      setInfoModalText(t("dashboard.dailyBonus.claimUnavailable"));
    }
    setInfoModalOpen(true);
  } catch (error) {
    console.error("claimDailyBonus final failed:", error?.code || error?.message || "unknown");
    setDailyBonusOpen(false);
    setInfoModalTitle(t("common.error"));
    setInfoModalText(t("dashboard.dailyBonus.claimError"));
    setInfoModalOpen(true);
  } finally {
    setDailyBonusLoading(false);
  }
};



const handleSendPasswordReset = async () => {
  try {
    const email = profile?.email || auth.currentUser?.email || "";

    if (!email) {
      setInfoModalTitle(t("dashboard.passwordReset.noEmailTitle"));
setInfoModalText(t("dashboard.passwordReset.noEmailText"));
      setInfoModalOpen(true);
      return;
    }

    setSendingPasswordReset(true);

    await resetPassword(email, language);

    setPasswordResetModalOpen(false);
    setInfoModalTitle(t("dashboard.passwordReset.sentTitle"));
setInfoModalText(t("dashboard.passwordReset.sentText"));
    setInfoModalOpen(true);
  } catch (error) {
    console.error("sendPasswordResetEmail failed:", error?.code || error?.message || "unknown");

    setInfoModalTitle(t("common.error"));
setInfoModalText(t("dashboard.passwordReset.errorText"));
    setInfoModalOpen(true);
  } finally {
    setSendingPasswordReset(false);
  }
};

const EMAIL_CHANGE_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getEmailChangeErrorMessage = (error) => {
  const code = String(error?.code || "").replace("functions/", "");
  if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
    return t("menu.account.emailChange.errors.reauthFailed");
  }
  if (code === "auth/email-already-in-use" || code === "already-exists") {
    return t("menu.account.emailChange.errors.alreadyInUse");
  }
  if (code === "auth/invalid-email" || code === "invalid-argument") {
    return t("menu.account.emailChange.errors.invalidEmail");
  }
  if (code === "auth/too-many-requests") {
    return t("menu.account.emailChange.errors.tooManyRequests");
  }
  if (code === "failed-precondition") {
    return t("menu.account.emailChange.errors.recentLoginRequired");
  }
  return t("menu.account.emailChange.errors.requestFailed");
};

const resetEmailChangeForm = () => {
  setEmailChangePassword("");
  setEmailChangeNewEmail("");
  setEmailChangeConfirmEmail("");
  setEmailChangeMessage("");
};

const handleRequestEmailChange = async () => {
  const currentUser = auth.currentUser;
  const currentEmail = String(currentUser?.email || profile?.email || "").trim().toLowerCase();
  const password = String(emailChangePassword || "");
  const newEmail = String(emailChangeNewEmail || "").trim().toLowerCase();
  const confirmEmail = String(emailChangeConfirmEmail || "").trim().toLowerCase();

  if (!currentUser || !currentEmail) {
    setEmailChangeMessage(t("menu.account.emailChange.errors.noEmail"));
    return;
  }

  if (!password) {
    setEmailChangeMessage(t("menu.account.emailChange.errors.passwordRequired"));
    return;
  }

  if (!EMAIL_CHANGE_REGEX.test(newEmail)) {
    setEmailChangeMessage(t("menu.account.emailChange.errors.invalidEmail"));
    return;
  }

  if (newEmail !== confirmEmail) {
    setEmailChangeMessage(t("menu.account.emailChange.errors.emailMismatch"));
    return;
  }

  if (newEmail === currentEmail) {
    setEmailChangeMessage(t("menu.account.emailChange.errors.sameEmail"));
    return;
  }

  try {
    setRequestingEmailChange(true);
    setEmailChangeMessage("");

    const credential = EmailAuthProvider.credential(currentEmail, password);
    await reauthenticateWithCredential(currentUser, credential);
    await currentUser.getIdToken(true);

    await requestAccountEmailChangeSecure({ newEmail, language });
    await syncMyAccountEmailSecure({ language }).catch((syncError) => {
      console.warn("Email sync before account email change skipped:", syncError?.code || syncError?.message || "unknown");
    });

    setEmailChangePassword("");
    setEmailChangeNewEmail("");
    setEmailChangeConfirmEmail("");
    setEmailChangeMessage(t("menu.account.emailChange.verificationSent"));
  } catch (error) {
    console.error("request email change failed:", error?.code || error?.message || "unknown");
    setEmailChangeMessage(getEmailChangeErrorMessage(error));
  } finally {
    setRequestingEmailChange(false);
  }
};



const handleRespondToChallenge = async (challengeId, action) => {
  if (!challengeId || !action) return;

  try {
    setChallengeResponseLoading(true);

    await respondToSingleFriendChallenge({
      challengeId,
      action, // "accept" | "decline"
    });


  setChallengeResponseDone(true);
  } catch (e) {
    console.error("respondToSingleFriendChallenge failed:", e?.code || e?.message || "unknown");

    const rawMsg = String(e?.message || "");
    const rawCode = String(e?.code || "");
    const code = rawCode.replace("functions/", "");

    if (String(e?.message || "").includes("BANNED")) {
  const details = e?.details || {};
  const bannedUntilRaw = details?.bannedUntil || null;

  let bannedUntilText = null;

  if (bannedUntilRaw) {
    const d = new Date(bannedUntilRaw);
    if (!isNaN(d.getTime())) {
      bannedUntilText = d.toLocaleString("de-DE");
    }
  }

  setInfoModalTitle(t("dashboard.challenge.bannedTitle"));

  if (bannedUntilText) {
    setInfoModalText(
      t("dashboard.challenge.bannedUntilText", { date: bannedUntilText })
    );
  } else {
    setInfoModalText(
      t("dashboard.challenge.bannedText")
    );
  }

  setInfoModalOpen(true);
  return;
}

	    if (
	      code === "failed-precondition" &&
	      (rawMsg.includes("PLAYER_PROFILE_INCOMPLETE") ||
	        e?.details?.missingConsoleId === true ||
	        e?.details?.missingEaId === true)
	    ) {
	      if (e?.details?.label === "challenge_challenger") {
	        showOtherPlayerProfileIncompleteNotice();
	      } else {
	        showProfileCompletionNotice();
	      }
	      return;
	    }

    if (code === "failed-precondition" && rawMsg.includes("Challenge is no longer pending")) {
      setInfoModalTitle(t("dashboard.challenge.unavailableTitle"));
setInfoModalText(t("dashboard.challenge.unavailableText"));
    } else if (code === "failed-precondition" && rawMsg.includes("You do not have enough coins")) {
      setInfoModalTitle(t("dashboard.challenge.notEnoughCoinsTitle"));
setInfoModalText(t("dashboard.challenge.notEnoughCoinsText"));
    } else if (code === "failed-precondition" && rawMsg.includes("Challenger does not have enough coins")) {
      setInfoModalTitle(t("dashboard.challenge.invalidTitle"));
setInfoModalText(t("dashboard.challenge.invalidText"));
    } else if (code === "permission-denied") {
      setInfoModalTitle(t("dashboard.challenge.notPossibleTitle"));
setInfoModalText(t("dashboard.challenge.notPossibleText"));
    } else if (code === "not-found") {
      setInfoModalTitle(t("dashboard.challenge.notFoundTitle"));
setInfoModalText(t("dashboard.challenge.notFoundText"));
    } else {
      setInfoModalTitle(t("common.error"));
setInfoModalText(t("dashboard.challenge.errorText"));
    }

    setInfoModalOpen(true);
  } finally {
    setChallengeResponseLoading(false);
  }
};


const handleRespondToFriendRequest = async (fromUid, action) => {
  if (friendRequestResponseLoading) return;

  try {
    setFriendRequestButtonPressed(action);
    setFriendRequestResponseLoading(true);

    const fn = httpsCallable(
      functions,
      "respondToFriendRequestSecure"
    );


    await fn({
      fromUid,
      action,
    });

    setSelectedNotification((prev) =>
      prev
        ? {
            ...prev,
            status: "handled",
            action:
              action === "accept"
                ? "accepted"
                : "declined",
          }
        : prev
    );
  } catch (err) {
    console.error("Login state update failed:", err?.code || err?.message || "unknown");
    setFriendRequestButtonPressed(null);
  } finally {
    setFriendRequestResponseLoading(false);
  }
};





const handleLogout = async () => {
  try {
    await logoutUser();
    closeMenu();
    navigate("/login", { replace: true });
  } catch (error) {
    console.error(t("dashboard.errors.logoutFailed"), error?.code || error?.message || "unknown");
  }
};


const menuItemStyle = {
  background: "rgba(255,255,255,0.07)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: "14px",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 900,
  textAlign: "left",
  padding: "13px 12px",
  cursor: "pointer",
  width: "100%",
  boxSizing: "border-box",
  minHeight: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  boxShadow: "0 10px 24px rgba(0,0,0,0.22)",
};

const getMenuCardStyle = ({ active = false, danger = false, index = 0 } = {}) => ({
  ...menuItemStyle,
  position: "relative",
  overflow: "hidden",
  background: danger
    ? "linear-gradient(135deg, rgba(128, 14, 34, 0.96) 0%, rgba(64, 4, 22, 0.96) 100%)"
    : active
    ? "linear-gradient(135deg, rgba(255,0,127,0.34) 0%, rgba(122,43,255,0.34) 100%)"
    : "linear-gradient(135deg, rgba(255,255,255,0.105) 0%, rgba(255,255,255,0.045) 100%)",
  border: active
    ? "1px solid rgba(255, 84, 190, 0.78)"
    : danger
    ? "1px solid rgba(255, 86, 105, 0.48)"
    : "1px solid rgba(255,255,255,0.16)",
  boxShadow: active
    ? "0 0 24px rgba(255,0,127,0.28), inset 0 1px 0 rgba(255,255,255,0.18)"
    : danger
    ? "0 0 22px rgba(255,48,79,0.18), inset 0 1px 0 rgba(255,255,255,0.12)"
    : "0 12px 26px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.1)",
  animation: menuOpen ? "vqMenuItemIn 360ms ease both" : "none",
  animationDelay: `${Math.min(index * 42, 260)}ms`,
});

const menuIconStyle = {
  width: 34,
  height: 34,
  borderRadius: 12,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  background: "rgba(255,255,255,0.10)",
  border: "1px solid rgba(255,255,255,0.12)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
  fontSize: 17,
};

const menuLabelWrapStyle = {
  display: "flex",
  alignItems: "center",
  gap: 11,
  minWidth: 0,
};

const menuChevronStyle = {
  color: "rgba(255,255,255,0.72)",
  fontSize: 22,
  lineHeight: 1,
  fontWeight: 900,
  flexShrink: 0,
};

  const [matches, setMatches] = useState([]);
  const [usernamesByUid, setUsernamesByUid] = useState({});
 const [activeTab, setActiveTab] = React.useState("home");
const didHandleInitialSingleMatchRef = useRef(false);

useEffect(() => {
  seedUsernames(usernamesByUid);
}, [usernamesByUid]);



  // ----------------------------
  // Reporting UI state (local)
  // ----------------------------
  const [reportingMatchId, setReportingMatchId] = useState(null);
  const [scoreP1, setScoreP1] = useState("");
  const [scoreP2, setScoreP2] = useState("");

  // ----------------------------
  // Helper
  // ----------------------------
useEffect(() => {
  if (!currentUid) {
    setLatestDataExportRequest(null);
    return undefined;
  }

  const exportRequestsRef = collection(db, "users", currentUid, "dataExportRequests");
  const exportRequestsQuery = query(
    exportRequestsRef,
    orderBy("createdAt", "desc"),
    limit(1)
  );

  const unsubscribe = onSnapshot(
    exportRequestsQuery,
    (snapshot) => {
      const latest = snapshot.docs[0];
      setLatestDataExportRequest(latest ? { id: latest.id, ...latest.data() } : null);
    },
    (error) => {
      console.error("data export status listener failed:", error?.code || error?.message || "unknown");
      setLatestDataExportRequest(null);
    }
  );

  return () => unsubscribe();
}, [currentUid]);

const dataExportStatus = latestDataExportRequest?.status || "idle";
const dataExportInProgress = ["queued", "processing"].includes(dataExportStatus);
const dataExportRetryDate = useMemo(() => {
  const source =
    latestDataExportRequest?.requestedAt ||
    latestDataExportRequest?.createdAt ||
    latestDataExportRequest?.updatedAt;
  let date = null;
  if (typeof source?.toDate === "function") {
    date = source.toDate();
  } else if (typeof source === "number" || typeof source === "string") {
    date = new Date(source);
  }

  if (!date || Number.isNaN(date.getTime())) return null;
  return new Date(date.getTime() + (24 * 60 * 60 * 1000));
}, [latestDataExportRequest]);

const dataExportRetryText = dataExportRetryDate
  ? dataExportRetryDate.toLocaleString(language === "de" ? "de-DE" : "en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  : "";

const handleRequestDataExport = async () => {
  const user = auth.currentUser;
  const password = String(dataExportPassword || "");

  if (!user?.email) {
    setDataExportMessage(t("menu.account.dataExport.noEmail"));
    return;
  }

  if (!dataExportUsesApple && !appleOnlyDataExportUnavailable && !password.trim()) {
    setDataExportMessage(t("menu.account.dataExport.passwordRequired"));
    return;
  }

  if (appleOnlyDataExportUnavailable) {
    setDataExportMessage(t("menu.account.dataExport.iosRequired"));
    return;
  }

  setRequestingDataExport(true);
  setDataExportMessage("");

  try {
    const authorization = await requestProviderAwareDataExport({
      firebaseUser: user,
      password,
      language,
      requestExport: requestMyDataExportSecure,
    });
    if (authorization.cancelled) return;
    const result = authorization.result;
    setDataExportPassword("");
    setDataExportMessage(
      result?.data?.message || t("menu.account.dataExport.requested")
    );
  } catch (error) {
    console.error("request data export failed:", error?.code || error?.message || "unknown");
    const code = String(error?.code || "");
    if (code.includes("APPLE_AUTH_CANCELLED")) {
      return;
    } else if (code.includes("APPLE_NATIVE_REQUIRED")) {
      setDataExportMessage(t("menu.account.dataExport.iosRequired"));
    } else if (
      dataExportUsesApple &&
      (code.includes("APPLE_AUTH_") ||
        code.includes("invalid-credential") ||
        code.includes("missing-or-invalid-nonce") ||
        code.includes("UID_MISMATCH"))
    ) {
      setDataExportMessage(t("menu.account.dataExport.appleReauthFailed"));
    } else if (code.includes("wrong-password") || code.includes("invalid-credential")) {
      setDataExportMessage(t("menu.account.dataExport.reauthFailed"));
    } else if (isDataExportRecentAuthError(error)) {
      setDataExportMessage(t("menu.account.dataExport.recentAuthRequired"));
    } else if (code.includes("failed-precondition") || code.includes("resource-exhausted")) {
      setDataExportMessage(
        dataExportRetryText
          ? t("menu.account.dataExport.activeExistsWithRetry").replace("{{time}}", dataExportRetryText)
          : t("menu.account.dataExport.activeExists")
      );
    } else {
      setDataExportMessage(t("menu.account.dataExport.requestError"));
    }
  } finally {
    setRequestingDataExport(false);
  }
};

const handleDeleteAccount = async () => {
  if (deletingAccount) return;

  const user = auth.currentUser;
  const password = String(deleteAccountPassword || "");

  if (!user) {
    setDeleteAccountMessage(t("dashboard.deleteAccount.error"));
    return;
  }

  if (appleLinkedDeleteUnavailable) {
    setDeleteAccountMessage(t("dashboard.deleteAccount.iosRequired"));
    return;
  }

  if (!deleteAccountUsesApple && !password.trim()) {
    setDeleteAccountMessage(t("dashboard.deleteAccount.passwordRequired"));
    return;
  }

  setDeletingAccount(true);
  setDeleteAccountMessage("");

  try {
    const deletion = await deleteAccountWithProviderConfirmation({
      firebaseUser: user,
      password,
      deleteAccount: deleteMyAccountSecure,
    });
    if (deletion?.cancelled) {
      return;
    }

    const uid = user.uid;
    setBiometricLoginEnabledForUser(uid, false);
    setBiometricLoginEnabled(false);
    await auth.signOut();
    resetDeleteAccountModal();
    setDeleteAccountModalOpen(false);
  } catch (e) {
    console.error("Account deletion failed:", e?.code || e?.message || "unknown");
    const code = String(e?.code || "");
    if (code.includes("APPLE_AUTH_CANCELLED")) {
      return;
    }
    if (code.includes("APPLE_NATIVE_REQUIRED")) {
      setDeleteAccountMessage(t("dashboard.deleteAccount.iosRequired"));
    } else if (
      code.includes("APPLE_AUTH_") ||
      code.includes("APPLE_REVOKE") ||
      code.includes("APPLE_AUTHORIZATION_CODE") ||
      code.includes("failed-precondition")
    ) {
      setDeleteAccountMessage(t("dashboard.deleteAccount.appleRevokeFailed"));
    } else if (code.includes("wrong-password") || code.includes("invalid-credential")) {
      setDeleteAccountMessage(t("dashboard.deleteAccount.reauthFailed"));
    } else if (code.includes("UID_MISMATCH")) {
      setDeleteAccountMessage(t("dashboard.deleteAccount.reauthFailed"));
    } else {
      setDeleteAccountMessage(t("dashboard.deleteAccount.error"));
    }
  } finally {
    setDeletingAccount(false);
  }
};


const handleOpenForeignProfile = (uid, backTarget = null, connectionDetails = null) => {
  if (!uid) return;

  setViewedProfileUid(uid);
  setViewedProfileConnectionDetails(connectionDetails || null);
  setProfileBackTarget(backTarget);
  setHomeSection("PROFILE");
  setView("LIST");
};

const getTournamentDayIdFromDateKey = (dateKey) => {
  const match = String(dateKey || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, yearRaw, monthRaw, dayRaw] = match;
  const target = new Date(Number(yearRaw), Number(monthRaw) - 1, Number(dayRaw));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0 || diffDays > 7) return null;
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  return `day${diffDays}`;
};

const handleOpenCreatorTournament = (requestOrTournamentId) => {
  const request =
    requestOrTournamentId && typeof requestOrTournamentId === "object"
      ? requestOrTournamentId
      : null;
  const dateKey = request?.requestedDate || request?.dateKey || "";
  const dayId = getTournamentDayIdFromDateKey(dateKey);

  if (dayId) {
    setSelectedDayKey(dayId);
  }

  setSelectedTournamentId(null);
  setSelectedTournament(null);
  setView("LIST");
  setHomeSection("TOURNAMENTS");
  setTournamentsSectionKey((key) => key + 1);
};

const buildTournamentDetailFallback = (tournamentId, tournament = {}, options = {}) => {
  const id = String(tournamentId || "").trim();
  const includeCurrentUser = options.includeCurrentUser === true;
  const parts = id.split("_");
  const dateKey = tournament.dateKey || tournament.dayKey || parts[0] || "";
  const baseTournamentId =
    tournament.tournamentId && tournament.tournamentId !== id
      ? tournament.tournamentId
      : parts.length > 1
      ? parts.slice(1).join("_")
      : tournament.id || id;
  const players = Array.isArray(tournament.players) ? tournament.players : [];
  const nextPlayers =
    includeCurrentUser && currentUid && !players.includes(currentUid)
      ? [...players, currentUid]
      : players;
  const fallbackStartAt = (() => {
    if (tournament.startAt) return tournament.startAt;
    if (!dateKey || !tournament.time) return null;

    const [year, month, day] = String(dateKey).split("-").map(Number);
    const [hour, minute] = String(tournament.time).split(":").map(Number);
    if (![year, month, day, hour, minute].every(Number.isFinite)) return null;

    const date = new Date(year, month - 1, day, hour, minute, 0, 0);
    return { toDate: () => date };
  })();

  return {
    ...tournament,
    id,
    dateKey,
    tournamentId: baseTournamentId,
    status: tournament.status || "open",
    players: nextPlayers,
    maxPlayers: tournament.maxPlayers || 0,
    startAt: fallbackStartAt,
  };
};

const handleOpenTournamentDetail = (tournamentId, tournament = {}, options = {}) => {
  const id = String(tournamentId || "").trim();
  if (!id) return;

  const includeCurrentUser = options?.includeCurrentUser === true;
  const fallback = buildTournamentDetailFallback(id, tournament || {}, { includeCurrentUser });
  lobbyTournamentFallbackRef.current = fallback;
  setSelectedTournament((prev) => {
    const current =
      prev && prev.id === id
        ? {
            ...fallback,
            ...prev,
          }
        : fallback;
    const players = Array.isArray(current.players) ? current.players : [];

    return {
      ...current,
      players:
        includeCurrentUser && currentUid && !players.includes(currentUid)
          ? [...players, currentUid]
          : players,
    };
  });
  setLobbyPlayers((prev) => {
    const players = Array.isArray(prev) && prev.length > 0
      ? prev
      : fallback.players || [];

    return includeCurrentUser && currentUid && !players.includes(currentUid)
      ? [...players, currentUid]
      : players;
  });
  setSelectedTournamentId(id);
  setHomeSection("TOURNAMENTS");
  setTournamentTab(includeCurrentUser ? "match" : "bracket");
  setView("DETAIL");
};

const handleOpenTournamentOverviewCard = (tournament = {}) => {
  const tournamentId = getDashboardTournamentId(tournament);
  const dateKey = String(
    tournament.dateKey ||
      tournament.requestedDate ||
      tournament.dayKey ||
      tournament.date ||
      tournament.startDate ||
      tournamentId.split("_")[0] ||
      ""
  ).trim();
  const dayId = getTournamentDayIdFromDateKey(dateKey);

  if (dayId) {
    setSelectedDayKey(dayId);
  }

  setSelectedTournamentId(null);
  setSelectedTournament(null);
  setView("LIST");
  setHomeSection("TOURNAMENTS");
  setTournamentsSectionKey((key) => key + 1);
};

useEffect(() => {
  if (loadingProfile || !currentUid || pendingPushNavigationConsumedRef.current) return;
  pendingPushNavigationConsumedRef.current = true;

  const pending = consumePendingPushNavigation(
    typeof window !== "undefined" ? window.sessionStorage : null
  );
  if (!pending) return;

  let cancelled = false;
  const openPendingTournament = async () => {
    try {
      const tournamentSnap = await getDoc(doc(db, "tournaments", pending.tournamentId));
      if (cancelled) return;

      if (tournamentSnap.exists()) {
        const tournament = { id: tournamentSnap.id, ...tournamentSnap.data() };
        const status = String(tournament.status || "open").toLowerCase();
        if (!DASHBOARD_TERMINAL_TOURNAMENT_STATUSES.has(status)) {
          handleOpenTournamentDetail(pending.tournamentId, tournament);
          return;
        }
      } else {
        const dailyPrizeSnap = await getDoc(doc(db, "dailyPrizes", pending.dateKey));
        if (cancelled) return;
        const special = dailyPrizeSnap.exists() ? dailyPrizeSnap.data()?.special : null;
        const specialStatus = String(special?.status || "open").toLowerCase();
        const unavailable = !special ||
          special.enabled === false ||
          special.active === false ||
          special.disabled === true ||
          special.deleted === true ||
          DASHBOARD_TERMINAL_TOURNAMENT_STATUSES.has(specialStatus) ||
          Boolean(special.deletedAt || special.tournamentDeletedAt || special.removedAt);

        if (!unavailable) {
          handleOpenTournamentDetail(pending.tournamentId, {
            id: pending.tournamentId,
            dateKey: pending.dateKey,
            tournamentId: "special",
            name: special.title || special.name || "Special Cup",
            time: special.startTime || special.time || "20:00",
            maxPlayers: Number(special.maxPlayers || 0),
            entryCoins: Number(special.entryCoins || 0),
            status: "open",
          });
          return;
        }
      }
    } catch (error) {
      console.warn("Pending push navigation could not open tournament.", {
        code: error?.code || "unknown",
      });
    }

    if (!cancelled) {
      handleOpenTournamentOverviewCard({ dateKey: pending.dateKey });
    }
  };

  openPendingTournament();
  return () => {
    cancelled = true;
  };
  // The pending payload must be consumed exactly once after auth/profile initialization.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [currentUid, loadingProfile]);

const handleUnlockPrivateTournament = async (code) => {
  const result = await unlockPrivateTournamentByCodeSecure({ code });
  const tournament = result?.data?.tournament || {};
  const tournamentId = String(tournament.id || "").trim();
  const dateKey = tournament.dateKey || tournament.requestedDate || "";

  if (tournamentId && currentUid) {
    setTournaments((current) =>
      (Array.isArray(current) ? current : []).map((entry) => {
        if (entry.id !== tournamentId) return entry;
        const accessUids = Array.isArray(entry.privateAccessUids)
          ? entry.privateAccessUids
          : [];
        return {
          ...entry,
          privateAccessUids: accessUids.includes(currentUid)
            ? accessUids
            : [...accessUids, currentUid],
        };
      })
    );
  }

  const dayId = getTournamentDayIdFromDateKey(dateKey);
  if (!dayId) {
    return {
      message: t("community.privateTournaments.outsideCalendar"),
    };
  }

  setSelectedDayKey(dayId);
  setSelectedTournamentId(null);
  setSelectedTournament(null);
  setView("LIST");
  setHomeSection("TOURNAMENTS");
  setTournamentsSectionKey((key) => key + 1);
  return { success: true };
};

const handleSendFriendRequest = async (toUid) => {
  if (!toUid || !currentUid) return;

  if (toUid === currentUid) {
    setInfoModalTitle(t("dashboard.friendRequest.notPossibleTitle"));
setInfoModalText(t("dashboard.friendRequest.cannotAddSelf"));
    setInfoModalOpen(true);
    return;
  }

  try {
    const friendRef = doc(db, "users", currentUid, "friends", toUid);
    const friendSnap = await getDoc(friendRef);

    if (friendSnap.exists()) {
     setInfoModalTitle(t("dashboard.friendRequest.alreadyFriendsTitle"));
setInfoModalText(t("dashboard.friendRequest.alreadyFriendsText"));
      setInfoModalOpen(true);
      return;
    }

    const res = await sendFriendRequestSecure({ toUid });

    if (res.data.status === "already_friends") {
      setInfoModalTitle(t("dashboard.friendRequest.alreadyFriendsTitle"));
setInfoModalText(t("dashboard.friendRequest.alreadyFriendsText"));
      setInfoModalOpen(true);
      return;
    }

    if (res.data.status === "already_pending") {
      setInfoModalTitle(t("dashboard.friendRequest.alreadyPendingTitle"));
setInfoModalText(t("dashboard.friendRequest.alreadyPendingText"));
      setInfoModalOpen(true);
      return;
    }

    setInfoModalTitle(t("dashboard.friendRequest.sentTitle"));
setInfoModalText(t("dashboard.friendRequest.sentText"));
    setInfoModalOpen(true);
  } catch (e) {
    console.error("sendFriendRequestSecure failed:", e?.code || e?.message || "unknown");

    const msg = String(e?.message || "");
    const code = String(e?.code || "").replace("functions/", "");

    if (code === "already-exists" || msg.toLowerCase().includes("already")) {
      setInfoModalTitle(t("dashboard.friendRequest.alreadyExistsTitle"));
setInfoModalText(t("dashboard.friendRequest.alreadyExistsText"));
    } else if (code === "invalid-argument") {
      setInfoModalTitle(t("dashboard.friendRequest.notPossibleTitle"));
setInfoModalText(t("dashboard.friendRequest.invalidText"));
    } else if (code === "permission-denied") {
      setInfoModalTitle(t("dashboard.friendRequest.notAllowedTitle"));
setInfoModalText(t("dashboard.friendRequest.notAllowedText"));
    } else {
      setInfoModalTitle(t("common.error"));
setInfoModalText(t("dashboard.friendRequest.errorText"));
    }

    setInfoModalOpen(true);
  }
};




 const showWatermark =
  (view === "LIST" && homeSection === null) ||
  view === "DETAIL";


const myMatch = useMemo(() => {
  if (!currentUid) return null;
  const groupStageActive =
    selectedTournament?.groupStageEnabled === true &&
    String(selectedTournament?.groupStageStatus || "").toLowerCase() !== "completed" &&
    !["running", "completed"].includes(String(selectedTournament?.knockoutStatus || "").toLowerCase());

  return selectCurrentTournamentMatch({ matches, currentUid, groupStageActive });
}, [matches, currentUid, selectedTournament]);

const surveyBlockedByActiveMatch = isSurveyBlockedByMatchState({
  dashboardActiveSingleMatch,
  dashboardTournamentMatches,
  view,
  selectedTournamentStatus: selectedTournament?.status,
  myMatchStatus: myMatch?.status,
});

useEffect(() => {
  if (surveyBlockedByActiveMatch && dueSurvey) {
    setDueSurvey(null);
  }
}, [surveyBlockedByActiveMatch, dueSurvey]);

useEffect(() => {
  surveySessionGuardRef.current = false;
  surveyCheckInFlightRef.current = false;
  surveyOpenInFlightRef.current = false;
  setDueSurvey(null);
  setSurveyAvailability({ survey: null, dueSurveyCount: 0 });
}, [currentUid]);

useEffect(() => {
  const requestSurveyCheck = () => {
    surveySessionGuardRef.current = false;
    setSurveyCheckTick((value) => value + 1);
  };
  const handleVisibility = () => {
    if (!document.hidden) requestSurveyCheck();
  };
  window.addEventListener("focus", requestSurveyCheck);
  document.addEventListener("visibilitychange", handleVisibility);
  return () => {
    window.removeEventListener("focus", requestSurveyCheck);
    document.removeEventListener("visibilitychange", handleVisibility);
  };
}, []);

useEffect(() => {
  if (!currentUid || loadingProfile || !isAppActive) return undefined;
  const timer = window.setInterval(() => {
    setSurveyCheckTick((value) => value + 1);
  }, 3 * 60 * 1000);
  return () => window.clearInterval(timer);
}, [currentUid, loadingProfile, isAppActive]);

useEffect(() => {
  if (!currentUid || loadingProfile || !isAppActive || surveyCheckInFlightRef.current) return;
  let cancelled = false;
  const requestedUid = currentUid;
  surveyCheckInFlightRef.current = true;
  getDueSurvey({})
    .then((response) => {
      if (cancelled || auth.currentUser?.uid !== requestedUid) return;
      setSurveyAvailability({
        survey: response.data?.survey || null,
        dueSurveyCount: Number(response.data?.dueSurveyCount || 0),
      });
    })
    .catch((error) => {
      console.error("getDueSurveySecure failed:", error?.code || error?.message || "unknown");
    })
    .finally(() => {
      surveyCheckInFlightRef.current = false;
    });
  return () => { cancelled = true; };
}, [currentUid, loadingProfile, isAppActive, surveyCheckTick, getDueSurvey]);

const openDueSurvey = useCallback(async (surveyId = "") => {
  if (
    !currentUid ||
    surveyOpenInFlightRef.current ||
    surveyBlockedByActiveMatch ||
    priorityNotification
  ) return "blocked";
  surveyOpenInFlightRef.current = true;
  const requestedUid = currentUid;
  try {
    const response = await getDueSurvey(surveyId ? { surveyId } : {});
    if (auth.currentUser?.uid !== requestedUid) return false;
    const survey = response.data?.survey || null;
    setSurveyAvailability({
      survey: surveyId ? surveyAvailability.survey : survey,
      dueSurveyCount: Number(response.data?.dueSurveyCount || 0),
    });
    if (!survey) return false;
    await markSurveyShown({ surveyId: survey.id });
    if (auth.currentUser?.uid !== requestedUid) return false;
    setNotificationsOpen(false);
    setNotificationDetailOpen(false);
    setDueSurvey(survey);
    return true;
  } catch (error) {
    console.error("Survey could not be opened:", error?.code || error?.message || "unknown");
    return false;
  } finally {
    surveyOpenInFlightRef.current = false;
  }
}, [
  currentUid,
  getDueSurvey,
  markSurveyShown,
  priorityNotification,
  surveyAvailability.survey,
  surveyBlockedByActiveMatch,
]);

useEffect(() => {
  if (!isAppActive || !canOpenSurveyPrompt({
    currentUid,
    loadingProfile,
    dueSurvey,
    surveyBusy,
    checkInFlight: surveyOpenInFlightRef.current,
    sessionGuard: surveySessionGuardRef.current,
    blockedByMatch: surveyBlockedByActiveMatch,
    blockingModalOpen: otherBlockingDashboardModalOpen,
    priorityNotification,
    menuOpen,
    notificationsOpen,
  }) || !surveyAvailability.survey) return;
  openDueSurvey(surveyAvailability.survey.id);
}, [
  currentUid,
  isAppActive,
  loadingProfile,
  dueSurvey,
  surveyBusy,
  surveyBlockedByActiveMatch,
  otherBlockingDashboardModalOpen,
  priorityNotification,
  menuOpen,
  notificationsOpen,
  surveyAvailability.survey,
  openDueSurvey,
  homeSection,
  view,
]);

const handleSurveyDefer = async () => {
  if (!dueSurvey?.id || surveyBusy) return;
  setSurveyBusy(true);
  try {
    await deferSurvey({ surveyId: dueSurvey.id });
    surveySessionGuardRef.current = true;
    setDueSurvey(null);
    setSurveyAvailability({ survey: null, dueSurveyCount: 0 });
    setSurveyCheckTick((value) => value + 1);
  } catch (error) {
    console.error("deferSurveySecure failed:", error?.code || error?.message || "unknown");
    setInfoModalTitle(t("common.error"));
    setInfoModalText(t("surveys.submitError"));
    setInfoModalOpen(true);
  } finally {
    setSurveyBusy(false);
  }
};

const handleSurveySubmit = async (answers, otherTexts) => {
  if (!dueSurvey?.id || surveyBusy) return;
  setSurveyBusy(true);
  try {
    await submitSurvey({ surveyId: dueSurvey.id, answers, otherTexts });
    surveySessionGuardRef.current = false;
    setDueSurvey(null);
    setSurveyAvailability({ survey: null, dueSurveyCount: 0 });
    setSurveyCheckTick((value) => value + 1);
    setInfoModalTitle(t("surveys.thanks"));
    setInfoModalText("");
    setInfoModalOpen(true);
  } catch (error) {
    console.error("submitSurveySecure failed:", error?.code || error?.message || "unknown");
    setInfoModalTitle(t("common.error"));
    setInfoModalText(t("surveys.submitError"));
    setInfoModalOpen(true);
  } finally {
    setSurveyBusy(false);
  }
};




 const latestMyMatchRaw = useMemo(() => {
  if (!currentUid) return null;
  const groupStageActive =
    selectedTournament?.groupStageEnabled === true &&
    String(selectedTournament?.groupStageStatus || "").toLowerCase() !== "completed" &&
    !["running", "completed"].includes(String(selectedTournament?.knockoutStatus || "").toLowerCase());

  const myMatches = matches.filter((m) => {
    const isOwnMatch = m?.player1Uid === currentUid || m?.player2Uid === currentUid;
    if (!isOwnMatch) return false;
    const phase = String(m?.phase || "").toLowerCase();
    const isGroupMatch = phase === "group" || !!m?.groupId;
    return groupStageActive ? isGroupMatch : !isGroupMatch;
  });

  if (myMatches.length === 0) return null;

  const statusPrio = (s) => {
    const status = String(s || "").toLowerCase();
    if (status === "confirmed" || status === "finished" || status === "cancelled") return 4;
    if (status === "disputed") return 3;
    if (status === "reported") return 2;
    if (status === "check_in_open") return 2;
    if (status === "pending") return 1;
    return 0;
  };

  myMatches.sort((a, b) => {
    if (groupStageActive) {
      const groupRoundDiff = Number(a?.groupRound || 0) - Number(b?.groupRound || 0);
      if (groupRoundDiff !== 0) return groupRoundDiff;
      return Number(a?.groupMatchIndex || 0) - Number(b?.groupMatchIndex || 0);
    }
    const roundDiff = Number(b?.roundNumber || 0) - Number(a?.roundNumber || 0);
    if (roundDiff !== 0) return roundDiff;
    const matchIndexDiff = Number(b?.matchIndex || 0) - Number(a?.matchIndex || 0);
    if (matchIndexDiff !== 0) return matchIndexDiff;
    const legDiff = Number(b?.legNumber || 0) - Number(a?.legNumber || 0);
    if (legDiff !== 0) return legDiff;
    return statusPrio(b?.status) - statusPrio(a?.status);
  });
  return myMatches[0];
}, [matches, currentUid, selectedTournament]);



  const bothConfirmed = (m) => {
    const confirmedBy = Array.isArray(m?.confirmedBy) ? m.confirmedBy : [];
    return (
      m?.player1Uid &&
      m?.player2Uid &&
      confirmedBy.includes(m.player1Uid) &&
      confirmedBy.includes(m.player2Uid)
    );
  };

useEffect(() => {
  if (view !== "DETAIL") return;
  if (!selectedTournament) return;

  const target = selectedTournament?.startAt?.toDate?.() || null;
  if (!target) return;

  const pad = (n) => String(n).padStart(2, "0");

  const tick = () => {
    const now = new Date();
    const diff = target.getTime() - now.getTime();

    if (diff <= 0) {
      setCountdownText("00:00:00");
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    setCountdownText(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
  };

  tick();
  const interval = setInterval(tick, 1000);

  return () => clearInterval(interval);
}, [view, selectedTournament]);


  // Join-Message automatisch ausblenden
  useEffect(() => {
    if (!joinMessage) return;

    const timer = setTimeout(() => {
      setJoinMessage("");
    }, 3000);

    return () => clearTimeout(timer);
  }, [joinMessage]);

  // 1) Live-Profil (users/{uid})
  useEffect(() => {
    const user = auth.currentUser;

    if (!user) {
      setLoadingProfile(false);
      return;
    }

    const ref = doc(db, "users", user.uid);

    const unsubscribe = onSnapshot(ref, (snap) => {
      setProfile(snap.exists() ? snap.data() : null);
      setLoadingProfile(false);
    });

    return () => unsubscribe();
  }, []);


useEffect(() => {
  setAccountConsoleType(String(profile?.consoleType || profile?.console || profile?.platform || ""));
  setAccountConsoleId(String(profile?.consoleId || ""));
  setAccountEaId(String(profile?.gameAccounts?.fc?.eaId || profile?.eaId || ""));
  setAccountAgeGroup(String(profile?.age || ""));
  setAccountAgeMessage("");
}, [profile]);


const saveAccountGamingProfile = async () => {
  const user = auth.currentUser;
  if (!user) return;

  setSavingAccountProfile(true);
  if (accountSaveMessageTimerRef.current) {
    clearTimeout(accountSaveMessageTimerRef.current);
    accountSaveMessageTimerRef.current = null;
  }
  setAccountSaveMessage("");

  try {
    await updateDoc(doc(db, "users", user.uid), {
      consoleType: String(accountConsoleType || "").trim(),
      consoleId: String(accountConsoleId || "").trim(),
      "gameAccounts.fc.eaId": String(accountEaId || "").trim(),
      updatedAt: serverTimestamp(),
    });
    setAccountSaveMessage(t("menu.account.saved"));
    accountSaveMessageTimerRef.current = setTimeout(() => {
      setAccountSaveMessage("");
      accountSaveMessageTimerRef.current = null;
    }, 3000);
  } catch (error) {
    console.error("Failed to save gaming profile:", error?.code || error?.message || "unknown");
    setAccountSaveMessage(t("menu.account.saveError"));
  } finally {
    setSavingAccountProfile(false);
  }
};

const saveAccountAgeGroup = async () => {
  const user = auth.currentUser;
  if (!user || savingAccountAgeGroup) return;

  setSavingAccountAgeGroup(true);
  setAccountAgeMessage("");
  setAiConsentMessage("");

  try {
    const response = await updateAccountAgeGroupSecure({
      age: String(accountAgeGroup || "").trim(),
    });
    const nextAge = String(response?.data?.age || accountAgeGroup || "").trim();
    const aiConsentRevoked = response?.data?.aiConflictAnalysisConsentRevoked === true;

    setProfile((currentProfile) => {
      if (!currentProfile) return currentProfile;
      return {
        ...currentProfile,
        age: nextAge,
        ...(aiConsentRevoked
          ? {
              aiConflictAnalysisConsent: {
                ...currentProfile.aiConflictAnalysisConsent,
                granted: false,
                promptHandled: true,
              },
            }
          : {}),
      };
    });

    setAccountAgeGroup(nextAge);
    setAccountAgeMessage(
      aiConsentRevoked
        ? t("menu.account.ageGroup.savedAndConsentRevoked")
        : t("menu.account.ageGroup.saved")
    );
    if (aiConsentRevoked) {
      setAiConsentMessage(t("menu.account.aiConflictConsent.savedOff"));
    }
  } catch (error) {
    console.error("Failed to save age group:", error?.code || error?.message || "unknown");
    const reason = String(error?.message || error?.code || "");
    setAccountAgeMessage(
      reason.includes("INVALID_AGE_GROUP")
        ? t("menu.account.ageGroup.invalid")
        : t("menu.account.ageGroup.saveError")
    );
  } finally {
    setSavingAccountAgeGroup(false);
  }
};

const saveAiConflictAnalysisConsent = async (granted, { closePrompt = false } = {}) => {
  const user = auth.currentUser;
  if (!user || savingAiConsent) return false;
  if (granted === true && !aiConflictAnalysisConsentAvailable) {
    setAiConsentMessage(t("menu.account.aiConflictConsent.adultOnly"));
    return false;
  }

  setSavingAiConsent(true);
  setAiConsentMessage("");

  try {
    await updateAiConflictAnalysisConsentSecure({ granted: granted === true });
    setAiConsentPromptHandledLocalUid(user.uid);
    if (closePrompt) {
      setAiConsentPromptOpen(false);
    }
    setAiConsentMessage(
      granted
        ? t("menu.account.aiConflictConsent.savedOn")
        : t("menu.account.aiConflictConsent.savedOff")
    );
    return true;
  } catch (error) {
    console.error("Failed to update AI conflict-analysis consent:", error?.code || error?.message || "unknown");
    setAiConsentMessage(t("menu.account.aiConflictConsent.saveError"));
    return false;
  } finally {
    setSavingAiConsent(false);
  }
};




useEffect(() => {
  const checkActiveSingleMatch = async () => {
    const activeSingleMatchId = profile?.activeSingleMatchId || null;
    const uid = auth?.currentUser?.uid || null;

    // ersten Profil-Load nach Login ignorieren
    if (!didHandleInitialSingleMatchRef.current) {
      didHandleInitialSingleMatchRef.current = true;
      return;
    }

    if (!uid || !activeSingleMatchId) return;

    try {
      const matchRef = doc(db, "singleMatches", String(activeSingleMatchId));
      const matchSnap = await getDoc(matchRef);

      const clearStaleActiveMatch = httpsCallable(
        firebaseFunctions,
        "clearStaleActiveSingleMatchSecure"
      );

      // Match existiert nicht mehr → sicherer serverseitiger Cleanup
      if (!matchSnap.exists()) {
        await clearStaleActiveMatch({ matchId: String(activeSingleMatchId) });
        return;
      }

      const m = matchSnap.data() || {};
      const status = String(m.status || "");

      // Nur bei wirklich aktiven Matches reinspringen
      if (
  status === "check_in_open" ||
  status === "pending" ||
  status === "resume_pending" ||
  status === "reported" ||
  status === "disputed"
) {
        setNotificationsOpen(false);
        setHomeSection("SINGLES");
        setSingleStep("matchFound");
        return;
      }

      // Match ist finished/cancelled → sicherer serverseitiger Cleanup
      await clearStaleActiveMatch({ matchId: String(activeSingleMatchId) });

    } catch (e) {
      console.error("checkActiveSingleMatch failed:", e?.code || e?.message || "unknown");
    }
  };

  checkActiveSingleMatch();
}, [profile?.activeSingleMatchId]);




  // 1c) Live-Notifications (notifications/{uid}/items)
useEffect(() => {
  if (!currentUid) {
    setNotifications([]);
    setSelectedNotification(null);
    setPriorityNotification(null);
    return;
  }

  const notificationsRef = collection(db, "notifications", currentUid, "items");
  const notificationsQuery = query(
    notificationsRef,
    orderBy("createdAt", "desc")
  );

  const unsubscribe = onSnapshot(
  notificationsQuery,
  (snapshot) => {
    const items = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    setNotifications(items);

    setSelectedNotification((prevSelected) => {
      if (!items.length) return null;
      if (!prevSelected) return items[0];

      const stillExists = items.find((item) => item.id === prevSelected.id);
      return stillExists || items[0];
    });
  },
  (error) => {
    console.error("Notifications onSnapshot error:", error?.code || error?.message || "unknown");
    setNotifications([]);
  }
);

  return () => unsubscribe();
}, [currentUid]);

useEffect(() => {
  setPriorityNotification(null);
  setPriorityNotificationActionError("");
  setPriorityNotificationActionLoading(false);
  setPriorityPresenceActionUnavailable(false);
}, [currentUid]);

useEffect(() => {
  if (!currentUid || !isAppActive || priorityNotification || blockingDashboardModalOpen) return undefined;

  const processedIds = processedPriorityNotificationIdsByUidRef.current.get(currentUid) || new Set();
  const candidates = notifications.filter((item) =>
    item?.id && item?.isRead === false && shouldShowPriority1Notification(item) && !processedIds.has(item.id)
  );
  if (candidates.length === 0) return undefined;

  let cancelled = false;
  const loadAndSelect = async () => {
    let statesById = {};
    let serverTimeOffsetMs = 0;
    try {
      for (let index = 0; index < candidates.length; index += 100) {
        const chunk = candidates.slice(index, index + 100);
        const response = await getPriorityNotificationPopupStates({ notificationIds: chunk.map((item) => item.id) });
        statesById = { ...statesById, ...(response.data?.statesById || {}) };
        if (Number.isFinite(response.data?.serverNowMs)) {
          serverTimeOffsetMs = response.data.serverNowMs - Date.now();
        }
      }
    } catch (error) {
      console.error("Priority notification state resolution failed:", error?.code || error?.message || "unknown");
    }

    if (cancelled || !isAppActive || auth.currentUser?.uid !== currentUid) return;
    const selected = selectPriorityNotificationPopup({
      notifications: candidates,
      statesById,
      currentUid,
      processedIds,
      nowMs: Date.now(),
    });
    if (!selected?.notification) return;

    const accountProcessedIds = processedPriorityNotificationIdsByUidRef.current.get(currentUid) || new Set();
    (selected.supersededNotificationIds || [selected.notification.id]).forEach((id) => accountProcessedIds.add(id));
    processedPriorityNotificationIdsByUidRef.current.set(currentUid, accountProcessedIds);
    setPriorityNotification({
      ...selected.notification,
      _priorityPopupState: statesById[selected.notification.id] || {},
      _serverTimeOffsetMs: serverTimeOffsetMs,
    });
    setPriorityPresenceActionUnavailable(false);
  };
  loadAndSelect();
  return () => { cancelled = true; };
}, [
  blockingDashboardModalOpen,
  currentUid,
  getPriorityNotificationPopupStates,
  isAppActive,
  notifications,
  priorityNotification,
]);

const priorityPresenceStatus = priorityNotification?.type === "presence_confirmation_required"
  ? getPresencePriorityPopupStatus({
      notification: priorityNotification,
      currentUid,
      state: priorityNotification._priorityPopupState || {},
      nowMs: priorityPresenceNowMs,
    })
  : null;
const priorityPresenceCountdown = getPresencePriorityCountdown(
  priorityPresenceStatus?.deadlineMs,
  priorityPresenceNowMs
);
const priorityPresenceExpired = priorityPresenceStatus?.status === "expired" || priorityPresenceCountdown.expired;
const priorityPresenceUnavailable = priorityPresenceExpired || priorityPresenceActionUnavailable;
const priorityPresenceServerOffsetMs = Number(priorityNotification?._serverTimeOffsetMs || 0);
const priorityNotificationId = priorityNotification?.id || null;
const priorityNotificationType = priorityNotification?.type || null;
const priorityNotificationPresenceCycle = priorityNotification?.presenceCycle || null;
const priorityNotificationLifecycleVersion = priorityNotification?.matchLifecycleVersion || null;

useEffect(() => {
  if (priorityNotification?.type !== "presence_confirmation_required" || !isAppActive) return undefined;
  setPriorityPresenceNowMs(Date.now() + priorityPresenceServerOffsetMs);
  const timer = window.setInterval(
    () => setPriorityPresenceNowMs(Date.now() + priorityPresenceServerOffsetMs),
    1000
  );
  return () => window.clearInterval(timer);
}, [isAppActive, priorityNotification?.id, priorityNotification?.type, priorityPresenceServerOffsetMs]);

useEffect(() => {
  if (
    priorityNotificationType !== "presence_confirmation_required" ||
    !priorityNotificationId || !currentUid || !isAppActive
  ) return undefined;

  let cancelled = false;
  const refreshPresenceState = async () => {
    try {
      const response = await getPriorityNotificationPopupStates({
        notificationIds: [priorityNotificationId],
      });
      const nextServerTimeOffsetMs = Number.isFinite(response.data?.serverNowMs)
        ? response.data.serverNowMs - Date.now()
        : priorityPresenceServerOffsetMs;
      const resolvedServerTimeOffsetMs = Math.abs(nextServerTimeOffsetMs - priorityPresenceServerOffsetMs) > 1000
        ? nextServerTimeOffsetMs
        : priorityPresenceServerOffsetMs;
      const nextState = response.data?.statesById?.[priorityNotificationId] || {};
      if (cancelled || auth.currentUser?.uid !== currentUid) return;
      const nextStatus = getPresencePriorityPopupStatus({
        notification: {
          presenceCycle: priorityNotificationPresenceCycle,
          matchLifecycleVersion: priorityNotificationLifecycleVersion,
        },
        currentUid,
        state: nextState,
        nowMs: Date.now() + resolvedServerTimeOffsetMs,
      });
      if (nextStatus.status === "obsolete") {
        setPriorityNotification(null);
        setPriorityNotificationActionLoading(false);
        setPriorityNotificationActionError("");
        setPriorityPresenceActionUnavailable(false);
        return;
      }
      setPriorityNotification((current) => current?.id === priorityNotificationId
        ? { ...current, _priorityPopupState: nextState, _serverTimeOffsetMs: resolvedServerTimeOffsetMs }
        : current);
      setPriorityPresenceNowMs(Date.now() + resolvedServerTimeOffsetMs);
    } catch (error) {
      console.error("Priority presence state refresh failed:", error?.code || error?.message || "unknown");
    }
  };

  refreshPresenceState();
  const refreshTimer = window.setInterval(refreshPresenceState, priorityPresenceExpired ? 2000 : 5000);
  return () => {
    cancelled = true;
    window.clearInterval(refreshTimer);
  };
}, [
  currentUid,
  getPriorityNotificationPopupStates,
  isAppActive,
  priorityNotificationId,
  priorityNotificationLifecycleVersion,
  priorityNotificationPresenceCycle,
  priorityNotificationType,
  priorityPresenceExpired,
  priorityPresenceServerOffsetMs,
]);



useEffect(() => {
  if (selectedNotification?.type !== "single_challenge_invite") {
    setChallengeCountdownText("");
    return;
  }

  const expiresAt = selectedNotification?.expiresAt || null;
  if (!expiresAt || typeof expiresAt.toDate !== "function") {
    setChallengeCountdownText("");
    return;
  }

  const updateCountdown = () => {
    const expiresAtDate = expiresAt.toDate();
    const diffMs = expiresAtDate.getTime() - Date.now();

    if (diffMs <= 0) {
      setChallengeCountdownText("00:00");
      return;
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    const mm = String(minutes).padStart(2, "0");
    const ss = String(seconds).padStart(2, "0");

    setChallengeCountdownText(`${mm}:${ss}`);
  };

  updateCountdown();
  const interval = setInterval(updateCountdown, 1000);

  return () => clearInterval(interval);
}, [selectedNotification]);

  // 2) Live-Turniere (tournaments)
  useEffect(() => {
    if (!currentUid) {
      setTournaments([]);
      return undefined;
    }

    const toMillis = (value) => {
      if (value && typeof value.toMillis === "function") return value.toMillis();
      if (value && typeof value.toDate === "function") return value.toDate().getTime();
      return 0;
    };

    const sortTournaments = (items) =>
      [...items].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

    const tournamentsRef = collection(db, "tournaments");

    if (hasAdminAccess) {
      const q = query(tournamentsRef, orderBy("createdAt", "desc"));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setTournaments(items);
      });

      return () => unsubscribe();
    }

    const participantQuery = query(
      tournamentsRef,
      where("players", "array-contains", currentUid)
    );
    const unsubscribe = onSnapshot(participantQuery, (snapshot) => {
      const items = sortTournaments(
        snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
      setTournaments(items);
    });

    return () => unsubscribe();
  }, [currentUid, hasAdminAccess]);

  const dashboardDailyPrizeKeys = useMemo(
    () => getDashboardDailyPrizeDateKeys(dashboardNow).join("|"),
    [dashboardNow]
  );

  useEffect(() => {
    if (!currentUid) {
      setDashboardDailySpecialCups([]);
      return undefined;
    }

    let cancelled = false;
    const dateKeys = dashboardDailyPrizeKeys.split("|").filter(Boolean);

    const loadUpcomingSpecialCups = async () => {
      try {
        const entries = await Promise.all(
          dateKeys.map(async (dateKey) => {
            const snapshot = await getDoc(doc(db, "dailyPrizes", dateKey));
            const special = snapshot.exists() ? snapshot.data()?.special : null;
            if (!isActiveDashboardDailyPrizeSpecial(special)) return null;
            return buildDashboardSpecialCupFromDailyPrize(dateKey, special);
          })
        );

        if (!cancelled) {
          setDashboardDailySpecialCups(entries.filter(Boolean));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load upcoming daily special cups:", error?.code || error?.message || "unknown");
          setDashboardDailySpecialCups([]);
        }
      }
    };

    loadUpcomingSpecialCups();

    return () => {
      cancelled = true;
    };
  }, [currentUid, dashboardDailyPrizeKeys]);

  useEffect(() => {
    const interval = setInterval(() => setDashboardNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!currentUid) {
      setDashboardActiveSingleMatch(null);
      return undefined;
    }

    const activeSingleMatchId = normalizeDashboardId(profile?.activeSingleMatchId);
    if (!activeSingleMatchId) {
      setDashboardActiveSingleMatch(null);
      return undefined;
    }

    const matchRef = doc(db, "singleMatches", activeSingleMatchId);
    const unsubscribe = onSnapshot(
      matchRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setDashboardActiveSingleMatch(null);
          return;
        }

        const data = { id: snapshot.id, ...snapshot.data() };
        setDashboardActiveSingleMatch(
          isDashboardActiveMatch(data, currentUid) ? data : null
        );
      },
      (error) => {
        console.error("dashboard single match listener failed:", error?.code || error?.message || "unknown");
        setDashboardActiveSingleMatch(null);
      }
    );

    return () => unsubscribe();
  }, [currentUid, profile?.activeSingleMatchId]);

  useEffect(() => {
    if (!currentUid) {
      setDashboardTournamentMatches([]);
      return undefined;
    }

    const candidateTournaments = tournaments
      .filter((tournament) => String(tournament?.status || "").toLowerCase() === "running")
      .filter((tournament) => tournamentIncludesUid(tournament, currentUid))
      .map((tournament) => ({
        id: getDashboardTournamentId(tournament),
        tournament,
      }))
      .filter((item) => item.id);

    if (candidateTournaments.length === 0) {
      setDashboardTournamentMatches([]);
      return undefined;
    }

    const matchesByTournament = new Map();
    const updateMatches = () => {
      setDashboardTournamentMatches(
        Array.from(matchesByTournament.values())
          .flat()
          .sort((a, b) => timestampToMillis(b.updatedAt || b.createdAt) - timestampToMillis(a.updatedAt || a.createdAt))
      );
    };

    const unsubscribers = candidateTournaments.map(({ id, tournament }) =>
      onSnapshot(
        collection(db, "tournaments", id, "matches"),
        (snapshot) => {
          const ownMatches = snapshot.docs
            .map((matchDoc) => ({ id: matchDoc.id, tournamentId: id, tournament, ...matchDoc.data() }))
            .filter((match) => isDashboardActiveMatch(match, currentUid));
          matchesByTournament.set(id, ownMatches);
          updateMatches();
        },
        (error) => {
          console.error("dashboard tournament match listener failed:", error?.code || error?.message || "unknown");
          matchesByTournament.set(id, []);
          updateMatches();
        }
      )
    );

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [currentUid, tournaments]);

  // Prefetch only participants that are actually visible in active match cards.
  useEffect(() => {
    const participantUids = new Set();

    (dashboardTournamentMatches || []).forEach((match) => {
      if (match?.player1Uid) participantUids.add(String(match.player1Uid).trim());
      if (match?.player2Uid) participantUids.add(String(match.player2Uid).trim());
    });

    if (dashboardActiveSingleMatch?.player1Uid) {
      participantUids.add(String(dashboardActiveSingleMatch.player1Uid).trim());
    }
    if (dashboardActiveSingleMatch?.player2Uid) {
      participantUids.add(String(dashboardActiveSingleMatch.player2Uid).trim());
    }

    let active = true;
    prefetchUsernames([...participantUids]).then((names) => {
      if (!active || Object.keys(names).length === 0) return;
      setUsernamesByUid((previous) => ({ ...previous, ...names }));
    });
    return () => { active = false; };
  }, [dashboardTournamentMatches, dashboardActiveSingleMatch]);

  // 3) Live: ausgewähltes Turnier laden
  useEffect(() => {
    if (!selectedTournamentId) {
      lobbyTournamentFallbackRef.current = null;
      setSelectedTournament(null);
      return;
    }

    const ref = doc(db, "tournaments", selectedTournamentId);

    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const fallback =
            lobbyTournamentFallbackRef.current?.id === snap.id
              ? lobbyTournamentFallbackRef.current
              : {};
          setSelectedTournament({ ...fallback, id: snap.id, ...snap.data() });
        } else {
          setSelectedTournament(null);
        }
      },
      (err) => {
        console.error("Selected tournament onSnapshot error:", err?.code || err?.message || "unknown");
        setSelectedTournament(null);
      }
    );

    return () => unsubscribe();
  }, [selectedTournamentId]);

    // 4) Live: Matches des ausgewählten Turniers laden (Subcollection)
  useEffect(() => {
    if (!selectedTournamentId) {
      setMatches([]);
      return;
    }

    const matchesRef = collection(
      db,
      "tournaments",
      selectedTournamentId,
      "matches"
    );

    const unsubscribe = onSnapshot(
      matchesRef,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMatches(sortTournamentMatches(items));
      },
      (err) => {
        console.error("Tournament matches onSnapshot error:", err?.code || err?.message || "unknown");
        setMatches([]);
      }
    );

    return () => unsubscribe();
  }, [selectedTournamentId]);

  // Critical live path: keep the user's active match subscribed directly.
  // The collection listener above is still the source for the bracket, but this
  // prevents report/no-show/response updates from being missed during reconnects
  // or while large tournament match collections are refreshing.
  useEffect(() => {
    const tournamentId = String(selectedTournamentId || "").trim();
    const matchId = String(myMatch?.id || "").trim();

    if (!tournamentId || !matchId) return undefined;

    const matchRef = doc(db, "tournaments", tournamentId, "matches", matchId);

    const unsubscribe = onSnapshot(
      matchRef,
      (snap) => {
        if (!snap.exists()) {
          setMatches((prev) => prev.filter((match) => match?.id !== matchId));
          return;
        }

        const nextMatch = { id: snap.id, ...snap.data() };

        setMatches((prev) => {
          const exists = prev.some((match) => match?.id === matchId);
          const next = exists
            ? prev.map((match) => (match?.id === matchId ? nextMatch : match))
            : [...prev, nextMatch];

          return sortTournamentMatches(next);
        });
      },
      (err) => {
        console.error("Active tournament match onSnapshot error:", err?.code || err?.message || "unknown");
      }
    );

    return () => unsubscribe();
  }, [selectedTournamentId, myMatch?.id]);

// 5) Tournament usernames: targeted, parallel and shared through the central cache.
useEffect(() => {
  if (!matches || matches.length === 0) return;

  // Alle relevanten UIDs sammeln
  const uids = new Set();
  matches.forEach((m) => {
    if (m.player1Uid) uids.add(m.player1Uid);
    if (m.player2Uid) uids.add(m.player2Uid);
  });

  let active = true;
  prefetchUsernames([...uids]).then((names) => {
    if (!active || Object.keys(names).length === 0) return;
    setUsernamesByUid((previous) => ({ ...previous, ...names }));
  });
  return () => { active = false; };
}, [matches]);

useEffect(() => {
  if (view !== "DETAIL") return;
  if (!selectedTournamentId) return;

  const getFallbackMaxPlayersByTournamentId = (tournamentId) => {
    if (tournamentId === "evening") return 16;
    if (tournamentId === "nightly") return 32;
    if (isSpecialCupTournament({ tournamentId })) return 64;
    return 0;
  };

  const parts = selectedTournamentId.split("_");
  if (parts.length < 2) return;

  const tournamentId = parts[1];
  const legacyMaxPlayers = getFallbackMaxPlayersByTournamentId(tournamentId);
  const openedTournament =
    lobbyTournamentFallbackRef.current?.id === selectedTournamentId
      ? lobbyTournamentFallbackRef.current
      : {};
  setLobbyMaxPlayers(getDashboardLobbyMaxPlayers({}, openedTournament, legacyMaxPlayers));

  const ref = doc(db, "tournaments", selectedTournamentId);

  const unsub = onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        setLobbyPlayers([]);
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      const players = Array.isArray(data.players) ? data.players : [];
      setLobbyPlayers(players);
      setLobbyMaxPlayers(getDashboardLobbyMaxPlayers(data, openedTournament, legacyMaxPlayers));
    },
    (err) => {
      console.error("Lobby onSnapshot error:", err?.code || err?.message || "unknown");
      setLobbyPlayers([]);
    }
  );

  return () => unsub();
}, [view, selectedTournamentId]);

useEffect(() => {
  if (!Array.isArray(lobbyPlayers) || lobbyPlayers.length === 0) return undefined;
  let active = true;
  prefetchUsernames(lobbyPlayers).then((names) => {
    if (!active || Object.keys(names).length === 0) return;
    setUsernamesByUid((previous) => ({ ...previous, ...names }));
  });
  return () => { active = false; };
}, [lobbyPlayers]);

useEffect(() => {
  const participantUids = Array.isArray(selectedTournament?.players)
    ? selectedTournament.players
    : [];
  if (participantUids.length === 0) return undefined;
  let active = true;
  prefetchUsernames(participantUids).then((names) => {
    if (!active || Object.keys(names).length === 0) return;
    setUsernamesByUid((previous) => ({ ...previous, ...names }));
  });
  return () => { active = false; };
}, [selectedTournament?.players]);

useEffect(() => {
  if (!openPlayerMenuUid) return;

  const handlePointerDown = (e) => {
    const menuEl = playerMenuRef.current;
    const btnEl = playerMenuButtonRef.current;

    if (menuEl && menuEl.contains(e.target)) return;
    if (btnEl && btnEl.contains(e.target)) return;

    setOpenPlayerMenuUid(null);
    setPlayerMenuPosition(null);
  };

  const closePlayerMenu = () => {
    setOpenPlayerMenuUid(null);
    setPlayerMenuPosition(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") closePlayerMenu();
  };

  document.addEventListener("pointerdown", handlePointerDown);
  window.addEventListener("resize", closePlayerMenu);
  window.addEventListener("scroll", closePlayerMenu, true);
  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("pointerdown", handlePointerDown);
    window.removeEventListener("resize", closePlayerMenu);
    window.removeEventListener("scroll", closePlayerMenu, true);
    document.removeEventListener("keydown", handleKeyDown);
  };
}, [openPlayerMenuUid]);


useEffect(() => {
  const runDailyBonus = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const res = await claimDailyBonus({ preview: true });
      const result = res?.data || null;

      console.log("claimDailyBonus preview result:", result?.reason || "unknown");

      if (result?.canClaim === true) {
        setDailyBonusData(result);

        // ✅ Modal NUR öffnen wenn wirklich claimbar
        setDailyBonusOpen(true);
      } else {
        setDailyBonusOpen(false);
      }
    } catch (error) {
      console.error("claimDailyBonus preview failed:", error?.code || error?.message || "unknown");
      setDailyBonusOpen(false);
    }
  };

  runDailyBonus();
}, []);






  // ----------------------------
  // Tournament join
  // ----------------------------
  const joinTournament = async (tournamentId) => {
    const user = auth.currentUser;
    if (!user) return;


   const tournament = tournaments.find((t) => t.id === tournamentId);

if (!tournament) {
  setJoinMessage(t("dashboard.joinMessage.notFound"));
  return;
}

const startAt = tournament?.startAt?.toDate?.() || null;

if (startAt) {
  const now = new Date();

  if (now.getTime() >= startAt.getTime()) {
    setJoinMessage(t("dashboard.joinMessage.alreadyStarted"));
    return;
  }
}


    setJoinMessage("");

    try {



      if (tournament.status !== "open") {
        setJoinMessage(t("dashboard.joinMessage.notJoinable"));
        return;
      }

      const ref = doc(db, "tournaments", tournamentId);
      await updateDoc(ref, {
        players: arrayUnion(user.uid),
        [`joinedAt.${user.uid}`]: serverTimestamp(),
      });

      setJoinMessage(t("dashboard.joinMessage.saved"));
    } catch (err) {
      setJoinMessage(t("dashboard.joinMessage.error"));
    }
  };

// ----------------------------
// Match actions (Report System)
// ----------------------------
const submitResult = async (matchId, reportType, payload = {}) => {
  if (!currentUid) {
    alert(t("dashboard.match.notLoggedIn"));
    return;
  }

  if (!matchId || !reportType) {
    alert(t("dashboard.match.missingData"));
    return;
  }

  // ✅ Option A: harte Validierung direkt im Client (damit nie NaN rausgeht)
  if (reportType === "A") {
    const s1 = Number(payload?.scoreP1);
    const s2 = Number(payload?.scoreP2);
    const matchForSubmit =
      (matches || []).find((match) => String(match?.id || "") === String(matchId || "")) ||
      null;
    const resumeOriginal = matchForSubmit?.resumeMatch?.originalReport || {};
    const isResumeMatch = matchForSubmit?.resumeMatch?.active === true;
    const finalScoreP1 = isResumeMatch
      ? Number(resumeOriginal?.scoreP1 || 0) + s1
      : s1;
    const finalScoreP2 = isResumeMatch
      ? Number(resumeOriginal?.scoreP2 || 0) + s2
      : s2;

    if (!Number.isFinite(s1) || !Number.isFinite(s2)) {
      alert(t("dashboard.match.enterValidNumbers"));
      return;
    }

    if (s1 < 0 || s2 < 0) {
      alert(t("dashboard.match.noNegativeScores"));
      return;
    }

    if (finalScoreP1 === finalScoreP2) {
      alert(t("dashboard.match.noDrawAllowed"));
      return;
    }
  }

  try {
    const submitMatchResultSecure = httpsCallable(
      functions,
      "submitMatchResultSecure"
    );

    await submitMatchResultSecure({
      tournamentId: selectedTournamentId,
      matchId,
      ...buildReportV2Request(reportType, payload),
    });

    // optional: UI cleanup (dein bisheriges)
    setReportingMatchId(null);
    setScoreP1("");
    setScoreP2("");
  } catch (e) {
    console.error("submitMatchResultSecure failed:", e?.code || e?.message || "unknown");
    const code = String(e?.code || "");
    const msg = String(e?.message || "");
    const raw = msg.toLowerCase();
    if (raw.includes("reporting not allowed yet")) {
      alert(
        language === "en"
          ? "Results cannot be reported yet. Please try again once the reporting window opens."
          : "Ergebnisse können noch nicht gemeldet werden. Bitte versuche es erneut, sobald das Meldefenster geöffnet ist."
      );
      return;
    }
    if (raw.includes("report already submitted")) {
      alert(
        language === "en"
          ? "You have already reported this match."
          : "Du hast dieses Match bereits gemeldet."
      );
      return;
    }
    if (raw.includes("dual report response deadline expired")) {
      alert(
        language === "en"
          ? "The response window has expired."
          : "Die Antwortfrist ist abgelaufen."
      );
      return;
    }
    if (raw.includes("only reporter")) {
      alert(
        language === "en"
          ? "This action is currently only available to the selected reporter."
          : "Diese Aktion ist aktuell nur für den ausgewählten Reporter verfügbar."
      );
      return;
    }
    alert(t("dashboard.match.saveError", { code, message: "" }).trim());
  }
};

  // Best-effort: wenn Snapshot zeigt "beide confirmed" und status noch reported -> setze confirmed.
  // (Das ist MVP-okay; sauberer wäre Cloud Function/Transaction.)
  const autoFinalizeConfirmed = async (m) => {
    if (!m || m.status !== "reported") return;
    if (!bothConfirmed(m)) return;

    const s1 = m?.report?.scoreP1;
    const s2 = m?.report?.scoreP2;
    if (typeof s1 !== "number" || typeof s2 !== "number") return;




    // Winner (bei Unentschieden: null, MVP)
    let winnerUid = null;
    if (s1 > s2) winnerUid = m.player1Uid || null;
    if (s2 > s1) winnerUid = m.player2Uid || null;

    try {
      const matchRef = doc(
        db,
        "tournaments",
        selectedTournamentId,
        "matches",
        m.id
      );
      await updateDoc(matchRef, {
        status: "confirmed",
        winnerUid: winnerUid,
        confirmedAt: serverTimestamp(),
      });

await placeWinnerIntoNextRound(winnerUid, m);


    } catch (e) {
      // still MVP: ignore
    }
  };

 const placeWinnerIntoNextRound = async (winnerUid, currentMatch) => {
  try {
    if (!winnerUid) return;
    if (!selectedTournamentId) return;
    if (
      String(currentMatch?.phase || "").toLowerCase() === "group" ||
      currentMatch?.groupId
    ) {
      return;
    }

    const currentRound = Number(currentMatch?.roundNumber || 1);

    const isThirdPlaceMatch =
  currentMatch?.isThirdPlaceMatch === true ||
  (
    isThirdPlaceEnabledTournament(selectedTournament, selectedTournamentId) &&
    Number(currentMatch?.matchIndex) === 1
  );

if (isThirdPlaceMatch) {
  return;
}

// ✅ KO-Runden anhand der tatsächlichen KO-Bracket-Größe erkennen.
// Bei Gruppenphase ist players.length die Gesamtteilnehmerzahl, nicht die KO-Größe.
const bracketSize = Number(
  selectedTournament?.bracketSize ||
    selectedTournament?.groupStageQualifierCount ||
    0
) || getBracketSize(
  Array.isArray(selectedTournament?.players)
    ? selectedTournament.players.length
    : 0
);
const totalRounds = getTournamentTotalRounds(bracketSize);

if (currentRound >= totalRounds) return;


    const currentIndex = Number(currentMatch?.matchIndex || 0);

    const nextRound = currentRound + 1;
    const nextIndex = Math.floor(currentIndex / 2);
    const slot = currentIndex % 2 === 0 ? "player1Uid" : "player2Uid";

    const matchesRef = collection(db, "tournaments", selectedTournamentId, "matches");


    // Next-Match suchen (roundNumber + matchIndex)
    const qNext = query(
      matchesRef,
      where("roundNumber", "==", nextRound),
      where("matchIndex", "==", nextIndex),
      limit(1)
    );

    const snap = await getDocs(qNext);

    if (snap.empty) {
      // Noch kein Next-Match existiert -> neu anlegen
      const newDoc = doc(matchesRef); // auto-id ist ok, wir finden es immer wieder per query
      await setDoc(
        newDoc,
        {
          roundNumber: nextRound,
          matchIndex: nextIndex,
          status: "waiting",
          [slot]: winnerUid,
          createdAt: serverTimestamp(),
          confirmedBy: [],
          disputedBy: [],
        },
        { merge: true }
      );
      return;
    }

    // Next-Match existiert -> Slot setzen (nur wenn leer)
    const nextDoc = snap.docs[0];
    const nextData = nextDoc.data();

    // Nicht überschreiben, falls Slot schon gefüllt ist
    if (nextData?.[slot]) return;

    const otherSlot = slot === "player1Uid" ? "player2Uid" : "player1Uid";

    // Slot setzen
    await updateDoc(nextDoc.ref, {
      [slot]: winnerUid,
    });

 // Wenn danach beide Spieler da sind -> status auf pending + reporterUid zufällig setzen
const updatedOther = nextData?.[otherSlot];
if (updatedOther) {
  // nach dem Slot-Update: aktueller Gegner ist entweder updatedOther oder winnerUid
  const p1 = slot === "player1Uid" ? winnerUid : updatedOther;
  const p2 = slot === "player2Uid" ? winnerUid : updatedOther;

  const reporterUid = Math.random() < 0.5 ? p1 : p2;

  await updateDoc(nextDoc.ref, {
    status: "pending",
    reporterUid,
  });
}

  } catch (e) {
    console.error("placeWinnerIntoNextRound failed:", e?.code || e?.message || "unknown");
  }
};

// Trigger auto-finalize when matches change (ohne unstable deps)
useEffect(() => {
  if (!matches || matches.length === 0) return;

  matches.forEach((m) => {
    if (
      m?.status === "reported" &&
      Array.isArray(m.confirmedBy) &&
      m.player1Uid &&
      m.player2Uid &&
      m.confirmedBy.includes(m.player1Uid) &&
      m.confirmedBy.includes(m.player2Uid)
    ) {
      autoFinalizeConfirmed(m);
    }
  });
// `autoFinalizeConfirmed` intentionally belongs to the same render as `matches`.
// Depending on its per-render identity would rerun this write effect after every render.
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [matches]);

    useEffect(() => {
  if (!currentUid) return;
  if (view !== "DETAIL") return;
  if (!selectedTournamentId) return;
  if (!selectedTournament) return;

  const m = latestMyMatchRaw;
  if (!m) return;

  const isGroupResultMatch =
    String(m?.phase || "").toLowerCase() === "group" ||
    !!m?.groupId;
  if (isGroupResultMatch) return;

  // Wir reagieren nur auf CONFIRMED
  if (m.status !== "confirmed") return;

  const seriesState = m?.tieId
    ? getTournamentSeriesState({ matches, tieId: m.tieId })
    : null;
  if (seriesState && seriesState.resolved !== true) return;

  const winnerUid = seriesState?.winnerUid || m.winnerUid || null;
  if (!winnerUid) return;


// Finale/Platz 3 anhand der tatsächlichen KO-Bracket-Größe erkennen.
// Bei Gruppenphase wäre players.length zu groß und verschiebt Halbfinale/Finale.
const bracketSize = Number(
  selectedTournament?.bracketSize ||
    selectedTournament?.groupStageQualifierCount ||
    0
) || getBracketSize(
  Array.isArray(selectedTournament?.players)
    ? selectedTournament.players.length
    : 0
);
const totalRounds = getTournamentTotalRounds(bracketSize);


const thirdPlaceEnabled = isThirdPlaceEnabledTournament(selectedTournament, selectedTournamentId);

const isThirdPlaceMatch =
  thirdPlaceEnabled &&
  Number(m?.roundNumber) === totalRounds &&
  Number(m?.matchIndex) === 1;

const isFinalMatch =
  Number(m?.roundNumber) === totalRounds &&
  Number(m?.matchIndex) === 0;


  // Doppeltrigger verhindern
  const type =
  winnerUid === currentUid
    ? (
        isThirdPlaceMatch
          ? "THIRD_PLACE"
          : "WIN"
      )
    : "LOSE";
  if (
    shownResultForMatchRef.current.matchId === (seriesState?.tieId || m.id) &&
    shownResultForMatchRef.current.type === type
  ) {
    return;
  }

const lostSemifinalButStillHasThirdPlace =
  thirdPlaceEnabled &&
  Number(m?.roundNumber) === totalRounds - 1 &&
  winnerUid !== currentUid;



  // LOSE -> immer Modal
  if (
  winnerUid !== currentUid &&
  !lostSemifinalButStillHasThirdPlace
) {
  shownResultForMatchRef.current = {
    matchId: seriesState?.tieId || m.id,
    type: "LOSE",
  };

  if (isFinalMatch) {
    setResultModalVariant("second");
  } else {
    setResultModalVariant("eliminated");
  }

  resultNextActionRef.current = () => {
    setResultModalOpen(false);
    setTournamentTab("bracket");
  };

  setResultModalOpen(true);
  return;
}




if (
  winnerUid === currentUid &&
  isThirdPlaceMatch
) {
  shownResultForMatchRef.current = {
    matchId: seriesState?.tieId || m.id,
    type: "THIRD_PLACE",
  };

  setResultModalVariant("third");

  resultNextActionRef.current = () => {
    setResultModalOpen(false);
    setTournamentTab("bracket");
  };

  setResultModalOpen(true);
  return;
}



  // WIN -> nur wenn Finale gewonnen
if (
  winnerUid === currentUid &&
  isFinalMatch
) {

    shownResultForMatchRef.current = { matchId: seriesState?.tieId || m.id, type: "WIN" };

    setResultModalVariant("winner");

    resultNextActionRef.current = () => {
      setResultModalOpen(false);
      setSelectedTournamentId(null);
      setView("LIST");
      setHomeSection("TOURNAMENTS");
    };

    setResultModalOpen(true);
  }
}, [latestMyMatchRaw, currentUid, view, selectedTournamentId, selectedTournament, matches]);



const prevTournamentStatusRef = useRef(null);
const loadedTournamentDetailIdRef = useRef(null);




useEffect(() => {
  if (view !== "DETAIL") return;
  if (!selectedTournamentId) {
    prevTournamentStatusRef.current = null;
    loadedTournamentDetailIdRef.current = null;
    return;
  }

  if (!selectedTournament) {
    if (loadedTournamentDetailIdRef.current === selectedTournamentId) {
      setSelectedTournamentId(null);
      setLobbyPlayers([]);
      setView("LIST");
      setHomeSection("TOURNAMENTS");
    }
    return;
  }

  loadedTournamentDetailIdRef.current = selectedTournamentId;

  const prevStatus = prevTournamentStatusRef.current;
  const currentStatus = selectedTournament.status;

  // ✅ Nur beim echten Statuswechsel rauswerfen
  if (
    prevStatus !== currentStatus &&
    (currentStatus === "cancelled" ||
      currentStatus === "void" ||
      currentStatus === "did_not_start")
  ) {
    setSelectedTournamentId(null);
    setLobbyPlayers([]);
    setView("LIST");
    setHomeSection("TOURNAMENTS");
  }

  prevTournamentStatusRef.current = currentStatus;
}, [view, selectedTournamentId, selectedTournament]);



  // ----------------------------
  // Derived lists (for UI)
  // ----------------------------




// ----------------------------
// UI
// ----------------------------


const headerCfg =
  homeSection === null
    ? { title: t("header.home"), size: 40 }
    : homeSection === "GAME"
    ? { title: t("header.game"), size: 32 }
    : homeSection === "TOURNAMENTS"
    ? { title: t("header.tournaments"), size: 40 }
    : homeSection === "SINGLES"
    ? { title: t("header.singles"), size: 34 }
    : homeSection === "PROFILE"
    ? { title: t("header.profile"), size: 40 }
    : homeSection === "ADMIN_PANEL"
    ? { title: "ADMIN PANEL", size: 32 }
    : homeSection === "CREATOR_TOURNAMENTS"
    ? { title: "MEINE TURNIERE", size: 30 }
    : homeSection === "COMMUNITY"
    ? { title: t("header.community"), size: 34 }
    : homeSection === "FAIRPLAY"
    ? { title: t("header.fairplay"), size: 28 }
    : homeSection === "COMMUNITY_FRIENDS" ||
  homeSection === "COMMUNITY_LEADERBOARDS" ||
  homeSection === "COMMUNITY_REWARDS" ||
  homeSection === "COMMUNITY_REPORT_BLOCK" ||
  homeSection === "TUTORIAL"
? { title: "", size: 0 }
    : homeSection === "VOUCHERS"
    ? { title: t("header.vouchers"), size: 34 }
    : homeSection === "SHOP"
    ? { title: t("header.shop"), size: 40 }
    : homeSection === "ABOUT"
    ? { title: t("common.about"), size: 28 }
    : homeSection === "AGB"
    ? { title: t("header.agb"), size: 34 }
    : homeSection === "DATENSCHUTZ"
    ? { title: t("header.privacy"), size: 20 }
    : homeSection === "IMPRESSUM"
    ? { title: t("header.impressum"), size: 28 }
    : homeSection === "WETTBEWERBSBEDINGUNGEN"
    ? { title: t("header.competitionTerms"), size: 20 }
    : homeSection === "FAQ"
    ? { title: t("header.faqs"), size: 34 }
    : { title: t("header.home"), size: 40 };



const notificationChallengeStatus =
  selectedNotification?.type === "single_challenge_invite"
    ? String(selectedNotification?.challengeStatus || "pending")
    : null;

const formatNotificationDate = (notificationItem) =>
  notificationItem?.createdAt?.toDate
    ? notificationItem.createdAt.toDate().toLocaleString("de-DE")
    : t("dashboard.notifications.dateUnavailable");

const openNotificationItem = (item) => {
  setChallengeResponseDone(false);
  setFriendRequestButtonPressed(null);
  setSelectedNotification(item);
  setSurveyNotificationUnavailableId(null);
  setSurveyNotificationBlockedId(null);
  setNotificationDetailOpen(true);
  markNotificationAsRead(item);
};

const handleOpenSurveyNotification = async () => {
  const surveyId = String(selectedNotification?.relatedId || "").trim();
  if (!surveyId || surveyBusy) return;
  setSurveyBusy(true);
  const opened = await openDueSurvey(surveyId);
  if (opened === "blocked") {
    setSurveyNotificationBlockedId(selectedNotification?.id || surveyId);
  } else if (!opened) {
    setSurveyNotificationUnavailableId(selectedNotification?.id || surveyId);
  }
  setSurveyBusy(false);
};

const inviteStillPending =
  selectedNotification?.type === "single_challenge_invite" &&
  notificationChallengeStatus === "pending" &&
  challengeCountdownText !== "00:00";

  const friendRequestPending =
  selectedNotification?.type === "friend_request_received" &&
  selectedNotification?.status !== "handled";

  const friendRequestAction =
  selectedNotification?.type === "friend_request_received"
    ? selectedNotification?.action || null
    : null;

const tournamentRulesForLobbyView = buildTournamentRulesViewModel(selectedTournament || {}, language, t);

const renderMenuCard = ({
  icon,
  label,
  onClick,
  active = false,
  danger = false,
  index = 0,
  chevron = "›",
}) => (
  <button
    type="button"
    className={`vq-side-menu-card${danger ? " vq-side-menu-card-danger" : ""}`}
    style={getMenuCardStyle({ active, danger, index })}
    onClick={onClick}
  >
    <span style={menuLabelWrapStyle}>
      <span style={menuIconStyle}>{icon}</span>
      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{label}</span>
    </span>
    <span style={menuChevronStyle}>{chevron}</span>
  </button>
);

const renderBackCard = (onClick, index = 0) =>
  renderMenuCard({
    icon: "←",
    label: t("common.back"),
    onClick,
    index,
    chevron: "",
  });

const dashboardUserName =
  String(profile?.username || profile?.displayName || auth.currentUser?.displayName || "").trim() ||
  (language === "en" ? "player" : "Spieler");

useEffect(() => {
  const knownOwnUsername = String(
    profile?.username || profile?.displayName || auth.currentUser?.displayName || ""
  ).trim();
  if (!currentUid || !knownOwnUsername) return;
  seedUsername(currentUid, knownOwnUsername);
  setUsernamesByUid((previous) => previous?.[currentUid] === knownOwnUsername
    ? previous
    : { ...previous, [currentUid]: knownOwnUsername });
}, [currentUid, profile?.username, profile?.displayName]);

const formatDashboardDateTime = (millis) => {
  if (!millis) return "";
  const date = new Date(millis);
  const today = new Date(dashboardNow);
  const tomorrow = new Date(dashboardNow);
  tomorrow.setDate(today.getDate() + 1);

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const dayLabel = sameDay(date, today)
    ? t("home.today")
    : sameDay(date, tomorrow)
      ? t("home.tomorrow")
      : date.toLocaleDateString(language === "en" ? "en-US" : "de-DE", {
          day: "2-digit",
          month: "2-digit",
        });

  const timeLabel = date.toLocaleTimeString(language === "en" ? "en-US" : "de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${dayLabel} · ${timeLabel}`;
};

const getDashboardTournamentName = (tournament = {}) => {
  const id = getDashboardTournamentId(tournament);
  const fallback = id.includes("_evening")
    ? "Evening Cup"
    : id.includes("_nightly")
      ? "Nightly Cup"
      : t("home.tournamentFallback");

  return String(tournament.name || tournament.title || tournament.tournamentName || fallback);
};

const getDashboardParticipantsLabel = (tournament = {}) => {
  const current = getDashboardParticipantCount(tournament);
  const max = getDashboardMaxPlayers(tournament);
  return max > 0
    ? `${current}/${max} ${t("home.players")}`
    : `${current} ${t("home.players")}`;
};

const upcomingDashboardCups = useMemo(() => {
  const latestAllowedStart = dashboardNow + HOME_UPCOMING_CUP_WINDOW_MS;
  const candidatesById = new Map();

  const addCandidate = (tournament, priority) => {
    const id = getDashboardTournamentId(tournament);
    if (!id || !isDashboardSpecialOrCreatorTournament(tournament)) return;
    if (!isDashboardUpcomingTournament(tournament, dashboardNow)) return;

    const startMillis = getDashboardTournamentStartMillis(tournament);
    if (!startMillis || startMillis > latestAllowedStart) return;

    const existing = candidatesById.get(id);
    if (existing && existing.priority >= priority) return;

    candidatesById.set(id, {
      tournament,
      id,
      startMillis,
      priority,
    });
  };

  (Array.isArray(dashboardDailySpecialCups) ? dashboardDailySpecialCups : [])
    .forEach((tournament) => addCandidate(tournament, 1));
  (Array.isArray(tournaments) ? tournaments : [])
    .forEach((tournament) => addCandidate(tournament, 2));

  return Array.from(candidatesById.values())
    .sort((a, b) => a.startMillis - b.startMillis)
    .slice(0, HOME_UPCOMING_CUP_LIMIT);
}, [dashboardDailySpecialCups, dashboardNow, tournaments]);

const getDashboardMatchPlayerName = (match, slot) => {
  const uid = String(match?.[`${slot}Uid`] || "").trim();
  return String(
    (uid && usernamesByUid?.[uid]) ||
      match?.[`${slot}Username`] ||
      match?.[`${slot}Name`] ||
      match?.[slot]?.username ||
      ""
  ).trim();
};

const dashboardActiveItems = (() => {
  const tournamentItems = (dashboardTournamentMatches || []).map((match) => {
    const player1Name = getDashboardMatchPlayerName(match, "player1");
    const player2Name = getDashboardMatchPlayerName(match, "player2");
    return {
      key: `tournament-${match.tournamentId}-${match.id}`,
      type: "tournament",
      title:
        player1Name && player2Name
          ? `${player1Name} ${t("home.vs")} ${player2Name}`
          : t("home.loadingPlayers"),
      meta: getDashboardTournamentName(match.tournament),
      status: t("home.status.active"),
      onClick: () => handleOpenTournamentDetail(match.tournamentId, match.tournament, { includeCurrentUser: true }),
    };
  });

  const singleItems =
    dashboardActiveSingleMatch && currentUid
      ? [
          {
            key: `single-${dashboardActiveSingleMatch.id}`,
            type: "single",
            title: (() => {
              const opponentUid =
                dashboardActiveSingleMatch.player1Uid === currentUid
                  ? dashboardActiveSingleMatch.player2Uid
                  : dashboardActiveSingleMatch.player1Uid;
              const currentName = usernamesByUid?.[currentUid] || dashboardUserName;
              const opponentSlot =
                dashboardActiveSingleMatch.player1Uid === opponentUid ? "player1" : "player2";
              const opponentName =
                getDashboardMatchPlayerName(dashboardActiveSingleMatch, opponentSlot) ||
                String(dashboardActiveSingleMatch.opponentName || "").trim();
              return currentName && opponentName
                ? `${currentName} ${t("home.vs")} ${opponentName}`
                : t("home.loadingPlayers");
            })(),
            meta: t("home.singleMatchMeta"),
            status: t("home.status.active"),
            onClick: () => {
              setSingleStep("matchFound");
              setHomeSection("SINGLES");
            },
          },
        ]
      : [];

  return [...tournamentItems, ...singleItems];
})();

const renderHomeActionCard = ({ icon, title, text, onClick, trailing = null }) => (
  <button
    type="button"
    className="vq-home-row-card"
    onClick={onClick}
  >
    <span className="vq-home-row-icon">{icon}</span>
    <span className="vq-home-row-copy" style={{ minWidth: 0 }}>
      <span className="vq-home-row-title">{title}</span>
      <span className="vq-home-row-text" style={{ display: "block" }}>
        {text}
      </span>
    </span>
    {trailing || <span className="vq-home-chevron" aria-hidden="true">›</span>}
  </button>
);

const resetDeleteAccountModal = () => {
  setDeleteAccountPassword("");
  setDeleteAccountMessage("");
  setDeleteAccountPasswordVisible(false);
  setDeleteAccountConfirmed(false);
};

const closeDeleteAccountModal = () => {
  resetDeleteAccountModal();
  setDeleteAccountModalOpen(false);
};

const passwordDeleteButtonDisabled =
  deletingAccount || !deleteAccountPassword.trim() || !deleteAccountConfirmed;






// Richtiger Return
return (
  <>
  <React.Suspense
    fallback={(
      <div
        role="status"
        style={{
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          color: "white",
          background: "linear-gradient(90deg, #080427 0%, #020047 100%)",
          fontWeight: 900,
          letterSpacing: "0.8px",
          textTransform: "uppercase",
        }}
      >
        {t("common.loading")}
      </div>
    )}
  >
  <div
    ref={dashboardScrollRef}
    style={{
      minHeight: notificationsMobileLayout ? "100dvh" : "100vh",
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      display: "flex",
      justifyContent: "center",
      background: notificationsMobileLayout
        ? "linear-gradient(90deg, #080427 0%, #020047 100%)"
        : "linear-gradient(135deg, #03021a 0%, #07042a 48%, #02001b 100%)",
      padding: notificationsMobileLayout
        ? 0
        : dashboardShellTabletLayout
          ? "calc(16px + var(--vq-safe-area-top)) calc(16px + var(--vq-safe-area-right)) 16px calc(16px + var(--vq-safe-area-left))"
          : "calc(24px + var(--vq-safe-area-top)) calc(24px + var(--vq-safe-area-right)) 24px calc(24px + var(--vq-safe-area-left))",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: notificationsMobileLayout
          ? "none"
          : dashboardShellTabletLayout
            ? "900px"
            : "1440px",
        minWidth: 0,
        minHeight: notificationsMobileLayout ? "100dvh" : "100vh",
        borderRadius: notificationsMobileLayout ? 0 : "28px",
        overflow: "hidden",
        background: "linear-gradient(90deg, #080427 0%, #020047 100%)",
        position: "relative",
        boxShadow: notificationsMobileLayout
          ? "none"
          : "0 20px 60px rgba(0,0,0,0.35)",
        boxSizing: "border-box",
        padding: notificationsMobileLayout
          ? "calc(16px + var(--vq-safe-area-top)) calc(16px + var(--vq-safe-area-right)) calc(180px + env(safe-area-inset-bottom)) calc(16px + var(--vq-safe-area-left))"
          : "16px 16px calc(180px + env(safe-area-inset-bottom)) 16px",
      }}
    >

{showWatermark && (
  <img
    src="/vaultquest-logo.png"
    alt=""
    style={{
      position: "absolute",
      top: homeSection === null ? "54%" : "45%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: homeSection === null
        ? "clamp(360px, 118vw, 620px)"
        : "clamp(300px, 82vw, 520px)",
      opacity: homeSection === null ? 0.28 : 0.2,
      filter: "drop-shadow(0 0 34px rgba(255,0,127,0.18)) drop-shadow(0 0 54px rgba(122,43,255,0.16))",
      pointerEvents: "none",
      zIndex: 0,
      userSelect: "none",
    }}
  />
)}

<div style={{ position: "relative", zIndex: 1 }}>














      {/* Overlay */}
      {menuOpen && (
        <div
          onClick={closeMenu}
	          style={{
	  position: notificationsMobileLayout ? "fixed" : "absolute",
	  inset: 0,
	  background: "rgba(2,0,18,0.64)",
	  backdropFilter: "blur(3px)",
  zIndex: 29999,
}}
        />
      )}

      {/* Slide-In Menü */}
      <div
	  onClick={(e) => e.stopPropagation()}
	  style={{
	  position: notificationsMobileLayout ? "fixed" : "absolute",
	  top: "max(76px, calc(64px + env(safe-area-inset-top)))",
	  left: "max(12px, env(safe-area-inset-left))",
	  width: "min(86vw, 336px)",
  maxWidth: "336px",
  height: "calc(100dvh - 96px - env(safe-area-inset-top))",
	  borderRadius: "24px",
	  overflowY: "auto",
	  overflowX: "hidden",
	  WebkitOverflowScrolling: "touch",
	  overscrollBehavior: "contain",
	  touchAction: "pan-y",
	  backgroundColor: "#020047",
	  background:
	    "radial-gradient(circle at 18% 4%, rgba(255,0,127,0.20), transparent 34%), radial-gradient(circle at 88% 0%, rgba(122,43,255,0.18), transparent 32%), linear-gradient(145deg, rgba(8,4,39,0.98) 0%, rgba(2,0,71,0.98) 100%)",
  border: "1px solid rgba(255,255,255,0.16)",
  transform: menuOpen ? "translate3d(0,0,0)" : "translate3d(-112%,0,0)",
  transition: "transform 320ms cubic-bezier(.2,.86,.2,1), opacity 240ms ease",
	  opacity: menuOpen ? 1 : 0,
	  pointerEvents: menuOpen ? "auto" : "none",
	  zIndex: 30000,
	  padding: "14px 14px calc(56px + env(safe-area-inset-bottom)) 14px",
	  boxSizing: "border-box",
  boxShadow: "0 28px 70px rgba(0,0,0,0.52), 0 0 44px rgba(122,43,255,0.18)",
}}
      >
        <style>
          {`
            @keyframes vqMenuItemIn {
              from { opacity: 0; transform: translateX(-14px) scale(0.985); }
              to { opacity: 1; transform: translateX(0) scale(1); }
            }
            .vq-side-menu-card {
              transition: transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease, background 150ms ease;
              -webkit-tap-highlight-color: transparent;
            }
            .vq-side-menu-card:hover {
              transform: translateX(3px) scale(1.012);
              box-shadow: 0 0 28px rgba(255,0,127,0.20), 0 14px 28px rgba(0,0,0,0.26) !important;
              border-color: rgba(255,255,255,0.30) !important;
            }
            .vq-side-menu-card:active {
              transform: scale(0.975);
            }
            .vq-side-menu-card-danger:hover {
              box-shadow: 0 0 28px rgba(255,48,79,0.24), 0 14px 28px rgba(0,0,0,0.26) !important;
            }
          `}
        </style>
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.12,
            pointerEvents: "none",
            backgroundImage:
              "linear-gradient(30deg, rgba(255,255,255,0.20) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.20) 87.5%, rgba(255,255,255,0.20)), linear-gradient(150deg, rgba(255,255,255,0.20) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.20) 87.5%, rgba(255,255,255,0.20)), linear-gradient(30deg, rgba(255,255,255,0.20) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.20) 87.5%, rgba(255,255,255,0.20)), linear-gradient(150deg, rgba(255,255,255,0.20) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.20) 87.5%, rgba(255,255,255,0.20))",
            backgroundSize: "34px 58px",
            backgroundPosition: "0 0, 0 0, 17px 29px, 17px 29px",
          }}
        />
        <div
          style={{
            position: "relative",
            isolation: "isolate",
            minHeight: 122,
            borderRadius: 20,
            padding: "16px 48px 15px 14px",
            marginBottom: 14,
            overflow: "hidden",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 100%)",
            border: "1px solid rgba(255,255,255,0.16)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12), 0 0 30px rgba(255,0,127,0.12)",
          }}
        >
          <div
            style={{
              position: "absolute",
              width: 110,
              height: 110,
              right: -34,
              top: -28,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,0,127,0.28), transparent 64%)",
              filter: "blur(2px)",
              zIndex: -1,
            }}
          />
          <button
            type="button"
            onClick={closeMenu}
            aria-label="Close menu"
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 32,
              height: 32,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(255,255,255,0.10)",
              color: "white",
              fontSize: 18,
              fontWeight: 1000,
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              lineHeight: 1,
            }}
          >
            ×
          </button>
          <img
            src="/vaultquest-logo.png"
            alt="VaultQuest Logo"
            style={{
              width: 70,
              height: 70,
              objectFit: "contain",
              display: "block",
              marginBottom: 7,
              filter: "drop-shadow(0 0 16px rgba(255,0,127,0.24))",
            }}
          />
          <div
            style={{
            color: "#fff",
              fontWeight: 1000,
              fontSize: "18px",
              lineHeight: 1,
              letterSpacing: "0.8px",
              textTransform: "uppercase",
            }}
          >
            VaultQuest
          </div>
          <div
            style={{
              marginTop: 8,
              color: "rgba(255,255,255,0.48)",
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
          }}
        >
         {t("menu.title")}
          </div>
        </div>



{menuPage === "legal" ? (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      marginTop: "16px",
      position: "relative",
      zIndex: 1,
    }}
  >
    {renderBackCard(() => setMenuPage("root"), 0)}

    <div style={{ height: "8px" }} />

    {renderMenuCard({
      icon: "📄",
      label: t("menu.terms"),
      onClick: () => {
        openMenuHomeSection("AGB");
      },
      active: homeSection === "AGB",
      index: 1,
    })}

    {renderMenuCard({
      icon: "🔒",
      label: t("menu.privacy"),
      onClick: () => {
        openMenuHomeSection("DATENSCHUTZ");
      },
      active: homeSection === "DATENSCHUTZ",
      index: 2,
    })}

    {renderMenuCard({
      icon: "🏛",
      label: t("menu.imprint"),
      onClick: () => {
        openMenuHomeSection("IMPRESSUM");
      },
      active: homeSection === "IMPRESSUM",
      index: 3,
    })}

  </div>




) : menuPage === "account" ? (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      marginTop: "16px",
      position: "relative",
      zIndex: 1,
    }}
  >
    {renderBackCard(() => {
      releaseFocusedFieldAndResetHorizontalOffset();
      setMenuPage("settings");
    }, 0)}

<div style={{ height: "8px" }} />

<div style={{ borderBottom: "1px solid rgba(255,255,255,0.18)", paddingBottom: "12px", marginBottom: "12px" }}>
  <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
    {t("menu.account.username")}
  </div>
  <div style={{ color: "white", fontWeight: 700, fontSize: "22px", lineHeight: 1.2 }}>
    {profile?.username || "-"}
  </div>
</div>

<div style={{ borderBottom: "1px solid rgba(255,255,255,0.18)", paddingBottom: "12px", marginBottom: "12px" }}>
  <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
    {t("menu.account.email")}
  </div>
  <div style={{ color: "white", fontWeight: 700, fontSize: "18px", lineHeight: 1.3, wordBreak: "break-word" }}>
    {profile?.email || auth.currentUser?.email || "-"}
  </div>
</div>

<div style={{ borderBottom: "1px solid rgba(255,255,255,0.18)", paddingBottom: "12px", marginBottom: "12px" }}>
  <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
    {t("menu.account.ageGroup.title")}
  </div>
  <select
    value={accountAgeGroup}
    onChange={(e) => {
      setAccountAgeGroup(e.target.value);
      setAccountAgeMessage("");
    }}
    style={{
      width: "100%",
      border: "2px solid rgba(255,255,255,0.16)",
      borderRadius: 14,
      background: "rgba(255,255,255,0.08)",
      color: "white",
      padding: "13px 12px",
      fontWeight: 900,
      fontSize: 16,
      outline: "none",
    }}
  >
    <option value="" style={{ color: "#111" }}>{t("menu.account.ageGroup.placeholder")}</option>
    {VAULTQUEST_AGE_GROUPS.map((group) => (
      <option key={group} value={group} style={{ color: "#111" }}>
        {group}
      </option>
    ))}
  </select>
  <button
    type="button"
    onClick={saveAccountAgeGroup}
    disabled={savingAccountAgeGroup || !accountAgeGroup || accountAgeGroup === String(profile?.age || "")}
    style={{
      width: "100%",
      border: "none",
      borderRadius: "14px",
      padding: "12px",
      marginTop: 10,
      fontWeight: 1000,
      fontSize: "14px",
      textAlign: "center",
      background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
      color: "white",
      cursor:
        savingAccountAgeGroup || !accountAgeGroup || accountAgeGroup === String(profile?.age || "")
          ? "default"
          : "pointer",
      opacity:
        savingAccountAgeGroup || !accountAgeGroup || accountAgeGroup === String(profile?.age || "")
          ? 0.58
          : 1,
      textTransform: "uppercase",
    }}
  >
    {savingAccountAgeGroup ? "..." : t("menu.account.ageGroup.save")}
  </button>
  {accountAgeMessage && (
    <div style={{ marginTop: 10, color: accountAgeMessage === t("menu.account.ageGroup.saveError") || accountAgeMessage === t("menu.account.ageGroup.invalid") ? "#ff6b7d" : "#2ed573", fontSize: 12, fontWeight: 900, lineHeight: 1.35, textAlign: "center" }}>
      {accountAgeMessage}
    </div>
  )}
</div>

<div style={{ borderBottom: "1px solid rgba(255,255,255,0.18)", paddingBottom: "12px", marginBottom: "12px" }}>
  <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
    {t("menu.account.consoleType")}
  </div>
  <select
    value={accountConsoleType}
    onChange={(e) => setAccountConsoleType(e.target.value)}
    style={{
      width: "100%",
      border: "2px solid rgba(255,255,255,0.16)",
      borderRadius: 14,
      background: "rgba(255,255,255,0.08)",
      color: "white",
      padding: "13px 12px",
      fontWeight: 900,
      fontSize: 15,
      outline: "none",
    }}
  >
    <option value="" style={{ color: "#111" }}>{t("register.consoleTypePlaceholder")}</option>
    <option value="PLAYSTATION" style={{ color: "#111" }}>{t("register.consoleOptions.playstation")}</option>
    <option value="XBOX" style={{ color: "#111" }}>{t("register.consoleOptions.xbox")}</option>
    <option value="PC" style={{ color: "#111" }}>{t("register.consoleOptions.pc")}</option>
  </select>
</div>

<div style={{ borderBottom: "1px solid rgba(255,255,255,0.18)", paddingBottom: "12px", marginBottom: "12px" }}>
  <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
    {t("menu.account.consoleId")}
  </div>
  <input
    value={accountConsoleId}
    onFocus={handleAccountKeyboardAwareFocus}
    onChange={(e) => setAccountConsoleId(e.target.value)}
    placeholder={t("register.consoleIdPlaceholder")}
    style={{
      width: "100%",
      border: "2px solid rgba(255,255,255,0.16)",
      borderRadius: 14,
      background: "rgba(255,255,255,0.08)",
      color: "white",
      padding: "13px 12px",
      fontWeight: 900,
      fontSize: 15,
      outline: "none",
      boxSizing: "border-box",
    }}
  />
</div>

<div style={{ borderBottom: "1px solid rgba(255,255,255,0.18)", paddingBottom: "12px", marginBottom: "18px" }}>
  <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "4px" }}>
    {t("menu.account.eaId")}
  </div>
  <input
    value={accountEaId}
    onFocus={handleAccountKeyboardAwareFocus}
    onChange={(e) => setAccountEaId(e.target.value)}
    placeholder={t("register.eaIdPlaceholder")}
    style={{
      width: "100%",
      border: "2px solid rgba(255,255,255,0.16)",
      borderRadius: 14,
      background: "rgba(255,255,255,0.08)",
      color: "white",
      padding: "13px 12px",
      fontWeight: 900,
      fontSize: 15,
      outline: "none",
      boxSizing: "border-box",
    }}
  />
</div>

<div style={{ marginTop: "-4px", marginBottom: "12px", color: "rgba(255,255,255,0.7)", fontSize: 12, lineHeight: 1.45, fontWeight: 700 }}>
  {t("menu.account.gamingIdsHint")}
</div>

<button
  type="button"
  onClick={saveAccountGamingProfile}
  disabled={savingAccountProfile}
  style={{
    width: "100%",
    border: "none",
    borderRadius: "14px",
    padding: "14px 12px",
    fontWeight: 1000,
    fontSize: "15px",
    textAlign: "center",
    background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
    color: "white",
    cursor: savingAccountProfile ? "default" : "pointer",
    opacity: savingAccountProfile ? 0.7 : 1,
    textTransform: "uppercase",
  }}
>
  {savingAccountProfile ? "..." : t("menu.account.saveGamingIds")}
</button>

{accountSaveMessage && (
  <div style={{ marginTop: 10, color: accountSaveMessage === t("menu.account.saved") ? "#2ed573" : "#ff5a5a", fontSize: 13, fontWeight: 900, textAlign: "center" }}>
    {accountSaveMessage}
  </div>
)}

<div
  style={{
    marginTop: 14,
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.055)",
    color: "white",
  }}
>
  <div style={{ fontSize: 12, fontWeight: 1000, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>
    {accountProviderCapabilities.hasPassword && accountProviderCapabilities.hasApple
      ? t("menu.account.signInMethods")
      : t("menu.account.signInMethod")}
  </div>
  {accountProviderCapabilities.hasApple && (
    <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}>✓ {t("menu.account.signInWithApple")}</div>
  )}
  {accountProviderCapabilities.hasPassword && (
    <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}>✓ {t("menu.account.signInWithPassword")}</div>
  )}
</div>

<div
  style={{
    marginTop: 14,
    padding: "14px",
    borderRadius: 14,
    border: "1px solid rgba(199,103,255,0.28)",
    background:
      "linear-gradient(135deg, rgba(255,0,127,0.12) 0%, rgba(122,43,255,0.10) 100%)",
    color: "white",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 8,
    }}
  >
    <div
      style={{
        minWidth: 0,
        fontSize: 12,
        fontWeight: 1000,
        letterSpacing: 0.8,
        textTransform: "uppercase",
      }}
    >
      {t("menu.account.aiConflictConsent.title")}
    </div>
    <button
      type="button"
      onClick={() =>
        saveAiConflictAnalysisConsent(!aiConflictAnalysisConsentValid)
      }
      disabled={savingAiConsent || !aiConflictAnalysisConsentAvailable}
      aria-pressed={aiConflictAnalysisConsentValid}
      style={{
        flex: "0 0 auto",
        width: 62,
        height: 34,
        borderRadius: 999,
        border: aiConflictAnalysisConsentValid
          ? "2px solid rgba(46,213,115,0.72)"
          : "2px solid rgba(255,255,255,0.22)",
        background: aiConflictAnalysisConsentValid
          ? "linear-gradient(135deg, rgba(46,213,115,0.90), rgba(18,190,95,0.72))"
          : "rgba(255,255,255,0.08)",
        padding: 3,
        cursor: savingAiConsent || !aiConflictAnalysisConsentAvailable ? "not-allowed" : "pointer",
        opacity: savingAiConsent || !aiConflictAnalysisConsentAvailable ? 0.58 : 1,
        boxSizing: "border-box",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: "block",
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "#fff",
          transform: aiConflictAnalysisConsentValid
            ? "translateX(28px)"
            : "translateX(0)",
          transition: "transform 160ms ease",
          boxShadow: "0 5px 14px rgba(0,0,0,0.32)",
        }}
      />
    </button>
  </div>
  <div style={{ color: "rgba(255,255,255,0.72)", fontSize: 13, lineHeight: 1.45, fontWeight: 750 }}>
    {t("menu.account.aiConflictConsent.description")}
  </div>
  <div
    style={{
      marginTop: 10,
      color: aiConflictAnalysisConsentValid ? "#2ed573" : "rgba(255,255,255,0.62)",
      fontSize: 12,
      fontWeight: 1000,
      textTransform: "uppercase",
      letterSpacing: 0.7,
    }}
  >
    {aiConflictAnalysisConsentValid
      ? t("menu.account.aiConflictConsent.statusOn")
      : t("menu.account.aiConflictConsent.statusOff")}
  </div>
  {!aiConflictAnalysisConsentAvailable && (
    <div style={{ marginTop: 8, color: "#ffcf5a", fontSize: 12, fontWeight: 900, lineHeight: 1.35 }}>
      {t("menu.account.aiConflictConsent.adultOnly")}
    </div>
  )}
  {aiConsentMessage && (
    <div
      style={{
        marginTop: 10,
        color: aiConsentMessage === t("menu.account.aiConflictConsent.saveError")
          ? "#ff6b7d"
          : "#2ed573",
        fontSize: 12,
        fontWeight: 900,
        lineHeight: 1.35,
      }}
    >
      {aiConsentMessage}
    </div>
  )}
</div>

<div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
  {accountProviderCapabilities.canChangeEmail && <button
    type="button"
    onClick={() => {
      resetEmailChangeForm();
      setEmailChangeModalOpen(true);
    }}
    style={{
      width: "100%",
      border: "none",
      borderRadius: "12px",
      padding: "14px 12px",
      fontWeight: 800,
      fontSize: "15px",
      textAlign: "center",
      background: "rgba(255,255,255,0.08)",
      color: "white",
      cursor: "pointer",
    }}
  >
    {t("menu.account.emailChange.title")}
  </button>}

  <button
    type="button"
    onClick={() => {
      setDataExportPassword("");
      setDataExportMessage("");
      setDataExportModalOpen(true);
    }}
    style={{
      width: "100%",
      border: "none",
      borderRadius: "12px",
      padding: "14px 12px",
      fontWeight: 800,
      fontSize: "15px",
      textAlign: "center",
      background: "rgba(255,255,255,0.08)",
      color: "white",
      cursor: "pointer",
    }}
  >
    {t("menu.account.dataExport.title")}
  </button>

  {accountProviderCapabilities.canChangePassword && <button
    type="button"
    onClick={() => setPasswordResetModalOpen(true)}
    style={{
      width: "100%",
      border: "none",
      borderRadius: "12px",
      padding: "14px 12px",
      fontWeight: 800,
      fontSize: "15px",
      textAlign: "center",
      background: "rgba(255,255,255,0.08)",
      color: "white",
      cursor: "pointer",
    }}
  >
    {t("menu.account.changePassword")}
  </button>}

  {isIosBiometricAuthSupportedPlatform() && biometricLoginEnabled && (
    <button
      type="button"
      onClick={() => {
        const uid = auth.currentUser?.uid;
        setBiometricLoginEnabledForUser(uid, false);
        setBiometricLoginEnabled(false);
      }}
      style={{
        width: "100%",
        border: "none",
        borderRadius: "12px",
        padding: "14px 12px",
        fontWeight: 800,
        fontSize: "15px",
        textAlign: "center",
        background: "rgba(255,255,255,0.08)",
        color: "white",
        cursor: "pointer",
      }}
    >
      {t("menu.account.disableBiometricLogin")}
    </button>
  )}

  {accountProviderCapabilities.canDeleteAccountInCurrentFlow && <button
    type="button"
    onClick={() => {
      setDeleteAccountPassword("");
      setDeleteAccountMessage("");
      setDeleteAccountModalOpen(true);
    }}
    style={{
      width: "100%",
      border: "none",
      borderRadius: "12px",
      padding: "14px 12px",
      fontWeight: 800,
      fontSize: "15px",
      textAlign: "center",
      background: "rgba(255,0,0,0.12)",
      color: "#ff5a5a",
      cursor: "pointer",
    }}
  >
    {t("menu.account.deleteAccount")}
  </button>}
</div>


  </div>
) : menuPage === "settings" ? (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      marginTop: "16px",
      position: "relative",
      zIndex: 1,
    }}
  >
    {renderBackCard(() => setMenuPage("root"), 0)}

    <div style={{ height: "8px" }} />

    {renderMenuCard({
      icon: <ProfileIcon size={34} />,
      label: t("menu.accountSettings"),
      onClick: () => setMenuPage("account"),
      active: menuPage === "account",
      index: 1,
    })}

    {renderMenuCard({
      icon: "🌐",
      label: t("common.languageSettings"),
      onClick: () => setMenuPage("language"),
      active: menuPage === "language",
      index: 2,
    })}
  </div>


) : menuPage === "language" ? (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      marginTop: "16px",
      position: "relative",
      zIndex: 1,
    }}
  >
    {renderBackCard(() => setMenuPage("settings"), 0)}

    <div style={{ height: "8px" }} />

    {renderMenuCard({
      icon: "DE",
      label: `${t("menu.language.german")} ${language === "de" ? "✓" : ""}`,
      onClick: () => setLanguage("de"),
      active: language === "de",
      index: 1,
    })}

    {renderMenuCard({
      icon: "EN",
      label: `${t("menu.language.english")} ${language === "en" ? "✓" : ""}`,
      onClick: () => setLanguage("en"),
      active: language === "en",
      index: 2,
    })}
  </div>


) : (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      marginTop: "16px",
      position: "relative",
      zIndex: 1,
    }}
  >
    {renderMenuCard({
      icon: "⚙",
      label: t("menu.settings"),
      onClick: () => setMenuPage("settings"),
      active: ["settings", "account", "language"].includes(menuPage),
      index: 0,
    })}

    {renderMenuCard({
      icon: "🎧",
      label: t("menu.support"),
      onClick: () => {
        setMenuNotice("");
        setSupportModalOpen(true);
      },
      index: 1,
    })}

    {nativeIosAppReviewAvailable && renderMenuCard({
      icon: "★",
      label: t("menu.rateVaultQuest"),
      onClick: handleRateVaultQuest,
      index: 2,
    })}

    {renderMenuCard({
      icon: "§",
      label: t("menu.legal"),
      onClick: () => setMenuPage("legal"),
      active:
        menuPage === "legal" ||
        ["AGB", "DATENSCHUTZ", "IMPRESSUM", "FAQ"].includes(homeSection),
      index: nativeIosAppReviewAvailable ? 3 : 2,
    })}

    {renderMenuCard({
      icon: <FaClipboardList aria-hidden="true" />,
      label: t("menu.competitionTerms"),
      onClick: () => {
        openMenuHomeSection("WETTBEWERBSBEDINGUNGEN");
      },
      active: homeSection === "WETTBEWERBSBEDINGUNGEN",
      index: nativeIosAppReviewAvailable ? 4 : 3,
    })}

    {renderMenuCard({
      icon: "ℹ",
      label: t("menu.about"),
      onClick: () => {
        openMenuHomeSection("ABOUT");
      },
      active: homeSection === "ABOUT",
      index: nativeIosAppReviewAvailable ? 5 : 4,
    })}

    {renderMenuCard({
      icon: "↪",
      label: t("menu.logout"),
      onClick: handleLogout,
      danger: true,
      index: nativeIosAppReviewAvailable ? 6 : 5,
    })}

    {menuNotice && (
      <div
        style={{
          marginTop: "10px",
          padding: "10px 12px",
          borderRadius: "12px",
          background: "rgba(255,255,255,0.08)",
          color: "rgba(255,255,255,0.9)",
          fontSize: "13px",
          lineHeight: 1.3,
        }}
      >
        {menuNotice}
      </div>
    )}
  </div>
)}
      </div>

      {/* ✅ EINZIGE Umschaltung: LIST ODER DETAIL */}
      {view === "LIST" ? (
	        <>
	          <div
	style={{
	  minHeight: "calc(100dvh - 120px)", // Platz für TopBar/TopHeader oben
	  overflowY: "visible",
	  overflowX: "hidden",
	  paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))", // Platz für Floating BottomBar
	  paddingTop: "1px",
	}}


          >
            {/* (Hier bleibt dein kompletter LIST-Inhalt so wie er war)
                Du musst hier nichts inhaltlich ändern – nur die Struktur ist jetzt sauber. */}

            {/* --- DEIN LIST-CONTENT START --- */}

<TopBar
  onBack={
    homeSection === "TOURNAMENTS"
      ? () => setHomeSection(null)
   : homeSection === "PROFILE"
? () => {
    const backTarget = profileBackTarget;

    setViewedProfileUid(null);
    setViewedProfileConnectionDetails(null);
    setProfileBackTarget(null);

    if (backTarget === "TOURNAMENT_DETAIL") {
      setView("DETAIL");
      return;
    }

    if (backTarget === "SINGLE_MATCH") {
      setHomeSection("SINGLES");
      setSingleStep("matchFound");
      return;
    }

    if (backTarget === "FRIENDS") {
      setHomeSection("COMMUNITY_FRIENDS");
      return;
    }

    setHomeSection(null);
  }
      : homeSection === "GAME"
      ? () => setHomeSection(null)
      : homeSection === "ADMIN_PANEL"
      ? () => {
          if (typeof adminPanelBackHandler === "function") {
            const handled = adminPanelBackHandler();
            if (handled === true) return;
          }
          setHomeSection(null);
        }
      : homeSection === "CREATOR_TOURNAMENTS"
      ? () => setHomeSection(null)
      : homeSection === "COMMUNITY"
      ? () => setHomeSection(null)
      : homeSection === "SHOP"
? () => setHomeSection(null)
	: homeSection === "VOUCHERS"
	? () => setHomeSection(null)
	: homeSection === "ABOUT"
	? handleMenuHomeSectionBack
	      : homeSection === "AGB"
	? handleMenuHomeSectionBack
	: homeSection === "DATENSCHUTZ"
	? handleMenuHomeSectionBack
	: homeSection === "IMPRESSUM"
	? handleMenuHomeSectionBack
	: homeSection === "WETTBEWERBSBEDINGUNGEN"
	? handleMenuHomeSectionBack
	: homeSection === "FAQ"
	? () => setHomeSection("ABOUT")
	      : homeSection === "COMMUNITY_LEADERBOARDS"
      ? () => setHomeSection("COMMUNITY")
      : homeSection === "COMMUNITY_FRIENDS"
      ? () => setHomeSection("COMMUNITY")
      : homeSection === "COMMUNITY_REWARDS"
? () => setHomeSection("COMMUNITY")
      : homeSection === "COMMUNITY_PRIZES"
? handleMyPrizesBack
      : homeSection === "COMMUNITY_REPORT_BLOCK"
? () => {
    const backTarget = reportBlockBackTarget;
    setReportBlockInitial({ mode: "", username: "", locked: false });
    setReportBlockBackTarget(null);

    if (backTarget === "TOURNAMENT_DETAIL") {
      setHomeSection("TOURNAMENTS");
      setView("DETAIL");
      return;
    }

    if (backTarget === "SINGLE_MATCH") {
      setHomeSection("SINGLES");
      setSingleStep("matchFound");
      return;
    }

    setHomeSection("COMMUNITY");
  }
      : homeSection === "FAIRPLAY"
      ? () => {
          if (fairplaySubscreen !== "MENU") {
            setFairplaySubscreen("MENU");
            return;
          }
          setHomeSection(null);
        }
      : homeSection === "SINGLES" && singleStep !== "queue"
? () => {
    if (profileBackTarget === "SINGLE_MATCH") {
      setViewedProfileUid(null);
      setViewedProfileConnectionDetails(null);
      setProfileBackTarget(null);
      return;
    }
    if (singleStep === "stake") {
      setHomeSection(null);
      return;
    }
    if (singleStep === "mode") {
      setSingleStep("stake");
      return;
    }
    if (singleStep === "challenge") {
      setSingleStep("stake");
      return;
    }
    if (singleStep === "matchFound") {
      setSingleStep("stake");
      return;
    }
  }
      : undefined
  }
  onMenu={() => setMenuOpen((prev) => !prev)}
  profile={profile}
  centerTitle={false}
  leftShift={-15}
  backShift={
  homeSection === "TOURNAMENTS" ||
  homeSection === "PROFILE" ||
  homeSection === "GAME" ||
  homeSection === "ADMIN_PANEL" ||
  homeSection === "CREATOR_TOURNAMENTS" ||
  homeSection === "COMMUNITY" ||
  homeSection === "SHOP" ||
  homeSection === "VOUCHERS" ||
  homeSection === "ABOUT" ||
  homeSection === "AGB" ||
  homeSection === "DATENSCHUTZ" ||
  homeSection === "IMPRESSUM" ||
  homeSection === "WETTBEWERBSBEDINGUNGEN" ||
  homeSection === "FAQ" ||
  homeSection === "FAIRPLAY" ||
  homeSection === "COMMUNITY_LEADERBOARDS" ||
  homeSection === "COMMUNITY_FRIENDS" ||
  homeSection === "COMMUNITY_REWARDS" ||
  homeSection === "COMMUNITY_PRIZES"
    ? 5
    : homeSection === "SINGLES" && singleStep !== "queue"
    ? 14
    : 0
}
  notificationsOpen={notificationsOpen}
  onOpenNotifications={() => setNotificationsOpen(true)}
  onCloseNotifications={() => setNotificationsOpen(false)}
/>





{headerCfg.title && (
  <div
    style={{
      marginTop:
        homeSection === null ? 30 :
        homeSection === "SINGLES" ? 14 :
        35,
    }}
  >
    <TopHeader title={headerCfg.title} size={headerCfg.size} />
  </div>
)}







{joinMessage && <p>{joinMessage}</p>}
{loadingProfile && <p>{t("dashboard.profile.loading")}</p>}

{!loadingProfile && profile && (
  <>
    {/* ACTION CARDS */}
    {homeSection === null && (
      <div className={`vq-home-shell${Capacitor.isNativePlatform() ? " is-native" : ""}`}>
        <style>
          {`
            .vq-home-shell {
              position: relative;
              display: grid;
              gap: 18px;
              margin-top: 18px;
              padding-bottom: 0;
              width: 100%;
              max-width: 100%;
              overflow-x: clip;
              container-type: inline-size;
            }

            .vq-home-greeting {
              display: grid;
              gap: 4px;
              text-align: left;
            }

            .vq-home-greeting h1 {
              margin: 0;
              color: #fff;
              font-size: clamp(24px, 4vw, 34px);
              line-height: 1.08;
              font-weight: 1000;
              letter-spacing: 0;
            }

            .vq-home-greeting p {
              margin: 0;
              color: rgba(255,255,255,0.72);
              font-size: 15px;
              font-weight: 700;
            }

            .vq-home-primary-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 14px;
            }

            .vq-home-primary-card,
            .vq-home-preview,
            .vq-home-row-card {
              border: 1px solid rgba(255,255,255,0.14);
              color: #fff;
              cursor: pointer;
              -webkit-tap-highlight-color: transparent;
              box-shadow: 0 16px 34px rgba(0,0,0,0.24);
            }

            .vq-home-primary-card {
              position: relative;
              min-height: 118px;
              padding: 16px 40px 16px 15px;
              border-radius: 18px;
              overflow: hidden;
              display: grid;
              grid-template-columns: auto minmax(0, 1fr);
              align-items: center;
              gap: 11px;
              text-align: left;
              background: linear-gradient(135deg, #ff0b86 0%, #b620d6 52%, #6927ff 100%);
            }

            .vq-home-primary-card.vq-home-singles {
              background: linear-gradient(135deg, #246dff 0%, #4f29df 48%, #7725ff 100%);
            }

            .vq-home-primary-card.vq-home-tournaments .vq-home-primary-title {
              font-size: 18px;
            }

            .vq-home-primary-card.vq-home-singles .vq-home-primary-title {
              font-size: 18px;
            }

            .vq-home-primary-title--long-mobile {
              white-space: nowrap;
            }

            .vq-home-primary-card::before,
            .vq-home-preview::before,
            .vq-home-row-card::before {
              content: "";
              position: absolute;
              inset: 0;
              pointer-events: none;
              background:
                linear-gradient(115deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.08) 18%, transparent 42%),
                radial-gradient(circle at 86% 18%, rgba(255,255,255,0.16), transparent 30%);
              opacity: 0.44;
            }

            .vq-home-primary-card > *,
            .vq-home-preview > *,
            .vq-home-row-card > * {
              position: relative;
              z-index: 1;
            }

            .vq-home-primary-icon {
              width: 64px;
              height: 64px;
              display: grid;
              place-items: center;
              filter: drop-shadow(0 12px 16px rgba(0,0,0,0.32));
            }

            .vq-home-primary-copy {
              min-width: 0;
              display: grid;
              gap: 6px;
            }

            .vq-home-primary-title,
            .vq-home-row-title {
              color: #fff;
              font-size: 20px;
              line-height: 1.05;
              font-weight: 1000;
              text-transform: uppercase;
              letter-spacing: 0;
            }

            .vq-home-primary-text,
            .vq-home-row-text,
            .vq-home-preview-meta {
              color: rgba(255,255,255,0.76);
              font-size: 13px;
              line-height: 1.25;
              font-weight: 700;
            }

            .vq-home-card-arrow {
              position: absolute;
              right: 14px;
              bottom: 14px;
              width: 31px;
              height: 31px;
              border-radius: 999px;
              display: grid;
              place-items: center;
              color: #fff;
              background: rgba(255,255,255,0.12);
              font-size: 25px;
              line-height: 1;
              font-weight: 800;
            }

            .vq-home-section-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              margin-bottom: 10px;
            }

            .vq-home-section-head h2 {
              margin: 0;
              color: #fff;
              font-size: 17px;
              font-weight: 1000;
              text-transform: uppercase;
              letter-spacing: 0.8px;
            }

            .vq-home-link-button {
              border: 0;
              background: transparent;
              color: #ff4fd8;
              font-size: 13px;
              font-weight: 900;
              cursor: pointer;
              padding: 4px 0;
            }

            .vq-home-preview {
              position: relative;
              width: 100%;
              min-height: 96px;
              border-radius: 18px;
              padding: 14px;
              display: grid;
              grid-template-columns: auto minmax(0, 1fr) auto;
              align-items: center;
              gap: 14px;
              text-align: left;
              overflow: hidden;
              background: linear-gradient(135deg, rgba(47,38,102,0.92), rgba(23,27,74,0.92));
            }

            .vq-home-tournament-preview {
              grid-template-areas:
                "icon title status"
                "icon meta meta"
                "icon action action";
              gap: 10px 14px;
            }

            .vq-home-preview-icon {
              width: 52px;
              height: 52px;
              display: grid;
              place-items: center;
              filter: drop-shadow(0 8px 12px rgba(0,0,0,0.28));
            }

            .vq-home-tournament-preview .vq-home-preview-icon {
              grid-area: icon;
            }

            .vq-home-preview-main {
              min-width: 0;
            }

            .vq-home-tournament-preview .vq-home-preview-main {
              grid-area: title;
              align-self: end;
            }

            .vq-home-preview-meta-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              min-width: 0;
            }

            .vq-home-tournament-preview .vq-home-preview-meta-row {
              grid-area: meta;
            }

            .vq-home-preview-status-dot {
              justify-self: end;
              align-self: start;
              margin-top: 3px;
            }

            .vq-home-tournament-preview .vq-home-preview-status-dot {
              grid-area: status;
            }

            .vq-home-preview-title {
              display: block;
              color: #fff;
              font-size: 17px;
              font-weight: 1000;
              line-height: 1.1;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .vq-home-status {
              width: fit-content;
              margin-top: 6px;
              border-radius: 999px;
              padding: 3px 9px;
              background: rgba(49,225,112,0.18);
              color: #31e170;
              font-size: 10px;
              font-weight: 1000;
              text-transform: uppercase;
            }

            .vq-home-preview-participants {
              display: block;
              color: rgba(255,255,255,0.66);
              font-size: 12px;
              line-height: 1.15;
              font-weight: 900;
              text-transform: uppercase;
              white-space: nowrap;
              text-align: right;
            }

            .vq-home-open-button {
              border: 0;
              border-radius: 14px;
              padding: 12px 14px;
              color: #fff;
              background: linear-gradient(135deg, #ff168d, #792cff);
              font-weight: 1000;
              white-space: nowrap;
            }

            .vq-home-tournament-preview .vq-home-open-button {
              grid-area: action;
              justify-self: start;
            }

            .vq-home-card-stack {
              display: grid;
              gap: 12px;
            }

            .vq-home-profile-prizes-grid {
              display: grid;
              grid-template-columns: minmax(0, 1fr);
              gap: 18px;
              min-width: 0;
            }

            .vq-home-profile-prizes-grid .vq-home-action-grid {
              grid-template-columns: minmax(0, 1fr) !important;
            }

            @container (min-width: 1100px) {
              .vq-home-shell:not(.is-native) .vq-home-profile-prizes-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                align-items: start;
              }
            }

            .vq-home-row-card {
              position: relative;
              width: 100%;
              min-height: 102px;
              border-radius: 18px;
              padding: 17px 18px;
              display: grid;
              grid-template-columns: auto minmax(0, 1fr) auto;
              align-items: center;
              gap: 14px;
              text-align: left;
              overflow: hidden;
              background: linear-gradient(135deg, #361059 0%, #28115f 52%, #15184b 100%);
            }

            .vq-home-row-icon {
              width: 48px;
              height: 48px;
              display: grid;
              place-items: center;
              flex-shrink: 0;
              filter: drop-shadow(0 8px 12px rgba(0,0,0,0.25));
            }

            .vq-home-socials {
              display: flex;
              align-items: center;
              gap: 6px;
            }

            .vq-home-socials a {
              width: 40px;
              height: 40px;
              display: grid;
              place-items: center;
              color: #fff;
              font-size: 25px;
              text-decoration: none;
            }

            .vq-home-chevron {
              color: rgba(255,255,255,0.86);
              font-size: 34px;
              line-height: 1;
              font-weight: 400;
            }

            .vq-home-creator-admin-grid {
              display: contents;
            }

            @media (min-width: 721px) {
              .vq-home-shell:not(.is-native) .vq-home-creator-admin-grid {
                display: grid;
                grid-template-columns: minmax(0, 1fr);
                align-items: start;
                gap: 18px;
              }

              .vq-home-shell:not(.is-native) .vq-home-creator-admin-grid .vq-home-action-grid {
                grid-template-columns: minmax(0, 1fr);
              }

              .vq-home-shell:not(.is-native) .vq-home-action-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                align-items: stretch;
              }

              .vq-home-shell:not(.is-native) .vq-home-social-grid > * {
                grid-column: 1 / -1;
              }

              .vq-home-shell:not(.is-native) .vq-home-action-grid .vq-home-row-card {
                min-height: 178px;
                grid-template-columns: minmax(0, 1fr) auto;
                grid-template-rows: auto auto 1fr;
                align-content: start;
                align-items: start;
                padding: 20px;
                gap: 10px 14px;
              }

              .vq-home-shell:not(.is-native) .vq-home-action-grid .vq-home-row-icon {
                width: 58px;
                height: 58px;
                grid-column: 1;
              }

              .vq-home-shell:not(.is-native) .vq-home-action-grid .vq-home-row-copy {
                grid-column: 1 / -1;
              }

              .vq-home-shell:not(.is-native) .vq-home-action-grid .vq-home-socials,
              .vq-home-shell:not(.is-native) .vq-home-action-grid .vq-home-chevron {
                grid-column: 2;
                grid-row: 1;
                align-self: center;
                justify-self: end;
              }

            }

            @media (min-width: 1120px) {
              .vq-home-shell:not(.is-native) .vq-home-action-grid {
                grid-template-columns: repeat(3, minmax(0, 1fr));
              }

            }

            @container (min-width: 1100px) {
              .vq-home-shell:not(.is-native) .vq-home-social-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                align-items: stretch;
              }

              .vq-home-shell:not(.is-native) .vq-home-social-grid > * {
                grid-column: auto;
              }

              .vq-home-shell:not(.is-native) .vq-home-creator-admin-grid.has-two-sections {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                align-items: stretch;
              }
            }

            @media (hover: hover) and (pointer: fine) {
              .vq-home-primary-card:hover,
              .vq-home-preview:hover,
              .vq-home-row-card:hover {
                transform: translateY(-1px) scale(1.004) !important;
                box-shadow:
                  0 18px 38px rgba(0,0,0,0.30),
                  0 0 0 1px rgba(255,255,255,0.24),
                  0 0 30px rgba(255,0,127,0.18) !important;
              }
            }

	            @media (max-width: 430px) {
	              .vq-home-primary-grid {
	                gap: 12px;
	              }

              .vq-home-primary-card {
                min-height: 108px;
                padding: 13px 32px 13px 12px;
                gap: 8px;
              }

              .vq-home-primary-icon {
                width: 54px;
                height: 54px;
              }

              .vq-home-primary-title {
                font-size: 15px;
              }

              .vq-home-primary-card.vq-home-tournaments .vq-home-primary-title {
                font-size: 14px;
              }

              .vq-home-primary-card.vq-home-tournaments .vq-home-primary-title--long-mobile {
                font-size: clamp(10.5px, 2.8vw, 12px);
                letter-spacing: -0.2px;
              }

              .vq-home-primary-card.vq-home-singles .vq-home-primary-title {
                font-size: 14px;
              }

              .vq-home-primary-text {
                font-size: 11px;
                line-height: 1.18;
              }

              .vq-home-card-arrow {
                right: 10px;
                bottom: 10px;
                width: 27px;
                height: 27px;
                font-size: 23px;
              }

              .vq-home-preview {
                grid-template-columns: auto minmax(0, 1fr);
              }

              .vq-home-tournament-preview {
                grid-template-columns: auto minmax(0, 1fr) auto;
              }

              .vq-home-open-button {
                grid-column: 2;
                justify-self: start;
                padding: 9px 12px;
              }

              .vq-home-tournament-preview .vq-home-open-button {
	                grid-column: auto;
	              }
	            }

	            @media (max-width: 720px) {
	              .vq-delete-account-modal {
	                width: min(100%, 392px) !important;
	                max-height: calc(100dvh - 44px - env(safe-area-inset-top) - env(safe-area-inset-bottom)) !important;
	                border-radius: 22px !important;
	              }

	              .vq-delete-account-modal-close {
	                top: 12px !important;
	                right: 12px !important;
	                width: 40px !important;
	                height: 40px !important;
	                border-radius: 12px !important;
	                font-size: 17px !important;
	              }

	              .vq-delete-account-modal-body {
	                padding: 14px 16px 15px !important;
	              }

	              .vq-delete-account-modal-header {
	                padding-right: 34px !important;
	              }

	              .vq-delete-account-modal-icon {
	                width: 58px !important;
	                height: 58px !important;
	                margin-bottom: 6px !important;
	                border-radius: 18px !important;
	                font-size: 29px !important;
	                filter: drop-shadow(0 0 18px rgba(255, 68, 99, 0.30)) !important;
	              }

	              .vq-delete-account-modal-title {
	                margin-bottom: 5px !important;
	                font-size: clamp(21px, 6.6vw, 28px) !important;
	                line-height: 1 !important;
	                letter-spacing: 1.1px !important;
	              }

	              .vq-delete-account-modal-subtitle {
	                margin-bottom: 12px !important;
	                font-size: clamp(13px, 3.5vw, 15px) !important;
	                line-height: 1.22 !important;
	              }

	              .vq-delete-account-modal-warning {
	                padding: 12px 13px !important;
	                margin-bottom: 12px !important;
	                border-radius: 14px !important;
	              }

	              .vq-delete-account-modal-warning-title {
	                gap: 10px !important;
	                margin-bottom: 8px !important;
	                font-size: 13px !important;
	                line-height: 1.18 !important;
	                letter-spacing: 0.7px !important;
	              }

	              .vq-delete-account-modal-warning-item {
	                grid-template-columns: 22px 1fr !important;
	                gap: 10px !important;
	                margin-top: 7px !important;
	                font-size: 12.5px !important;
	                line-height: 1.24 !important;
	              }

	              .vq-delete-account-modal-warning-item > span:first-child {
	                font-size: 15px !important;
	                padding-top: 1px !important;
	              }

	              .vq-delete-account-modal-password-label {
	                margin-bottom: 6px !important;
	                font-size: 12px !important;
	                letter-spacing: 0.75px !important;
	              }

	              .vq-delete-account-modal-password-wrap {
	                margin-bottom: 10px !important;
	              }

		              .vq-delete-account-modal-password-input {
		                min-height: 46px !important;
		                padding: 12px 48px 12px 48px !important;
		                border-radius: 12px !important;
		                font-size: 16px !important;
		                line-height: 1.2 !important;
		              }

	              .vq-delete-account-modal-confirmation {
	                grid-template-columns: 24px 1fr !important;
	                gap: 10px !important;
	                margin-bottom: 12px !important;
	                font-size: 12.5px !important;
	                line-height: 1.28 !important;
	              }

	              .vq-delete-account-modal-confirmation > span:first-of-type {
	                width: 20px !important;
	                height: 20px !important;
	                border-radius: 6px !important;
	                font-size: 14px !important;
	              }

	              .vq-delete-account-modal-message {
	                margin-bottom: 12px !important;
	                padding: 9px 11px !important;
	                font-size: 12.5px !important;
	                line-height: 1.25 !important;
	              }

	              .vq-delete-account-modal-actions {
	                gap: 10px !important;
	              }

	              .vq-delete-account-modal-action {
	                min-height: 48px !important;
	                padding: 10px 9px !important;
	                border-radius: 12px !important;
	                font-size: 13px !important;
	                letter-spacing: 0.85px !important;
	              }
	            }

	            @media (max-width: 360px) {
	              .vq-home-primary-grid {
	                display: flex;
                overflow-x: auto;
                scroll-snap-type: x mandatory;
                padding-bottom: 4px;
                margin-right: -8px;
                -webkit-overflow-scrolling: touch;
              }

              .vq-home-primary-card {
                min-width: 78%;
                scroll-snap-align: start;
              }
            }
          `}
        </style>

        {!isPlayerProfileComplete && (
          <button
            type="button"
            onClick={openAccountSettings}
            style={{
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 16,
              background: "rgba(255,255,255,0.07)",
              color: "white",
              padding: "12px 14px",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              cursor: "pointer",
              boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
            }}
          >
            <span style={{ fontWeight: 900, fontSize: 13, letterSpacing: 0.4 }}>
              {t("profileCompletion.dashboardHint")}
            </span>
            <span style={{ color: "#ff4fd8", fontWeight: 1000, fontSize: 13, whiteSpace: "nowrap" }}>
              {t("profileCompletion.dashboardCta")}
            </span>
          </button>
        )}

        <section className="vq-home-greeting">
          <h1>{t("home.greeting", { name: dashboardUserName })}</h1>
          <p>{t("home.greetingSubtitle")}</p>
        </section>

        <div className="vq-home-primary-grid">
          <button
            type="button"
            className="vq-home-primary-card vq-home-tournaments"
            onClick={() => setHomeSection("TOURNAMENTS")}
          >
            <span className="vq-home-primary-icon">
              <TrophyIcon size={dashboardTabletLayout ? 70 : 58} />
            </span>
            <span className="vq-home-primary-copy">
              <span className={`vq-home-primary-title${String(t("home.joinTournament") || "").length > 8 ? " vq-home-primary-title--long-mobile" : ""}`}>
                {t("home.joinTournament")}
              </span>
              <span className="vq-home-primary-text">{t("home.joinTournamentText")}</span>
            </span>
            <span className="vq-home-card-arrow" aria-hidden="true">›</span>
          </button>

          <button
            type="button"
            className="vq-home-primary-card vq-home-singles"
            onClick={() => {
              setSingleStep("stake");
              setHomeSection("SINGLES");
            }}
          >
            <span className="vq-home-primary-icon">
              <SinglesIcon size={dashboardTabletLayout ? 70 : 58} />
            </span>
            <span className="vq-home-primary-copy">
              <span className="vq-home-primary-title">{t("home.singles")}</span>
              <span className="vq-home-primary-text">{t("home.singlesText")}</span>
            </span>
            <span className="vq-home-card-arrow" aria-hidden="true">›</span>
          </button>
        </div>

        {upcomingDashboardCups.length > 0 && (
          <section>
            <div className="vq-home-section-head">
              <h2>{t("home.upcomingTitle")}</h2>
              <button
                type="button"
                className="vq-home-link-button"
                onClick={() => setHomeSection("TOURNAMENTS")}
              >
                {t("home.showAll")} ›
              </button>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {upcomingDashboardCups.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="vq-home-preview vq-home-tournament-preview"
                  onClick={() => handleOpenTournamentOverviewCard(item.tournament)}
                >
                  <span className="vq-home-preview-icon">
                    {(() => {
                      const PreviewIcon = getDashboardTournamentIcon(item.tournament);
                      return <PreviewIcon size={50} />;
                    })()}
                  </span>
                  <span className="vq-home-preview-main">
                    <strong className="vq-home-preview-title">
                      {getDashboardTournamentName(item.tournament)}
                    </strong>
                  </span>
                  {(() => {
                    const statusKey = getTournamentStatusKeyForCard(item.tournament, {
                      playerCount: getDashboardParticipantCount(item.tournament),
                      maxPlayers: getDashboardMaxPlayers(item.tournament),
                    });
                    const statusDefinition = getTournamentStatusVisualDefinition(statusKey, language);
                    const statusLabelPrefix = language === "de" ? "Turnierstatus" : "Tournament status";

                    return (
                      <span
                        className="vq-home-preview-status-dot"
                        aria-label={`${statusLabelPrefix}: ${statusDefinition.ariaLabel}`}
                        title={statusDefinition.label}
                        style={{
                          ...tournamentStatusDotBaseStyle,
                          background: statusDefinition.style.color,
                          color: statusDefinition.style.color,
                          border: statusDefinition.style.dotBorder || "none",
                          boxShadow: statusDefinition.style.dotBoxShadow || tournamentStatusDotBaseStyle.boxShadow,
                        }}
                      />
                    );
                  })()}
                  <span className="vq-home-preview-meta-row">
                    <span className="vq-home-preview-meta">
                      {formatDashboardDateTime(item.startMillis)}
                    </span>
                    <span className="vq-home-preview-participants">
                      {getDashboardParticipantsLabel(item.tournament)}
                    </span>
                  </span>
                  <span className="vq-home-open-button">{t("home.toTournament")} ›</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {dashboardActiveItems.length > 0 && (
          <section>
            <div className="vq-home-section-head">
              <h2>{t("home.activeMatchesTitle")}</h2>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {dashboardActiveItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="vq-home-preview"
                  onClick={item.onClick}
                >
                  <span className="vq-home-preview-icon">
                    {item.type === "single" ? <SinglesIcon size={44} /> : <TrophyIcon size={44} />}
                  </span>
                  <span className="vq-home-preview-main">
                    <strong className="vq-home-preview-title">{item.title}</strong>
                    <span className="vq-home-preview-meta">{item.meta}</span>
                    <span className="vq-home-status">{item.status}</span>
                  </span>
                  <span className="vq-home-open-button">{t("home.toMatch")} ›</span>
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="vq-home-profile-prizes-grid">
        <section>
          <div className="vq-home-section-head">
            <h2>{t("home.profileSection")}</h2>
          </div>
          <div className="vq-home-card-stack vq-home-action-grid">
            {renderHomeActionCard({
              icon: <ProfileIcon size={48} />,
              title: t("home.profile"),
              text: t("home.profileText"),
              onClick: () => {
                  setViewedProfileUid(null);
                  setViewedProfileConnectionDetails(null);
                  setHomeSection("PROFILE");
              },
            })}
          </div>
        </section>

        <section>
          <div className="vq-home-section-head">
            <h2>{t("home.prizesSection")}</h2>
          </div>
          <div className="vq-home-card-stack vq-home-action-grid">
            {renderHomeActionCard({
              icon: <TrophyIcon size={48} />,
              title: t("home.myPrizes"),
              text: t("home.myPrizesText"),
              onClick: () => openMyPrizes(),
            })}
          </div>
        </section>
        </div>

        <section>
          <div className="vq-home-section-head">
            <h2>{t("home.socialSection")}</h2>
          </div>
          <div className="vq-home-card-stack vq-home-action-grid vq-home-social-grid">
            {renderHomeActionCard({
              icon: <CommunityIcon size={52} />,
              title: t("home.community"),
              text: t("home.communityText"),
              onClick: () => setHomeSection("COMMUNITY"),
              trailing: (
                <span className="vq-home-socials" aria-hidden="true">
                <a
                  href="https://instagram.com/vaultquestofficial"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </a>
                <a
                  href="https://tiktok.com/@vaultquest_de"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="TikTok"
                >
                  <FaTiktok />
                </a>
                <span className="vq-home-chevron">›</span>
              </span>
              ),
            })}

            {renderHomeActionCard({
              icon: <TrustIcon size={44} />,
              title: t("home.fairplay"),
              text: t("home.fairplayText"),
              onClick: () => setHomeSection("FAIRPLAY"),
            })}
          </div>
        </section>

        <div className={`vq-home-creator-admin-grid${hasCreatorTournamentAccess && hasAdminAccess ? " has-two-sections" : ""}`}>
        {hasCreatorTournamentAccess && (
          <section>
            <div className="vq-home-section-head">
              <h2>{t("home.creatorSection")}</h2>
            </div>
            <div className="vq-home-card-stack vq-home-action-grid">
            {renderHomeActionCard({
              icon: (
                <img
                  src={ownTournamentsIconUrl}
                  alt=""
                  aria-hidden="true"
                  style={{ width: 48, height: 48, display: "block", objectFit: "contain" }}
                />
              ),
              title: t("home.creatorTournaments"),
              text: t("home.creatorTournamentsText"),
              onClick: () => setHomeSection("CREATOR_TOURNAMENTS"),
            })}
            </div>
          </section>
        )}

        {hasAdminAccess && (
          <section>
            <div className="vq-home-section-head">
              <h2>{t("home.adminSection")}</h2>
            </div>
            <div className="vq-home-card-stack vq-home-action-grid">
            {renderHomeActionCard({
              icon: <ConflictIcon size={48} />,
              title: "Admin Panel",
              text: t("home.adminPanelText"),
              onClick: () => setHomeSection("ADMIN_PANEL"),
            })}
            </div>
          </section>
        )}
        </div>

      </div>
    )}

{homeSection === "ADMIN_PANEL" && hasAdminAccess && (
  <div data-vq-admin-panel>
    <AdminPanel
      currentUid={currentUid}
      currentUserRole={profile?.role}
      onBackHandlerChange={setAdminPanelBackHandler}
      adminRecipientUsernames={adminRecipientUsernames}
      setAdminRecipientUsernames={setAdminRecipientUsernames}
      adminNotificationTitle={adminNotificationTitle}
      setAdminNotificationTitle={setAdminNotificationTitle}
      adminNotificationMessage={adminNotificationMessage}
      setAdminNotificationMessage={setAdminNotificationMessage}
      sendingAdminNotification={sendingAdminNotification}
      handleSendAdminNotification={handleSendAdminNotification}
    />
  </div>
)}

{homeSection === "CREATOR_TOURNAMENTS" && hasCreatorTournamentAccess && (
  <CreatorTournamentsPage onOpenTournament={handleOpenCreatorTournament} />
)}

{/* PROFILE */}
{homeSection === "PROFILE" && (
  <div style={{ marginTop: "22px" }}>
    <ProfilePage
      profile={
        viewedProfileUid
          ? viewedProfileUid === currentUid
            ? { uid: currentUid, ...(profile || {}) }
            : { uid: viewedProfileUid }
          : profile
      }
      initialConnectionDetails={viewedProfileConnectionDetails}
      onAddFriend={handleSendFriendRequest}
      onReportUser={(uid, username) =>
        openReportBlockForUser(uid, "report", username, profileBackTarget)
      }
      onBlockUser={(uid, username) =>
        openReportBlockForUser(uid, "block", username, profileBackTarget)
      }
      onOpenProfile={(uid) => handleOpenForeignProfile(uid, profileBackTarget)}
    />
  </div>
)}

{/*AGB, Datenschutz & Impressum*/}
{(homeSection === "AGB" ||
  homeSection === "DATENSCHUTZ" ||
  homeSection === "IMPRESSUM") && (
  <div style={{ marginTop: "22px" }}>

    <div
  style={{
    width: "92%",
    margin: "0 auto 14px auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "10px",
  }}
>
  <button
    type="button"
    onClick={() => setHomeSection("AGB")}
    style={{
      border: "none",
      borderRadius: "12px",
      padding: "12px 10px",
      fontWeight: 800,
      fontSize: "14px",
      cursor: "pointer",
      background:
        homeSection === "AGB"
          ? "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)"
          : "rgba(255,255,255,0.08)",
      color: "white",
    }}
  >
   {t("header.agb")}
  </button>

  <button
    type="button"
    onClick={() => setHomeSection("DATENSCHUTZ")}
    style={{
      border: "none",
      borderRadius: "12px",
      padding: "12px 10px",
      fontWeight: 800,
      fontSize: "14px",
      cursor: "pointer",
      background:
        homeSection === "DATENSCHUTZ"
          ? "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)"
          : "rgba(255,255,255,0.08)",
      color: "white",
    }}
  >
    {t("header.privacy")}
  </button>

  <button
    type="button"
    onClick={() => setHomeSection("IMPRESSUM")}
    style={{
      border: "none",
      borderRadius: "12px",
      padding: "12px 10px",
      fontWeight: 800,
      fontSize: "14px",
      cursor: "pointer",
      background:
        homeSection === "IMPRESSUM"
          ? "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)"
          : "rgba(255,255,255,0.08)",
      color: "white",
    }}
  >
    {t("header.impressum")}
  </button>
</div>

    {homeSection === "AGB" && <AgbPage />}
{homeSection === "DATENSCHUTZ" && <DatenschutzPage />}
{homeSection === "IMPRESSUM" && <ImpressumPage />}
  </div>
)}

{homeSection === "WETTBEWERBSBEDINGUNGEN" && (
  <div style={{ marginTop: "22px" }}>
    <CompetitionTermsPage />
  </div>
)}


{homeSection === "GAME" && <GamePage />}
{homeSection === "SHOP" && <ShopPage />}

{homeSection === "VOUCHERS" && currentUid && (
  <VouchersPage currentUid={currentUid} />
)}



    {/* TOURNAMENTS */}
    {homeSection === "TOURNAMENTS" && (
      <div style={{ marginTop: "14px" }}>
        <TournamentsSection
        key={tournamentsSectionKey}
          tournaments={tournaments}
          currentUid={currentUid}
          joinTournament={joinTournament}
          setSelectedTournamentId={setSelectedTournamentId}
          setView={setView}
          onOpenTournamentDetail={handleOpenTournamentDetail}

          onBack={() => setHomeSection(null)}
          selectedDayKey={selectedDayKey}
          setSelectedDayKey={setSelectedDayKey}
          onRequestLeave={handleRequestLeave}
          hasAdminAccess={hasAdminAccess}
          canJoinGames={isPlayerProfileComplete}
          onCompletePlayerProfile={openAccountSettings}
          onOpenMyPrizes={() => openMyPrizes("TOURNAMENTS")}

        />
      </div>
    )}

{homeSection === "SINGLES" && (
  <div style={{ marginTop: "22px" }}>


  <SingleGamesPage
  currentUid={currentUid}
  step={singleStep}
  setStep={setSingleStep}
  onBack={() => setHomeSection(null)}
  onOpenOpponentProfile={(uid, connectionDetails) =>
  handleOpenForeignProfile(uid, "SINGLE_MATCH", connectionDetails)
}
  onReportOpponent={(uid, username) => openReportBlockForUser(uid, "report", username, "SINGLE_MATCH")}
  onBlockOpponent={(uid, username) => openReportBlockForUser(uid, "block", username, "SINGLE_MATCH")}
  knownUsernamesByUid={usernamesByUid}
  canJoinGames={isPlayerProfileComplete}
  onCompletePlayerProfile={openAccountSettings}
/>
  </div>
)}

{homeSection === "COMMUNITY" && (
  <div style={{ marginTop: "22px" }}>
    <CommunityPage
      questSummary={communityQuestSummary}
      dueSurveyCount={surveyAvailability.dueSurveyCount}
      onUnlockPrivateTournament={handleUnlockPrivateTournament}
      onOpen={(target) => {
        if (target === "SURVEYS") openDueSurvey();
        if (target === "LEADERBOARDS") setHomeSection("COMMUNITY_LEADERBOARDS");
        if (target === "FRIENDS") setHomeSection("COMMUNITY_FRIENDS");
        if (target === "REWARDS") setHomeSection("COMMUNITY_REWARDS");
        if (target === "PRIZES") openMyPrizes("COMMUNITY");
        if (target === "REPORT_BLOCK") {
          setReportBlockInitial({ mode: "", username: "", locked: false });
          setReportBlockBackTarget(null);
          setHomeSection("COMMUNITY_REPORT_BLOCK");
        }
      }}
    />
  </div>
)}

{homeSection === "COMMUNITY_REPORT_BLOCK" && (
  <div style={{ marginTop: "22px" }}>
    <ReportBlockUserPage
      initialMode={reportBlockInitial.mode}
      initialUsername={reportBlockInitial.username}
      initialLocked={reportBlockInitial.locked}
    />
  </div>
)}

{homeSection === "FAIRPLAY" && (
  <div style={{ marginTop: "22px" }}>
    <FairPlayCommunityPage trustLevel={profile?.trustLevel ?? 1} />
  </div>
)}

{homeSection === "ABOUT" && (
  <div style={{ marginTop: "22px", paddingBottom: "100px" }}>
    <div
      style={{
        width: "92%",
        margin: "0 auto",
        padding: "14px", // 🔥 kleiner
        borderRadius: "20px",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.15)",
        color: "white",
        textAlign: "center",
        boxSizing: "border-box", // 🔥 EXTREM wichtig
      }}
    >


    {/* LOGO */}
    <img
      src="/vaultquest-logo.png"
      alt="VaultQuest Logo"
      style={{
        width: "80px",
        marginBottom: "12px",
        opacity: 0.95,
      }}
    />

    {/* TITLE */}
    <div style={{ fontSize: "22px", fontWeight: 900, marginBottom: "10px" }}>
      VAULTQUEST
    </div>

    {/* VERSION */}
    <div
      style={{
        fontSize: "12px",
        opacity: 0.7,
        marginBottom: "14px",
        letterSpacing: "1px",
      }}
    >
      {t("dashboard.about.version")}
    </div>

    {/* TEXT */}
    <div style={{ fontSize: "14px", opacity: 0.85, lineHeight: 1.5 }}>
      {t("dashboard.about.text")}
    </div>

    <button
      type="button"
      onClick={() => setHomeSection("FAQ")}
      style={{
        width: "100%",
        marginTop: "16px",
        border: "none",
        borderRadius: "14px",
        padding: "13px 14px",
        color: "white",
        background: "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)",
        fontWeight: 900,
        fontSize: "14px",
        letterSpacing: "0.8px",
        textTransform: "uppercase",
        cursor: "pointer",
      }}
    >
      {t("menu.faqs")}
    </button>
  </div>
</div>
)}

{homeSection === "FAQ" && (
  <div style={{ marginTop: "22px", paddingBottom: "100px" }}>
    <FaqPage />
  </div>
)}


  </>
)}

{homeSection === "COMMUNITY_LEADERBOARDS" && (
  <div style={{ marginTop: "22px" }}>
    <LeaderboardsPage
      currentUid={currentUid}
      onOpenProfile={(uid) => handleOpenForeignProfile(uid, "COMMUNITY_LEADERBOARDS")}
      onReportUser={(uid, username) => openReportBlockForUser(uid, "report", username)}
      onBlockUser={(uid, username) => openReportBlockForUser(uid, "block", username)}
    />
  </div>
)}

{homeSection === "COMMUNITY_FRIENDS" && (
  <div style={{ marginTop: "22px" }}>
        <FriendsPage
          currentUid={currentUid}
          onOpenProfile={(uid) => handleOpenForeignProfile(uid, "FRIENDS")}
          onReportUser={(uid, username) => openReportBlockForUser(uid, "report", username)}
          onBlockUser={(uid, username) => openReportBlockForUser(uid, "block", username)}
        />
  </div>
)}

{homeSection === "COMMUNITY_REWARDS" && (
  <div style={{ marginTop: "22px" }}>
    <RewardsPage currentUid={currentUid} />
  </div>
)}

{homeSection === "COMMUNITY_PRIZES" && (
  <div style={{ marginTop: "22px" }}>
    <MyPrizesPage />
  </div>
)}

{!loadingProfile && profile && <CopyrightFooter />}



{!loadingProfile && !profile && <p>{t("dashboard.profile.notFound")}</p>}

            {/* --- DEIN LIST-CONTENT ENDE --- */}
          </div>


        </>
      ) : (













	        // DETAIL-View (ohne BottomBar)
	        <div
	style={{
	  minHeight: "calc(100dvh - 120px)",
	  overflowY: "visible",
	  overflowX: "hidden",
	  paddingBottom: "180px",         // ✅ Platz für BottomBar
	  paddingLeft: "0px",
  paddingRight: "0px",
  paddingTop: "0px",
}}

>




         <TopBar
  onBack={() => setView("LIST")}
  onMenu={() => setMenuOpen((prev) => !prev)}
  profile={profile}
  centerTitle={false}
  leftShift={-18}
  backShift={14}
  notificationsOpen={notificationsOpen}
  onOpenNotifications={() => setNotificationsOpen(true)}
  onCloseNotifications={() => setNotificationsOpen(false)}
/>




          <div style={{ marginTop: 25 }}>
  <TopHeader title={t("header.tournaments")} />
</div>

{(() => {
  // selectedTournamentId ist z.B. "2026-02-11_evening"
  const parts = String(selectedTournamentId || "").split("_");
  if (parts.length < 2) return null;

  const baseId = parts[1];       // "evening"

const startAt =
  selectedTournament?.startAt?.toDate?.() || null;

if (!startAt) return null;



// Turnier gilt als gestartet, wenn es läuft ODER bereits beendet ist
const isStarted =
  selectedTournament?.status === "running" ||
  selectedTournament?.status === "finished" ||
  selectedTournament?.status === "void";

const startTimeText = startAt.toLocaleTimeString("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
});

const lobbyTournament = selectedTournament || {
  id: selectedTournamentId,
  tournamentId: selectedTournamentId,
  type: baseId,
};
const LobbyTournamentIcon = getDashboardTournamentIcon(lobbyTournament);
const lobbyTournamentName = getDashboardTournamentName(lobbyTournament);
const lobbyStartLabel =
  language === "de" ? `${startTimeText.replace(":00", "")} UHR` : startTimeText;
const lobbyCountdownParts = String(countdownText || "00:00:00")
  .split(":")
  .map((part) => String(part || "00").padStart(2, "0"))
  .slice(0, 3);
while (lobbyCountdownParts.length < 3) {
  lobbyCountdownParts.push("00");
}
const lobbyParticipantCountText = `${lobbyPlayers.length}/${lobbyMaxPlayers || "–"}`;

return (
    <>

{isStarted ? (
  <>





<TournamentRunningView
  startTime={startTimeText}
  tournamentTab={tournamentTab}
  setTournamentTab={setTournamentTab}
  myMatch={myMatch}
  currentUid={currentUid}
  usernamesByUid={usernamesByUid}
  selectedTournamentId={selectedTournamentId}
  selectedTournament={selectedTournament}
  matches={matches}
  isAdmin={hasAdminAccess}

  // ✅ NEU: Aktionen + State fürs Melden
  reportingMatchId={reportingMatchId}
  setReportingMatchId={setReportingMatchId}
  scoreP1={scoreP1}
  setScoreP1={setScoreP1}
  scoreP2={scoreP2}
  setScoreP2={setScoreP2}
  submitResult={submitResult}

onSubmitResult={submitResult}
onOpenOpponentProfile={(uid, connectionDetails) =>
  handleOpenForeignProfile(uid, "TOURNAMENT_DETAIL", connectionDetails)
}

onAddOpponentFriend={handleSendFriendRequest}
onReportOpponent={(uid, username) => openReportBlockForUser(uid, "report", username, "TOURNAMENT_DETAIL")}
onBlockOpponent={(uid, username) => openReportBlockForUser(uid, "block", username, "TOURNAMENT_DETAIL")}


/>
</>

) : (


        <>
          <style>{`
            .vq-lobby-shell {
              display: grid;
              gap: 22px;
              max-width: min(100%, 900px);
              margin: 0 auto;
              padding-bottom: calc(124px + env(safe-area-inset-bottom));
            }

            .vq-lobby-hero {
              position: relative;
              overflow: hidden;
              border-radius: 28px;
              padding: 24px;
              color: #fff;
              background:
                radial-gradient(circle at 20% 12%, rgba(255, 0, 160, 0.26) 0%, rgba(255, 0, 160, 0.08) 28%, transparent 58%),
                radial-gradient(circle at 92% 12%, rgba(63, 75, 255, 0.24) 0%, rgba(63, 75, 255, 0.08) 30%, transparent 62%),
                linear-gradient(135deg, rgba(33, 23, 88, 0.98) 0%, rgba(13, 13, 59, 0.98) 100%);
              border: 2px solid rgba(255, 35, 180, 0.75);
              box-shadow:
                0 24px 70px rgba(0, 0, 0, 0.34),
                inset 0 1px 0 rgba(255, 255, 255, 0.12);
            }

            .vq-lobby-hero-main {
              display: grid;
              grid-template-columns: clamp(76px, 11vw, 100px) minmax(0, 1fr);
              gap: 24px;
              align-items: center;
            }

            .vq-lobby-trophy {
              width: 100%;
              aspect-ratio: 1;
              display: grid;
              place-items: center;
              pointer-events: none;
              filter: drop-shadow(0 18px 30px rgba(0, 0, 0, 0.32));
            }

            .vq-lobby-trophy svg,
            .vq-lobby-trophy img {
              width: 100% !important;
              height: 100% !important;
              object-fit: contain;
              display: block;
            }

            .vq-lobby-copy {
              min-width: 0;
            }

            .vq-lobby-copy h1 {
              margin: 0;
              font-size: clamp(24px, 2.8vw, 32px);
              line-height: 1;
              font-weight: 1000;
              letter-spacing: 1.2px;
              text-transform: uppercase;
              overflow-wrap: normal;
              word-break: normal;
              hyphens: none;
              text-wrap: balance;
            }

            .vq-lobby-copy p {
              margin: 8px 0 0;
              display: flex;
              flex-wrap: wrap;
              gap: 7px;
              align-items: center;
              color: rgba(255, 255, 255, 0.72);
              font-size: clamp(12px, 1.7vw, 15px);
              font-weight: 1000;
              letter-spacing: 0.9px;
              text-transform: uppercase;
            }

            .vq-lobby-copy p span:first-child {
              color: #f82bd8;
            }

            .vq-lobby-countdown {
              margin: 16px auto 18px;
              max-width: min(100%, 390px);
              text-align: center;
            }

            .vq-lobby-countdown-kicker {
              display: block;
              color: rgba(255, 255, 255, 0.62);
              font-size: 13px;
              font-weight: 1000;
              letter-spacing: 1.2px;
              text-transform: uppercase;
            }

            .vq-lobby-countdown-value {
              margin-top: 6px;
              display: grid;
              grid-template-columns: 1fr auto 1fr auto 1fr;
              align-items: center;
              gap: clamp(6px, 1.5vw, 12px);
              font-size: clamp(30px, 4.1vw, 42px);
              line-height: 0.96;
              font-weight: 1000;
              letter-spacing: 1px;
              text-shadow: 0 10px 24px rgba(0, 0, 0, 0.26);
            }

            .vq-lobby-countdown-value span {
              color: rgba(255, 255, 255, 0.72);
              transform: translateY(-3px);
            }

            .vq-lobby-countdown-labels {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 18px;
              margin-top: 7px;
              color: rgba(255, 255, 255, 0.55);
              font-size: 11px;
              font-weight: 1000;
              letter-spacing: 1px;
              text-transform: uppercase;
            }

            .vq-lobby-hero-footer {
              display: flex;
              justify-content: center;
              gap: 14px;
              align-items: center;
            }

            .vq-lobby-participant-pill,
            .vq-lobby-rules-action {
              min-height: 42px;
              border-radius: 999px;
              background: rgba(255, 255, 255, 0.07);
              border: 1.5px solid rgba(255, 255, 255, 0.12);
              box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
            }

            .vq-lobby-participant-pill {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 6px;
              width: clamp(112px, 15vw, 138px);
              min-width: 0;
              max-width: 138px;
              padding: 0 10px;
              color: rgba(255, 255, 255, 0.78);
              font-size: 12px;
              font-weight: 1000;
              letter-spacing: 0.7px;
              text-transform: uppercase;
            }

            .vq-lobby-participant-pill strong {
              color: #f441ef;
              font-size: 18px;
              white-space: nowrap;
            }

            .vq-lobby-participant-pill svg,
            .vq-lobby-participant-pill img {
              width: 17px;
              height: 17px;
              flex: 0 0 auto;
            }

            .vq-lobby-participant-pill span {
              display: none;
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .vq-lobby-rules-action {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              width: clamp(210px, 31vw, 250px);
              max-width: 250px;
              min-width: 0;
              padding: 0 14px;
              color: white;
              font-size: 12px;
              font-weight: 1000;
              letter-spacing: 0.8px;
              text-transform: uppercase;
              cursor: pointer;
              border-color: rgba(255, 43, 193, 0.9);
              background: rgba(9, 8, 42, 0.38);
              white-space: nowrap;
            }

            .vq-lobby-rules-action span {
              color: #ff35c8;
              font-size: 24px;
              line-height: 1;
              transform: translateY(-1px);
            }

            .vq-lobby-section-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 16px;
              padding: 0 4px;
              color: white;
              text-transform: uppercase;
            }

            .vq-lobby-section-head h2 {
              margin: 0;
              font-size: clamp(24px, 4.2vw, 32px);
              font-weight: 1000;
              letter-spacing: 0.9px;
            }

            .vq-lobby-section-head strong {
              color: #ef39f6;
              font-size: clamp(20px, 3.4vw, 26px);
              font-weight: 1000;
            }

            .vq-lobby-player-list {
              display: grid;
              gap: 10px;
            }

            .vq-lobby-player-row {
              position: relative;
              display: grid;
              grid-template-columns: auto auto minmax(0, 1fr) auto;
              gap: 14px;
              align-items: center;
              min-height: 78px;
              padding: 12px 14px;
              border-radius: 18px;
              background:
                linear-gradient(90deg, rgba(17, 17, 64, 0.92) 0%, rgba(24, 24, 82, 0.9) 100%);
              border: 1.5px solid rgba(255, 255, 255, 0.12);
              box-shadow:
                0 14px 34px rgba(0, 0, 0, 0.18),
                inset 0 1px 0 rgba(255, 255, 255, 0.07);
              color: #fff;
            }

            .vq-lobby-player-row.is-current {
              border-color: rgba(255, 0, 166, 0.95);
              box-shadow:
                0 16px 38px rgba(0, 0, 0, 0.24),
                inset 0 1px 0 rgba(255, 255, 255, 0.1);
            }

            .vq-lobby-player-dot {
              width: 16px;
              height: 16px;
              border-radius: 999px;
              background: #2fe268;
              box-shadow: 0 0 16px rgba(47, 226, 104, 0.5);
            }

            .vq-lobby-player-icon {
              width: 50px;
              aspect-ratio: 1;
              display: grid;
              place-items: center;
              pointer-events: none;
            }

            .vq-lobby-player-icon svg,
            .vq-lobby-player-icon img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              display: block;
            }

            .vq-lobby-player-main {
              min-width: 0;
            }

            .vq-lobby-player-name-line {
              display: flex;
              align-items: center;
              gap: 10px;
              min-width: 0;
            }

            .vq-lobby-player-name {
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              color: white;
              font-size: clamp(22px, 4vw, 30px);
              font-weight: 1000;
              letter-spacing: 0.4px;
            }

            .vq-lobby-you-badge {
              flex: 0 0 auto;
              border-radius: 8px;
              padding: 4px 8px;
              color: white;
              background: linear-gradient(135deg, #ff148f 0%, #8c2cff 100%);
              font-size: 13px;
              font-weight: 1000;
              letter-spacing: 0.8px;
              text-transform: uppercase;
            }

            .vq-lobby-player-menu-button {
              width: 52px;
              height: 52px;
              border-radius: 999px;
              border: none;
              background: rgba(5, 7, 33, 0.62);
              color: white;
              font-weight: 1000;
              font-size: 22px;
              letter-spacing: 1px;
              cursor: pointer;
              box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
            }

            .vq-lobby-action-menu {
              position: fixed;
              border-radius: 28px;
              background:
                radial-gradient(circle at 12% 10%, rgba(152, 71, 255, 0.18), transparent 44%),
                linear-gradient(145deg, rgba(17, 15, 58, 0.98) 0%, rgba(24, 21, 78, 0.98) 58%, rgba(10, 9, 44, 0.98) 100%);
              border: 1px solid rgba(174, 142, 255, 0.28);
              box-shadow:
                0 24px 58px rgba(0, 0, 0, 0.48),
                inset 0 1px 0 rgba(255, 255, 255, 0.1);
              padding: 14px 0;
              overflow: hidden;
              z-index: 9999;
              backdrop-filter: blur(18px);
              -webkit-backdrop-filter: blur(18px);
            }

            .vq-lobby-action-menu-item {
              display: grid;
              grid-template-columns: 34px minmax(0, 1fr) auto;
              align-items: center;
              gap: 14px;
              width: 100%;
              min-height: 56px;
              padding: 0 20px;
              border: 0;
              background: transparent;
              color: white;
              cursor: pointer;
              text-align: left;
              transition: background 140ms ease, transform 140ms ease;
            }

            .vq-lobby-action-menu-item:hover,
            .vq-lobby-action-menu-item:focus-visible {
              background: rgba(255, 255, 255, 0.06);
              outline: none;
            }

            .vq-lobby-action-menu-icon {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              color: #c767ff;
              font-size: 25px;
            }

            .vq-lobby-action-menu-icon.is-warning {
              color: #ff725f;
            }

            .vq-lobby-action-menu-icon.is-danger {
              color: #ff5264;
            }

            .vq-lobby-action-menu-label {
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              color: rgba(255, 255, 255, 0.92);
              font-size: 17px;
              font-weight: 900;
              letter-spacing: 0;
            }

            .vq-lobby-action-menu-item.is-danger .vq-lobby-action-menu-label {
              color: #ff5264;
            }

            .vq-lobby-action-menu-chevron {
              color: rgba(255, 255, 255, 0.86);
              font-size: 18px;
            }

            .vq-lobby-action-menu-divider {
              height: 1px;
              margin: 4px 22px;
              background: rgba(255, 255, 255, 0.11);
            }

            @media (max-width: 760px) {
              .vq-lobby-hero {
                padding: 22px 20px;
              }

              .vq-lobby-hero-main {
                grid-template-columns: clamp(68px, 18vw, 86px) minmax(0, 1fr);
                gap: clamp(34px, 8vw, 44px);
              }

              .vq-lobby-copy h1 {
                font-size: clamp(22px, 5.8vw, 28px);
              }

              .vq-lobby-copy p {
                font-size: clamp(12px, 3.2vw, 15px);
              }

              .vq-lobby-countdown {
                max-width: 100%;
              }
            }

            @media (max-width: 640px) {
              .vq-lobby-hero-footer {
                grid-template-columns: 1fr;
              }

                .vq-lobby-rules-action {
                max-width: none;
                width: 100%;
              }
            }

            @media (max-width: 560px) {
              .vq-lobby-shell {
                gap: 18px;
              }

              .vq-lobby-hero {
                padding: 18px 16px;
                border-radius: 24px;
              }

              .vq-lobby-hero-main {
                grid-template-columns: 58px minmax(0, 1fr);
                gap: 38px;
              }

              .vq-lobby-trophy {
                width: 58px;
              }

              .vq-lobby-copy h1 {
                font-size: clamp(19px, 5.2vw, 24px);
              }

              .vq-lobby-countdown {
                margin: 18px auto 20px;
              }

              .vq-lobby-countdown-value {
                gap: 6px;
              }

              .vq-lobby-countdown-labels {
                gap: 16px;
              }

              .vq-lobby-hero-footer {
                flex-direction: column;
                gap: 12px;
              }

              .vq-lobby-participant-pill,
              .vq-lobby-rules-action {
                width: 100%;
                max-width: none;
              }

              .vq-lobby-participant-pill span {
                display: inline;
              }

              .vq-lobby-player-row {
                grid-template-columns: auto auto minmax(0, 1fr) auto;
                gap: 10px;
                min-height: 72px;
                padding: 11px 12px;
              }

              .vq-lobby-player-icon {
                width: 44px;
              }

              .vq-lobby-player-menu-button {
                width: 48px;
                height: 48px;
              }
            }

            @media (max-width: 380px) {
              .vq-lobby-trophy {
                width: 54px;
              }

              .vq-lobby-copy h1 {
                font-size: 22px;
              }

              .vq-lobby-copy p {
                font-size: 14px;
              }

              .vq-lobby-player-icon {
                width: 38px;
              }
            }
          `}</style>

          <div className="vq-lobby-shell">
            <section className="vq-lobby-hero">
              <div className="vq-lobby-hero-main">
                <span className="vq-lobby-trophy" aria-hidden="true">
                  <LobbyTournamentIcon size="100%" />
                </span>
                <div className="vq-lobby-copy">
                  <h1>{lobbyTournamentName}</h1>
                  <p>
                    <span>{lobbyStartLabel}</span>
                    <span aria-hidden="true">·</span>
                    <span>{t("dashboard.lobby.tournamentLobby")}</span>
                  </p>
                </div>
              </div>

              <div className="vq-lobby-countdown" aria-label={countdownText}>
                <span className="vq-lobby-countdown-kicker">
                  {t("dashboard.lobby.startIn")}
                </span>
                <div className="vq-lobby-countdown-value">
                  <strong>{lobbyCountdownParts[0]}</strong>
                  <span aria-hidden="true">:</span>
                  <strong>{lobbyCountdownParts[1]}</strong>
                  <span aria-hidden="true">:</span>
                  <strong>{lobbyCountdownParts[2]}</strong>
                </div>
                <div className="vq-lobby-countdown-labels" aria-hidden="true">
                  <span>{t("dashboard.lobby.hoursShort")}</span>
                  <span>{t("dashboard.lobby.minutesShort")}</span>
                  <span>{t("dashboard.lobby.secondsShort")}</span>
                </div>
              </div>

              <div className="vq-lobby-hero-footer">
                <div className="vq-lobby-participant-pill">
                  <CommunityIcon size={24} />
                  <strong>{lobbyParticipantCountText}</strong>
                  <span>{t("dashboard.lobby.participants")}</span>
                </div>
                <button
                  type="button"
                  className="vq-lobby-rules-action"
                  onClick={() => setRulesModalOpen(true)}
                >
                  {t("dashboard.lobby.viewRules")}
                  <span aria-hidden="true">›</span>
                </button>
              </div>
            </section>

            <div className="vq-lobby-section-head">
              <h2>{t("dashboard.lobby.participants")}</h2>
              <strong>{lobbyParticipantCountText}</strong>
            </div>

            <div className="vq-lobby-player-list">
              {lobbyPlayers.map((uid) => {
                const playerName = usernamesByUid[uid] || t("common.loadingPlayer");
                const isCurrentLobbyUser = uid === currentUid;

                return (
                  <div
                    key={uid}
                    className={`vq-lobby-player-row${isCurrentLobbyUser ? " is-current" : ""}`}
                  >
                    <span className="vq-lobby-player-dot" aria-hidden="true" />
                    <span className="vq-lobby-player-icon" aria-hidden="true">
                      <CommunityIcon size={50} />
                    </span>
                    <div className="vq-lobby-player-main">
                      <div className="vq-lobby-player-name-line">
                        <span className="vq-lobby-player-name">{playerName}</span>
                        {isCurrentLobbyUser && (
                          <span className="vq-lobby-you-badge">
                            {t("dashboard.lobby.you")}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      ref={openPlayerMenuUid === uid ? playerMenuButtonRef : null}
                      className="vq-lobby-player-menu-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (openPlayerMenuUid === uid) {
                          setOpenPlayerMenuUid(null);
                          setPlayerMenuPosition(null);
                          return;
                        }

                        setPlayerMenuPosition(
                          getLobbyPlayerMenuPosition(e.currentTarget, uid)
                        );
                        setOpenPlayerMenuUid(uid);
                      }}
                      aria-label="Mehr"
                      type="button"
                    >
                      ⋯
                    </button>
                    {openPlayerMenuUid === uid && (
                      <div
                        ref={playerMenuRef}
                        className="vq-lobby-action-menu"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          top: playerMenuPosition?.top ?? 0,
                          left: playerMenuPosition?.left ?? 0,
                          width: playerMenuPosition?.width ?? 304,
                        }}
                      >
                        <button
                          type="button"
                          className="vq-lobby-action-menu-item"
                          onClick={() => {
                            setOpenPlayerMenuUid(null);
                            setPlayerMenuPosition(null);
                            handleOpenForeignProfile(uid, "TOURNAMENT_DETAIL");
                          }}
                        >
                          <span className="vq-lobby-action-menu-icon" aria-hidden="true">
                            <FaRegUser />
                          </span>
                          <span className="vq-lobby-action-menu-label">
                            {t("dashboard.lobby.viewProfile")}
                          </span>
                          <FaChevronRight
                            className="vq-lobby-action-menu-chevron"
                            aria-hidden="true"
                          />
                        </button>

                        {uid !== currentUid && (
                          <>
                            <div className="vq-lobby-action-menu-divider" />

                            <button
                              type="button"
                              className="vq-lobby-action-menu-item"
                              onClick={() => {
                                setOpenPlayerMenuUid(null);
                                setPlayerMenuPosition(null);
                                handleSendFriendRequest(uid);
                              }}
                            >
                              <span className="vq-lobby-action-menu-icon" aria-hidden="true">
                                <FaUserPlus />
                              </span>
                              <span className="vq-lobby-action-menu-label">
                                {t("dashboard.lobby.addFriend")}
                              </span>
                            </button>

                            <div className="vq-lobby-action-menu-divider" />

                            <button
                              type="button"
                              className="vq-lobby-action-menu-item"
                              onClick={() => {
                                setOpenPlayerMenuUid(null);
                                setPlayerMenuPosition(null);
                                openReportBlockForUser(uid, "report", usernamesByUid[uid]);
                              }}
                            >
                              <span
                                className="vq-lobby-action-menu-icon is-warning"
                                aria-hidden="true"
                              >
                                <FaExclamationTriangle />
                              </span>
                              <span className="vq-lobby-action-menu-label">
                                {t("dashboard.lobby.reportUser")}
                              </span>
                            </button>

                            <div className="vq-lobby-action-menu-divider" />

                            <button
                              type="button"
                              className="vq-lobby-action-menu-item is-danger"
                              onClick={() => {
                                setOpenPlayerMenuUid(null);
                                setPlayerMenuPosition(null);
                                openReportBlockForUser(uid, "block", usernamesByUid[uid]);
                              }}
                            >
                              <span
                                className="vq-lobby-action-menu-icon is-danger"
                                aria-hidden="true"
                              >
                                <FaBan />
                              </span>
                              <span className="vq-lobby-action-menu-label">
                                {t("dashboard.lobby.blockUser")}
                              </span>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

<div
  style={{
    position: "sticky",
    bottom: 0,
    padding: "16px",
    background: "transparent",
  }}
>
  <button
    onClick={() => {
      setSelectedLeaveTournament({
        time: "18:00",
        maxPlayers: lobbyMaxPlayers,
        id: selectedTournamentId,
      });
      setLeaveModalOpen(true);
    }}
    style={{
      width: "100%",
      height: 70,
      borderRadius: 18,
      border: "none",
      background: "white",
      color: "#ff3b30",
      fontWeight: 1000,
      letterSpacing: 1,
      fontSize: 18,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {t("dashboard.lobby.leaveTournament")}
  </button>
</div>




        </>
      )}
    </>
  );
})()}






        </div>
      )}
    </div>

 <BottomBar
    activeTab={activeTab}
    setActiveTab={setActiveTab}
    setHomeSection={setHomeSection}
    setView={setView}
    hidden={menuOpen}
  />



    </div>
  </div>

{notificationsOpen && (
  <div
    className="vq-notification-center-backdrop"
    onClick={() => setNotificationsOpen(false)}
    style={{
      position: "fixed",
      inset: 0,
      background: "transparent",
      zIndex: 18500,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      paddingTop: "86px",
      paddingLeft: "14px",
      paddingRight: "14px",
      boxSizing: "border-box",
    }}
  >
   <div
  className="vq-notification-center"
  onClick={(e) => e.stopPropagation()}
  style={{
    width: notificationsMobileLayout
      ? "min(430px, calc(100vw - 28px))"
      : "min(720px, calc(100vw - 32px))",
    height: notificationsMobileLayout
      ? "min(74dvh, calc(100dvh - 112px))"
      : "430px",
    maxHeight: notificationsMobileLayout ? "calc(100dvh - 112px)" : "620px",
    minHeight: notificationsMobileLayout ? "360px" : "320px",
    borderRadius: "22px",
    overflow: "hidden",
    background: "linear-gradient(135deg, #ff007f 0%, #7a2bff 100%)",
    border: "1px solid rgba(255,255,255,5)",
    boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
    backdropFilter: "blur(10px)",
    display: "flex",
    position: "relative",
  }}
>
    <button
      type="button"
      onClick={() => setNotificationsOpen(false)}
      aria-label="Notifications schließen"
      style={{
        position: "absolute",
        top: "10px",
        right: "10px",
        zIndex: 3,
        width: "34px",
        height: "34px",
        borderRadius: "999px",
        border: "2px solid rgba(255,255,255,0.45)",
        background: "rgba(14,8,50,0.72)",
        color: "#fff",
        fontSize: "22px",
        fontWeight: 1000,
        lineHeight: 1,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        boxShadow: "0 10px 24px rgba(0,0,0,0.32)",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      }}
    >
      ×
    </button>
     <div
  style={{
    width: notificationsMobileLayout ? "100%" : "300px",
    flex: notificationsMobileLayout ? "1 1 auto" : "0 0 300px",
    minWidth: 0,
    minHeight: 0,
    borderRight: notificationsMobileLayout
      ? "none"
      : "1px solid rgba(255,255,255,5)",
    display: notificationsMobileLayout && notificationDetailOpen ? "none" : "flex",
    flexDirection: "column",
    background: "rgba(255,255,255,0.02)",
    overflowX: "hidden",
  }}
>
        <div
          style={{
            padding: "16px 18px",
            borderBottom: "1px solid rgba(255,255,255,5)",
            color: "#fff",
            fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
            fontWeight: 700,
            fontSize: "16px",
            letterSpacing: "0.3px",
	            textTransform: "uppercase",
	            flexShrink: 0,
	          }}
        >
          {t("dashboard.notifications.messages")}
        </div>

           <div
          style={{
	            flex: "1 1 auto",
	            minHeight: 0,
	            overflowY: "auto",
	            overflowX: "hidden",
	            WebkitOverflowScrolling: "touch",
	            overscrollBehavior: "contain",
	            display: "flex",
            flexDirection: "column",
          }}
        >
          {notifications.length === 0 ? (
            <div
              style={{
                padding: "14px 18px",
                color: "rgba(255,255,255,0.72)",
                fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
                fontSize: "14px",
                lineHeight: 1.45,
              }}
            >
              {t("dashboard.notifications.none")}
            </div>
          ) : (





            notifications.map((item) => {
              const isSelected = selectedNotification?.id === item.id;

              return (
                <button
                  key={item.id}
                  type="button"
	                 onClick={() => openNotificationItem(item)}
	                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderBottom: "1px solid rgba(255,255,255,0.20)",
                    background: isSelected
                      ? "rgba(255,255,255,0.16)"
                      : "transparent",
	                    minHeight: notificationsMobileLayout ? "58px" : "56px",
	                    padding: notificationsMobileLayout ? "0 18px" : "0 14px",
		                    cursor: "pointer",
		                    color: "white",
		                    overflowX: "hidden",
	                    display: "grid",
                    gridTemplateColumns: notificationsMobileLayout
                      ? "32px minmax(0, 1fr) minmax(82px, 108px) 10px"
                      : "34px minmax(0, 1fr) minmax(94px, 122px) 10px",
	                    alignItems: "center",
	                    columnGap: notificationsMobileLayout ? "10px" : "12px",
	                    boxSizing: "border-box",
		                    fontFamily:
                      "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
                  }}
                >
	                  <span className={`vq-notification-list-icon is-${getNotificationPresentation(item).tone}`}>
	                    {getNotificationPresentation(item).icon}
	                  </span>
	                  <div
	                      style={{
	                        fontWeight: 800,
	                        fontSize: notificationsMobileLayout ? "12px" : "13px",
	                        letterSpacing: "0.4px",
	                        textTransform: "uppercase",
		                        lineHeight: 1.2,
		                        opacity: item?.isRead === false ? 1 : 0.88,
		                        minWidth: 0,
	                        whiteSpace: "normal",
	                        overflowWrap: "anywhere",
		                      }}
		                    >
		                      {item.title || t("dashboard.notifications.notification")}
		                    </div>

		                    <div
		                      style={{
	                        fontSize: notificationsMobileLayout ? "10px" : "11px",
		                        lineHeight: 1.25,
		                        color: "rgba(255,255,255,0.86)",
		                        opacity: item?.isRead === false ? 1 : 0.88,
		                        letterSpacing: "0.2px",
	                        textAlign: "right",
	                        whiteSpace: "normal",
	                        overflowWrap: "anywhere",
		                      }}
		                    >
		                      {formatNotificationDate(item)}
		                    </div>

	                      <div
	                        style={{
	                          width: "8px",
	                          height: "8px",
	                          minWidth: "8px",
	                          borderRadius: "999px",
	                          background: "#ffffff",
	                        opacity: item?.isRead === false ? 1 : 0,
	                        }}
	                      />
		                </button>
              );
            })
          )}
        </div>
      </div>

     <div
  style={{
	    flex: 1,
	    minWidth: 0,
	    minHeight: 0,
	    display: notificationsMobileLayout && !notificationDetailOpen ? "none" : "flex",
	    flexDirection: "column",
	    background: "rgba(255,255,255,0.01)",
	  }}
>
        <div
          style={{
            padding: "16px 18px",
            borderBottom: "1px solid rgba(255,255,255,5)",
            color: "#fff",
            fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
            fontWeight: 700,
	            fontSize: "16px",
	            letterSpacing: "0.3px",
	            textTransform: "uppercase",
	            display: "flex",
	            alignItems: "center",
	            gap: "10px",
	          }}
	        >
	          {notificationsMobileLayout ? (
	            <button
	              type="button"
	              onClick={() => setNotificationDetailOpen(false)}
	              style={{
	                border: "none",
	                background: "transparent",
	                color: "#fff",
	                padding: 0,
	                fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
	                fontWeight: 900,
	                fontSize: "15px",
	                letterSpacing: "0.4px",
	                textTransform: "uppercase",
	                cursor: "pointer",
	              }}
	            >
	              ← {t("common.back")}
	            </button>
	          ) : (
	            t("dashboard.notifications.content")
	          )}
	        </div>

           <div
  style={{
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    padding: "18px",
    color: "white",
    fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
  }}
>
          {!selectedNotification ? (
            <div
              style={{
                color: "rgba(255,255,255,0.74)",
                fontSize: "14px",
                lineHeight: 1.5,
              }}
            >
              {t("dashboard.notifications.selectOne")}
            </div>
          ) : (
            <div className={`vq-notification-detail is-${getNotificationPresentation(selectedNotification).tone}`}>
              <div className="vq-notification-detail-icon">
                {getNotificationPresentation(selectedNotification).icon}
              </div>
              <div
  className="vq-notification-detail-title"
  style={{
    fontWeight: 900,
    fontSize: "18px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    lineHeight: 1.2,
    marginBottom: "12px",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  }}
>
  {selectedNotification.title || t("dashboard.notifications.notification")}
</div>

              <div
                className="vq-notification-detail-date"
                style={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.72)",
                  marginBottom: "16px",
                  letterSpacing: "0.3px",
                  textTransform: "uppercase",
                }}
              >
	                {formatNotificationDate(selectedNotification)}
	              </div>

         <div
  className="vq-notification-detail-body"
  style={{
    fontSize: "15px",
    lineHeight: 1.65,
    color: "rgba(255,255,255,0.92)",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  }}
>
  {selectedNotification?.type === "single_challenge_invite"
    ? t("dashboard.notifications.challengeInviteText", {
    name: selectedNotification?.challengerUsername || t("dashboard.notifications.aUser"),
  })
    : (selectedNotification.body || selectedNotification.message || t("dashboard.notifications.noContent"))}
</div>

{selectedNotification?.type === "survey_available" && (
  <div style={{ marginTop: "18px", display: "grid", gap: "10px" }}>
    {surveyNotificationBlockedId === selectedNotification.id ? (
      <div style={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
        {t("surveys.blockedByPriorityAction")}
      </div>
    ) : surveyNotificationUnavailableId === selectedNotification.id ? (
      <div style={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
        {t("surveys.unavailable")}
      </div>
    ) : (
      <button
        type="button"
        className="vq-notification-action-primary"
        disabled={surveyBusy}
        onClick={handleOpenSurveyNotification}
        style={{
          width: "100%",
          minHeight: "38px",
          border: "2px solid rgba(255,255,255,0.28)",
          borderRadius: "12px",
          background: "rgba(255,255,255,0.08)",
          color: "white",
          fontWeight: 1000,
          letterSpacing: 1,
          cursor: surveyBusy ? "default" : "pointer",
          opacity: surveyBusy ? 0.7 : 1,
        }}
      >
        {t("surveys.notificationAction")}
      </button>
    )}
  </div>
)}

{selectedNotification?.type === "single_challenge_invite" && (
  <div
    style={{
      marginTop: "14px",
      display: "grid",
      gap: "8px",
    }}
  >
    <div
      style={{
        fontSize: "12px",
        color: "rgba(255,255,255,0.82)",
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        fontWeight: 900,
      }}
    >
      {t("dashboard.notifications.stake", {
  amount: selectedNotification.stake || 0,
})}
    </div>

    <div
      style={{
        fontSize: "12px",
        color: "rgba(255,255,255,0.82)",
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        fontWeight: 900,
      }}
    >
      {t("dashboard.notifications.expiresIn", {
  time: challengeCountdownText || "--:--",
})}
    </div>
  </div>
)}

{selectedNotification?.type === "single_challenge_invite" &&
 notificationChallengeStatus &&
 notificationChallengeStatus !== "pending" && (
  <div
    style={{
      marginTop: "14px",
      fontSize: "12px",
      color: "rgba(255,255,255,0.78)",
      letterSpacing: "0.4px",
      textTransform: "uppercase",
      fontWeight: 900,
      lineHeight: 1.5,
    }}
  >
    {notificationChallengeStatus === "accepted" && t("dashboard.notifications.statusAccepted")}
{notificationChallengeStatus === "declined" && t("dashboard.notifications.statusDeclined")}
{notificationChallengeStatus === "expired" && t("dashboard.notifications.statusExpired")}
{notificationChallengeStatus === "cancelled" && t("dashboard.notifications.statusCancelled")}
{notificationChallengeStatus === "missing" && t("dashboard.notifications.statusMissing")}
{notificationChallengeStatus === "error" && t("dashboard.notifications.statusError")}
  </div>
)}

             {inviteStillPending && !challengeResponseDone && (
  <div style={{ marginTop: "18px", display: "grid", gap: "10px" }}>


    <button
      type="button"
      className="vq-notification-action-primary"
      disabled={challengeResponseLoading}
      onClick={() =>
        handleRespondToChallenge(selectedNotification.challengeId, "accept")
      }
    style={{
  width: "100%",
  height: "35px",
  border: "2px solid rgba(255,255,255,0.28)",
  borderRadius: "12px",
  background: "rgba(255,255,255,0.08)",
  color: "white",
  fontWeight: 1000,
  letterSpacing: 1,
  textTransform: "uppercase",
  cursor: challengeResponseLoading ? "default" : "pointer",
  opacity: challengeResponseLoading ? 0.7 : 1,
}}
    >
      {challengeResponseLoading
  ? t("dashboard.notifications.processing")
  : t("friends.actions.accept")}
    </button>

    <button
      type="button"
      className="vq-notification-action-secondary"
      disabled={challengeResponseLoading}
      onClick={() =>
        handleRespondToChallenge(selectedNotification.challengeId, "decline")
      }
      style={{
        width: "100%",
        height: "35px",
        borderRadius: "12px",
        background: "rgba(255,255,255,0.08)",
        color: "white",
        fontWeight: 1000,
        letterSpacing: 1,
        textTransform: "uppercase",
        cursor: challengeResponseLoading ? "default" : "pointer",
        opacity: challengeResponseLoading ? 0.7 : 1,
      }}
    >
     {t("friends.actions.decline")}
    </button>
  </div>
)}


{friendRequestAction === "accepted" && (
  <div
    style={{
      marginTop: "18px",
      textAlign: "center",
      fontWeight: 1000,
      letterSpacing: 1,
      textTransform: "uppercase",
      color: "#fff",
      fontSize: "14px",
    }}
  >
   {t("dashboard.notifications.friendRequestAccepted")}
  </div>
)}

{friendRequestAction === "declined" && (
  <div
    style={{
      marginTop: "18px",
      textAlign: "center",
      fontWeight: 1000,
      letterSpacing: 1,
      textTransform: "uppercase",
      color: "#fff",
      fontSize: "14px",
    }}
  >
    {t("dashboard.notifications.friendRequestDeclined")}
  </div>
)}





{friendRequestPending && !friendRequestAction && (
  <div
    style={{
      marginTop: "18px",
      display: "grid",
      gap: "10px",
    }}
  >
    <button
      type="button"
      className="vq-notification-action-primary"
      disabled={friendRequestResponseLoading}
      onClick={() =>
        handleRespondToFriendRequest(
          selectedNotification.fromUid,
          "accept"
        )
      }
      style={{
        width: "100%",
        height: "35px",
        borderRadius: "12px",
        background: "rgba(255,255,255,0.08)",
        color: "white",
        fontWeight: 1000,
        letterSpacing: 1,
        transform:
  friendRequestButtonPressed === "accept"
    ? "scale(1.03)"
    : "scale(1)",

border:
  friendRequestButtonPressed === "accept"
    ? "2px solid rgba(255,255,255,0.95)"
    : "2px solid rgba(255,255,255,0.28)",

boxShadow:
  friendRequestButtonPressed === "accept"
    ? "0 0 22px rgba(255,255,255,0.42), 0 0 28px rgba(0,255,132,0.28)"
    : "none",

transition: "all 0.2s ease",
        textTransform: "uppercase",
        cursor: friendRequestResponseLoading ? "default" : "pointer",
        opacity:
          friendRequestButtonPressed && friendRequestButtonPressed !== "accept"
            ? 0.35
            : 1,
      }}
    >
      {t("friends.actions.accept")}
    </button>

    <button
      type="button"
      className="vq-notification-action-secondary"
      disabled={friendRequestResponseLoading}
      onClick={() =>
        handleRespondToFriendRequest(
          selectedNotification.fromUid,
          "decline"
        )
      }
      style={{
        width: "100%",
        height: "35px",
        borderRadius: "12px",
        background: "rgba(255,255,255,0.08)",
        color: "white",
        fontWeight: 1000,
        letterSpacing: 1,
        transform:
  friendRequestButtonPressed === "decline"
    ? "scale(1.03)"
    : "scale(1)",

border:
  friendRequestButtonPressed === "decline"
    ? "2px solid rgba(255,255,255,0.95)"
    : "2px solid rgba(255,255,255,0.28)",

boxShadow:
  friendRequestButtonPressed === "decline"
    ? "0 0 22px rgba(255,255,255,0.42), 0 0 28px rgba(255,65,100,0.28)"
    : "none",

transition: "all 0.2s ease",
        textTransform: "uppercase",
        cursor: friendRequestResponseLoading ? "default" : "pointer",
        opacity:
          friendRequestButtonPressed && friendRequestButtonPressed !== "decline"
            ? 0.35
            : 1,
      }}
    >
      {t("friends.actions.decline")}
    </button>
  </div>
)}






            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}


{dueSurvey && !surveyBlockedByActiveMatch && (
  <SurveyModal
    survey={dueSurvey}
    busy={surveyBusy}
    onDefer={handleSurveyDefer}
    onSubmit={handleSurveySubmit}
  />
)}

{priorityNotification && (
  <div
    className="vq-priority-notification-backdrop"
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.65)",
      zIndex: 50000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
    }}
  >
    <div
      className={`vq-priority-notification-modal is-${getNotificationPresentation(priorityNotification).tone}`}
      style={{
        width: "100%",
        maxWidth: "420px",
        borderRadius: "22px",
        padding: "24px",
        background:
          "linear-gradient(135deg, #ff007f 0%, #7a2bff 100%)",
        color: "white",
        textAlign: "center",
        boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
      }}
    >
      {(priorityNotification?.type !== "presence_confirmation_required" || priorityPresenceUnavailable) && <button
        type="button"
        className="vq-priority-notification-close"
        onClick={async () => {
          if (priorityNotification?.type !== "presence_confirmation_required") {
            await markNotificationAsRead(priorityNotification);
          }
          setPriorityNotification(null);
          setPriorityPresenceActionUnavailable(false);
        }}
        aria-label={t("common.close")}
      >×</button>}
      <div className="vq-priority-notification-icon">{getNotificationPresentation(priorityNotification).icon}</div>
      <div
        className="vq-priority-notification-title"
        style={{
          fontSize: "22px",
          fontWeight: 1000,
          marginBottom: "12px",
          textTransform: "uppercase",
        }}
      >
        {priorityNotification.title}
      </div>

     <div
  className="vq-priority-notification-body"
  style={{
    fontSize: "15px",
    lineHeight: 1.6,
    marginBottom: "24px",
    whiteSpace: "pre-wrap",
  }}
>
  {priorityNotification.body || priorityNotification.message}
</div>

      {priorityNotification?.type === "presence_confirmation_required" && (
        <div style={{ margin: "-8px 0 20px" }}>
          <div style={{ marginBottom: 8, fontSize: "14px", fontWeight: 800 }}>
            {language === "en" ? "Time remaining:" : "Verbleibende Zeit:"}
          </div>
          <div
            aria-label={language === "en" ? "Presence confirmation time remaining" : "Verbleibende Zeit zur Anwesenheitsbestätigung"}
            style={{ fontSize: "34px", lineHeight: 1, fontWeight: 1000, letterSpacing: "0.06em" }}
          >
            {priorityPresenceCountdown.text}
          </div>
          {priorityPresenceUnavailable && (
            <div role="status" style={{ marginTop: 10, fontSize: "14px", lineHeight: 1.45, fontWeight: 800 }}>
              {language === "en"
                ? "The confirmation deadline has expired. The match status is being updated."
                : "Die Bestätigungsfrist ist abgelaufen. Der Matchstatus wird aktualisiert."}
            </div>
          )}
        </div>
      )}

      {priorityNotificationActionError && (
        <div role="alert" style={{ marginBottom: 12, color: "#ffd0d0", fontWeight: 800 }}>
          {priorityNotificationActionError}
        </div>
      )}

      <button
        type="button"
        className="vq-notification-action-primary"
        disabled={priorityNotificationActionLoading || (
          priorityNotification?.type === "presence_confirmation_required" && priorityPresenceUnavailable
        )}
        onClick={async () => {
          if (priorityNotification?.type === "presence_confirmation_required") {
            const clickCountdown = getPresencePriorityCountdown(
              priorityPresenceStatus?.deadlineMs,
              Date.now() + priorityPresenceServerOffsetMs
            );
            if (priorityPresenceUnavailable || clickCountdown.expired) {
              setPriorityPresenceNowMs(Date.now());
              setPriorityPresenceActionUnavailable(true);
              setPriorityNotificationActionError(
                language === "en"
                  ? "The confirmation deadline has expired."
                  : "Die Bestätigungsfrist ist abgelaufen."
              );
              return;
            }
            setPriorityNotificationActionLoading(true);
            setPriorityNotificationActionError("");
            try {
              const confirmPresence = httpsCallable(firebaseFunctions, "confirmPresenceForMatch");
              await confirmPresence(
                priorityNotification?.singleMatchId
                  ? { singleMatchId: priorityNotification.singleMatchId }
                  : {
                      tournamentId: priorityNotification?.tournamentId,
                      matchId: priorityNotification?.matchId,
                    }
              );
            } catch (error) {
              console.error("Priority presence confirmation failed:", error?.code || error?.message || "unknown");
              const rawError = `${error?.code || ""} ${error?.message || ""} ${error?.details || ""}`.toLowerCase();
              const noLongerActionable = rawError.includes("deadline") || rawError.includes("expired") ||
                rawError.includes("presence_not_active") || rawError.includes("not active") ||
                rawError.includes("already") || rawError.includes("conflict");
              if (noLongerActionable) {
                setPriorityPresenceActionUnavailable(true);
                setPriorityPresenceNowMs(Date.now());
              }
              setPriorityNotificationActionError(
                noLongerActionable
                  ? (language === "en"
                      ? "Presence can no longer be confirmed. The match status is being updated."
                      : "Die Anwesenheit kann nicht mehr bestätigt werden. Der Matchstatus wird aktualisiert.")
                  : (language === "en"
                      ? "Presence could not be confirmed. Please try again."
                      : "Die Anwesenheit konnte nicht bestätigt werden. Bitte versuche es erneut.")
              );
              setPriorityNotificationActionLoading(false);
              return;
            }
          }
          await markNotificationAsRead(priorityNotification);
          setPriorityNotification(null);
          setPriorityNotificationActionLoading(false);
          setPriorityNotificationActionError("");
          setPriorityPresenceActionUnavailable(false);
        }}
        style={{
          width: "100%",
          height: "44px",
          borderRadius: "12px",
          border: "none",
          background: "rgba(255,255,255,0.15)",
          color: "white",
          fontWeight: 1000,
          cursor: priorityNotificationActionLoading || (
            priorityNotification?.type === "presence_confirmation_required" && priorityPresenceUnavailable
          ) ? "default" : "pointer",
          opacity: priorityNotification?.type === "presence_confirmation_required" && priorityPresenceUnavailable ? 0.5 : 1,
        }}
      >
        {priorityNotificationActionLoading
          ? (language === "en" ? "CONFIRMING…" : "WIRD BESTÄTIGT…")
          : priorityNotification?.type === "presence_confirmation_required"
            ? (language === "en" ? "I AM PRESENT" : "ICH BIN ANWESEND")
            : t("dashboard.notifications.modalConfirm")}
      </button>
    </div>
  </div>
)}



  {/* LEAVE MODAL (Bestätigung) */}

  {leaveModalOpen && selectedLeaveTournament && (
    <div
      onClick={() => {
        setLeaveModalOpen(false);
        setSelectedLeaveTournament(null);
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "18px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "380px",
          borderRadius: "18px",
          overflow: "hidden",
          background: "linear-gradient(180deg, #f2e9ff 0%, #e7d6ff 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
          fontFamily:
            "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        }}
      >
        <div style={{ padding: "18px 18px 10px" }}>
          <div
            style={{
              fontWeight: 1000,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              fontSize: 18,
              color: "#000",
              marginBottom: 12,
            }}
          >
            {t("dashboard.leaveModal.title")}
          </div>

          <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 22 }}>🕒</div>
              <div style={{ fontWeight: 900, color: "#000" }}>
                {t("dashboard.leaveModal.time", { time: selectedLeaveTournament.time })}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 22 }}>🎮</div>
              <div style={{ fontWeight: 900, color: "#000" }}>
                {t("dashboard.leaveModal.players", {
  count: selectedLeaveTournament.maxPlayers,
})}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 14 }}>
            <button
              type="button"
              onClick={async () => {
                try {


const idToLeave =
  selectedLeaveTournament?.joinDocId ||
  selectedLeaveTournament?.id ||
  selectedTournamentId;


                  const ok = await leaveTournamentFirestore(idToLeave);
                  if (!ok) return;

                  setTournamentsSectionKey((k) => k + 1);
setHomeSection("TOURNAMENTS");


                  setLeaveModalOpen(false);
                  setSelectedLeaveTournament(null);
                  setSelectedTournamentId(null);
                  setView("LIST");
                } catch (e) {
                  console.error("LEAVE CLICK ERROR:", e?.code || e?.message || "unknown");
                }
              }}
              style={{
                flex: 1,
                border: "none",
                borderRadius: 12,
                padding: "12px 10px",
                fontWeight: 1000,
                letterSpacing: 1,
                background: "#18c964",
                color: "white",
                cursor: "pointer",
              }}
            >
              {t("common.yes")}
            </button>

            <button
              type="button"
              onClick={() => {
                setLeaveModalOpen(false);
                setSelectedLeaveTournament(null);
              }}
              style={{
                flex: 1,
                border: "none",
                borderRadius: 12,
                padding: "12px 10px",
                fontWeight: 1000,
                letterSpacing: 1,
                background: "#ff3b30",
                color: "white",
                cursor: "pointer",
              }}
            >
              {t("common.no")}
            </button>
          </div>
        </div>

        <div
          style={{
            padding: "14px 18px 18px",
            fontWeight: 900,
            letterSpacing: 1,
            textTransform: "uppercase",
            fontSize: 12,
            lineHeight: 1.35,
            color: "#ff3b30",
          }}
        >
          {t("dashboard.leaveModal.hint")}
        </div>
      </div>
    </div>
  )}

{/* RESULT MODAL (Elimination / Winner) */}
{resultModalOpen && (
  <TournamentResultModal
    variant={resultModalVariant}
    onClose={() => {
      if (typeof resultNextActionRef.current === "function") {
        resultNextActionRef.current();
        resultNextActionRef.current = null;
      } else {
        setResultModalOpen(false);
      }
    }}
  />
)}




	  {/* INFO MODAL */}
	  {infoModalOpen && (
	    <div
	      onClick={closeInfoModal}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 20000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "18px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "380px",
          borderRadius: "18px",
          overflow: "hidden",
          background: "linear-gradient(180deg, #f2e9ff 0%, #e7d6ff 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
          fontFamily:
            "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        }}
      >
        <div style={{ padding: "18px 18px 14px" }}>
          <div
            style={{
              fontWeight: 1000,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              fontSize: 18,
              color: "#000",
              marginBottom: 10,
            }}
          >
            {infoModalTitle}
          </div>

          <div
            style={{
              fontWeight: 900,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              fontSize: 12,
              lineHeight: 1.4,
              color: "#000",
              opacity: 0.9,
            }}
          >
            {infoModalText}
          </div>

	          <button
	            type="button"
	            onClick={() => {
	              closeInfoModal();
	              if (infoModalAccountAction) {
	                openAccountSettings();
	              }
	            }}
            style={{
              marginTop: 16,
              width: "100%",
              height: 54,
              border: "none",
              borderRadius: 12,
              background:
                "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)",
              color: "black",
              fontWeight: 1000,
              textTransform: "uppercase",
              letterSpacing: 1,
              cursor: "pointer",
            }}
          >
	           {infoModalAccountAction ? t("profileCompletion.button") : t("common.ok")}
	          </button>

        </div>
      </div>
    </div>
  )}

  {rulesModalOpen && (
  <div
    onClick={() => {
      setRulesModalOpen(false);
      setRulesCompetitionTermsOpen(false);
    }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 20500,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: "440px",
        maxHeight: "calc(100dvh - 36px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
        borderRadius: "18px",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        background: "linear-gradient(145deg, rgba(23,12,74,0.98), rgba(52,18,106,0.98))",
        border: "2px solid rgba(255,255,255,0.20)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div style={{ padding: "18px 18px 14px" }}>
        <CompetitionRulesView
          coreRules={tournamentRulesForLobbyView.coreRules}
          creatorRules={tournamentRulesForLobbyView.creatorRules}
          additionalRulesTitleKey={tournamentRulesForLobbyView.additionalRulesTitleKey}
          showCompetitionTerms={rulesCompetitionTermsOpen}
          onOpenCompetitionTerms={() => setRulesCompetitionTermsOpen(true)}
          onBackFromCompetitionTerms={() => setRulesCompetitionTermsOpen(false)}
        />

        <button
          type="button"
          onClick={() => {
            setRulesModalOpen(false);
            setRulesCompetitionTermsOpen(false);
          }}
          style={{
            marginTop: 16,
            width: "100%",
            height: 54,
            border: "none",
            borderRadius: 12,
            background:
              "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)",
            color: "white",
            fontWeight: 1000,
            textTransform: "uppercase",
            letterSpacing: 1,
            cursor: "pointer",
          }}
        >
          {t("common.close")}
        </button>
      </div>
    </div>
  </div>
)}



{supportModalOpen && (
  <div
    onClick={() => {
      if (!sendingSupport) setSupportModalOpen(false);
    }}
    style={{
      position: "fixed",
      inset: 0,
      background:
        "radial-gradient(circle at 50% 12%, rgba(255,0,127,0.18), transparent 34%), rgba(1,0,18,0.72)",
      zIndex: 21500,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: "392px",
        borderRadius: "24px",
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.14)",
        background:
          "linear-gradient(145deg, rgba(19,10,64,0.98) 0%, rgba(28,13,83,0.98) 48%, rgba(12,5,48,0.98) 100%)",
        boxShadow:
          "0 24px 70px rgba(0,0,0,0.58), 0 0 34px rgba(255,0,127,0.22), inset 0 1px 0 rgba(255,255,255,0.14)",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        position: "relative",
      }}
    >
      <style>
        {`
          .vq-support-field::placeholder {
            color: rgba(255,255,255,0.48);
          }
          .vq-support-field:focus {
            border-color: rgba(255,0,127,0.72) !important;
            box-shadow: 0 0 0 3px rgba(255,0,127,0.14), inset 0 1px 0 rgba(255,255,255,0.08) !important;
            outline: none;
          }
          .vq-support-action:active {
            transform: scale(0.98);
          }
        `}
      </style>
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(circle at 16% 0%, rgba(255,0,127,0.24), transparent 34%), radial-gradient(circle at 88% 16%, rgba(122,43,255,0.20), transparent 30%), linear-gradient(135deg, rgba(255,255,255,0.05) 0 1px, transparent 1px 18px)",
          opacity: 0.75,
        }}
      />
      <div style={{ padding: "24px 18px 18px", position: "relative" }}>
        <div
          style={{
            fontWeight: 1000,
            letterSpacing: 1,
            textTransform: "uppercase",
            fontSize: 22,
            color: "#fff",
            marginBottom: 4,
            textShadow: "0 0 18px rgba(255,0,127,0.26)",
          }}
        >
          {t("dashboard.support.modalTitle")}
        </div>
        <div
          style={{
            height: 4,
            width: 84,
            borderRadius: 999,
            background: "linear-gradient(90deg, #ff007f 0%, #a03cff 100%)",
            boxShadow: "0 0 18px rgba(255,0,127,0.34)",
            marginBottom: 18,
          }}
        />

        <input
          type="text"
          value={supportTitle}
          onChange={(e) => setSupportTitle(e.target.value)}
          placeholder={t("dashboard.support.titlePlaceholder")}
          maxLength={100}
          className="vq-support-field"
          style={{
            width: "100%",
            height: "48px",
            borderRadius: "14px",
            border: "1px solid rgba(255,255,255,0.16)",
            padding: "0 14px",
            boxSizing: "border-box",
            marginBottom: "12px",
            fontFamily: "inherit",
            background: "rgba(8,3,34,0.72)",
            color: "#fff",
            fontSize: 15,
            fontWeight: 800,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        />

        <textarea
          value={supportMessage}
          onChange={(e) => setSupportMessage(e.target.value)}
          placeholder={t("dashboard.support.messagePlaceholder")}
          rows={6}
          maxLength={2000}
          className="vq-support-field"
          style={{
            width: "100%",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.16)",
            padding: "14px",
            boxSizing: "border-box",
            resize: "none",
            fontFamily: "inherit",
            marginBottom: "16px",
            background: "rgba(8,3,34,0.72)",
            color: "#fff",
            fontSize: 15,
            fontWeight: 800,
            lineHeight: 1.35,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        />

        <div style={{ display: "flex", gap: 12 }}>
          <button
            type="button"
            disabled={sendingSupport}
            onClick={() => setSupportModalOpen(false)}
            className="vq-support-action"
            style={{
              flex: 1,
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: 16,
              padding: "13px 10px",
              fontWeight: 1000,
              letterSpacing: 1,
              textTransform: "uppercase",
              background:
                "linear-gradient(135deg, rgba(104,15,36,0.98) 0%, rgba(190,34,55,0.96) 100%)",
              color: "white",
              cursor: sendingSupport ? "not-allowed" : "pointer",
              opacity: sendingSupport ? 0.6 : 1,
              boxShadow: "0 10px 24px rgba(255,55,72,0.18), inset 0 1px 0 rgba(255,255,255,0.14)",
              transition: "transform 140ms ease, opacity 180ms ease",
            }}
          >
            {t("common.cancel")}
          </button>

          <button
            type="button"
            disabled={sendingSupport}
            onClick={handleSubmitSupportRequest}
            className="vq-support-action"
            style={{
              flex: 1,
              border: "1px solid rgba(255,255,255,0.24)",
              borderRadius: 16,
              padding: "13px 10px",
              fontWeight: 1000,
              letterSpacing: 1,
              textTransform: "uppercase",
              background: "linear-gradient(135deg, #ff007f 0%, #a03cff 100%)",
              color: "white",
              cursor: sendingSupport ? "not-allowed" : "pointer",
              opacity: sendingSupport ? 0.6 : 1,
              boxShadow: "0 10px 28px rgba(255,0,127,0.28), 0 0 20px rgba(160,60,255,0.18), inset 0 1px 0 rgba(255,255,255,0.20)",
              transition: "transform 140ms ease, opacity 180ms ease",
            }}
          >
            {sendingSupport ? t("dashboard.support.sending") : t("common.send")}
          </button>
        </div>
      </div>
    </div>
  </div>
)}


{emailChangeModalOpen && (
  <div
    onClick={() => {
      if (!requestingEmailChange) setEmailChangeModalOpen(false);
    }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 21800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: "420px",
        borderRadius: "18px",
        maxHeight: "calc(100dvh - 36px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
        overflowX: "hidden",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        background: "linear-gradient(180deg, #f2e9ff 0%, #e7d6ff 100%)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div style={{ padding: "18px 18px 16px" }}>
        <div
          style={{
            fontWeight: 1000,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            fontSize: 18,
            color: "#000",
            marginBottom: 10,
          }}
        >
          {t("menu.account.emailChange.modalTitle")}
        </div>

        <div
          style={{
            fontWeight: 850,
            fontSize: 13,
            lineHeight: 1.45,
            color: "#000",
            opacity: 0.9,
            marginBottom: 14,
            whiteSpace: "pre-line",
          }}
        >
          {t("menu.account.emailChange.modalText")}
        </div>

        <div
          style={{
            borderRadius: 14,
            background: "rgba(255,255,255,0.72)",
            color: "#000",
            padding: "10px 12px",
            fontWeight: 1000,
            fontSize: 13,
            lineHeight: 1.35,
            marginBottom: 12,
            wordBreak: "break-word",
          }}
        >
          {profile?.email || auth.currentUser?.email || "-"}
        </div>

        <label htmlFor="email-change-password" style={{ display: "block", color: "#000", fontWeight: 1000, fontSize: 13, marginBottom: 8 }}>
          {t("menu.account.emailChange.currentPassword")}
        </label>
        <input
          id="email-change-password"
          type="password"
          value={emailChangePassword}
          onFocus={resetDashboardHorizontalOffset}
          onChange={(e) => setEmailChangePassword(e.target.value)}
          placeholder={t("menu.account.emailChange.currentPassword")}
          autoComplete="current-password"
          style={{
            width: "100%",
            border: "2px solid rgba(0,0,0,0.12)",
            borderRadius: "14px",
            background: "rgba(255,255,255,0.8)",
            color: "#000",
            padding: "13px 12px",
            fontWeight: 900,
            fontSize: 16,
            outline: "none",
            boxSizing: "border-box",
            marginBottom: "12px",
          }}
        />

        <label htmlFor="email-change-new" style={{ display: "block", color: "#000", fontWeight: 1000, fontSize: 13, marginBottom: 8 }}>
          {t("menu.account.emailChange.newEmail")}
        </label>
        <input
          id="email-change-new"
          type="email"
          value={emailChangeNewEmail}
          onFocus={handleAccountKeyboardAwareFocus}
          onChange={(e) => setEmailChangeNewEmail(e.target.value)}
          placeholder={t("menu.account.emailChange.newEmail")}
          autoComplete="email"
          inputMode="email"
          style={{
            width: "100%",
            border: "2px solid rgba(0,0,0,0.12)",
            borderRadius: "14px",
            background: "rgba(255,255,255,0.8)",
            color: "#000",
            padding: "13px 12px",
            fontWeight: 900,
            fontSize: 16,
            outline: "none",
            boxSizing: "border-box",
            marginBottom: "12px",
          }}
        />

        <label htmlFor="email-change-confirm" style={{ display: "block", color: "#000", fontWeight: 1000, fontSize: 13, marginBottom: 8 }}>
          {t("menu.account.emailChange.confirmEmail")}
        </label>
        <input
          id="email-change-confirm"
          type="email"
          value={emailChangeConfirmEmail}
          onFocus={handleAccountKeyboardAwareFocus}
          onChange={(e) => setEmailChangeConfirmEmail(e.target.value)}
          placeholder={t("menu.account.emailChange.confirmEmail")}
          autoComplete="email"
          inputMode="email"
          style={{
            width: "100%",
            border: "2px solid rgba(0,0,0,0.12)",
            borderRadius: "14px",
            background: "rgba(255,255,255,0.8)",
            color: "#000",
            padding: "13px 12px",
            fontWeight: 900,
            fontSize: 16,
            outline: "none",
            boxSizing: "border-box",
            marginBottom: "14px",
          }}
        />

        <button
          type="button"
          onClick={handleRequestEmailChange}
          disabled={requestingEmailChange}
          style={{
            width: "100%",
            border: "none",
            borderRadius: "14px",
            padding: "14px 12px",
            fontWeight: 1000,
            fontSize: "15px",
            textAlign: "center",
            background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
            color: "white",
            cursor: requestingEmailChange ? "default" : "pointer",
            opacity: requestingEmailChange ? 0.7 : 1,
            textTransform: "uppercase",
          }}
        >
          {requestingEmailChange ? "..." : t("menu.account.emailChange.submit")}
        </button>

        {emailChangeMessage && (
          <div
            style={{
              marginTop: 12,
              color: emailChangeMessage === t("menu.account.emailChange.verificationSent") ? "#167a3a" : "#000",
              fontSize: 13,
              fontWeight: 900,
              lineHeight: 1.35,
              textAlign: "center",
              whiteSpace: "pre-line",
            }}
          >
            {emailChangeMessage}
          </div>
        )}

        <button
          type="button"
          disabled={requestingEmailChange}
          onClick={() => setEmailChangeModalOpen(false)}
          style={{
            width: "100%",
            marginTop: "12px",
            border: "none",
            borderRadius: "12px",
            padding: "12px 10px",
            fontWeight: 1000,
            background: "rgba(0,0,0,0.10)",
            color: "#000",
            cursor: requestingEmailChange ? "default" : "pointer",
            opacity: requestingEmailChange ? 0.6 : 1,
          }}
        >
          {t("common.close")}
        </button>
      </div>
    </div>
  </div>
)}


{dataExportModalOpen && (
  <div
    onClick={() => {
      if (!requestingDataExport) setDataExportModalOpen(false);
    }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 21800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: "420px",
        borderRadius: "18px",
        maxHeight: "calc(100dvh - 36px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
        overflowX: "hidden",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        background: "linear-gradient(180deg, #f2e9ff 0%, #e7d6ff 100%)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div style={{ padding: "18px 18px 16px" }}>
        <div
          style={{
            fontWeight: 1000,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            fontSize: 18,
            color: "#000",
            marginBottom: 10,
          }}
        >
          {t("menu.account.dataExport.modalTitle")}
        </div>

        <div
          style={{
            fontWeight: 850,
            fontSize: 14,
            lineHeight: 1.45,
            color: "#000",
            opacity: 0.9,
            marginBottom: 16,
            whiteSpace: "pre-line",
          }}
        >
          {t("menu.account.dataExport.modalText")}
        </div>

        {dataExportInProgress ? (
          <div
            style={{
              borderRadius: 14,
              background: "rgba(255,255,255,0.72)",
              color: "#000",
              padding: "13px 14px",
              fontWeight: 900,
              fontSize: 13,
              lineHeight: 1.4,
              marginBottom: 16,
            }}
          >
            {dataExportRetryText
              ? t("menu.account.dataExport.activeExistsWithRetry").replace("{{time}}", dataExportRetryText)
              : t("menu.account.dataExport.activeExists")}
          </div>
        ) : (
          <>
            {dataExportUsesApple ? (
              <div
                style={{
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.72)",
                  color: "#000",
                  padding: "13px 14px",
                  fontWeight: 900,
                  fontSize: 13,
                  lineHeight: 1.4,
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                {t("menu.account.dataExport.appleConfirmationHint")}
              </div>
            ) : !appleOnlyDataExportUnavailable ? <><label
              htmlFor="data-export-password"
              style={{
                display: "block",
                color: "#000",
                fontWeight: 1000,
                fontSize: 13,
                marginBottom: 8,
              }}
            >
              {t("menu.account.dataExport.passwordPlaceholder")}
            </label>

            <input
              id="data-export-password"
              type="password"
              value={dataExportPassword}
              onFocus={handleAccountKeyboardAwareFocus}
              onChange={(e) => setDataExportPassword(e.target.value)}
              placeholder={t("menu.account.dataExport.passwordPlaceholder")}
              autoComplete="current-password"
              style={{
                width: "100%",
                border: "2px solid rgba(0,0,0,0.12)",
                borderRadius: "14px",
                background: "rgba(255,255,255,0.8)",
                color: "#000",
                padding: "13px 12px",
                fontWeight: 900,
                fontSize: 16,
                outline: "none",
                boxSizing: "border-box",
                marginBottom: "12px",
              }}
            /></> : (
              <div
                style={{
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.72)",
                  color: "#000",
                  padding: "13px 14px",
                  fontWeight: 900,
                  fontSize: 13,
                  lineHeight: 1.4,
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                {t("menu.account.dataExport.iosRequired")}
              </div>
            )}

            <button
              type="button"
              onClick={handleRequestDataExport}
              disabled={requestingDataExport || appleOnlyDataExportUnavailable}
              style={{
                width: "100%",
                border: "none",
                borderRadius: "14px",
                padding: "14px 12px",
                fontWeight: 1000,
                fontSize: "15px",
                textAlign: "center",
                background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
                color: "white",
                cursor: requestingDataExport || appleOnlyDataExportUnavailable ? "default" : "pointer",
                opacity: requestingDataExport || appleOnlyDataExportUnavailable ? 0.7 : 1,
                textTransform: "uppercase",
              }}
            >
              {requestingDataExport
                ? "..."
                : dataExportUsesApple
                  ? t("menu.account.dataExport.confirmWithApple")
                  : t("menu.account.dataExport.requestButton")}
            </button>
          </>
        )}

        {dataExportMessage && (
          <div
            style={{
              marginTop: 12,
              color: dataExportMessage === t("menu.account.dataExport.requested") ? "#167a3a" : "#000",
              fontSize: 13,
              fontWeight: 900,
              lineHeight: 1.35,
              textAlign: "center",
            }}
          >
            {dataExportMessage}
          </div>
        )}

        <button
          type="button"
          disabled={requestingDataExport}
          onClick={() => setDataExportModalOpen(false)}
          style={{
            width: "100%",
            border: "none",
            borderRadius: 12,
            padding: "12px 10px",
            marginTop: 12,
            fontWeight: 1000,
            letterSpacing: 1,
            background: "rgba(0,0,0,0.1)",
            color: "#000",
            cursor: requestingDataExport ? "not-allowed" : "pointer",
            opacity: requestingDataExport ? 0.6 : 1,
          }}
        >
          {t("common.close")}
        </button>
      </div>
    </div>
  </div>
)}

{aiConsentPromptOpen && (
  <div
    onClick={() => {
      if (!savingAiConsent) setAiConsentPromptOpen(false);
    }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(2,0,18,0.68)",
      backdropFilter: "blur(6px)",
      zIndex: 21850,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-conflict-consent-title"
      style={{
        width: "100%",
        maxWidth: 480,
        borderRadius: 26,
        padding: "22px",
        boxSizing: "border-box",
        background:
          "radial-gradient(circle at 22% 0%, rgba(255,0,127,0.30), transparent 36%), radial-gradient(circle at 88% 0%, rgba(122,43,255,0.26), transparent 36%), linear-gradient(145deg, rgba(8,4,39,0.98) 0%, rgba(2,0,71,0.98) 100%)",
        border: "2px solid rgba(199,103,255,0.42)",
        boxShadow: "0 30px 80px rgba(0,0,0,0.58), 0 0 44px rgba(255,0,127,0.18)",
        color: "white",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      }}
    >
      <div
        id="ai-conflict-consent-title"
        style={{
          fontSize: "clamp(22px, 5vw, 30px)",
          fontWeight: 1000,
          lineHeight: 1.05,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          marginBottom: 14,
        }}
      >
        {t("aiConflictConsentPrompt.title")}
      </div>
      <div
        style={{
          display: "grid",
          gap: 12,
          color: "rgba(255,255,255,0.82)",
          fontSize: 15,
          lineHeight: 1.48,
          fontWeight: 750,
        }}
      >
        <p style={{ margin: 0 }}>{t("aiConflictConsentPrompt.text1")}</p>
        <p style={{ margin: 0 }}>{t("aiConflictConsentPrompt.text2")}</p>
        <p style={{ margin: 0 }}>
          {t("aiConflictConsentPrompt.text3BeforePrivacy")}{" "}
          <button
            type="button"
            onClick={() => {
              setAiConsentPromptOpen(false);
              openMenuHomeSection("DATENSCHUTZ");
            }}
            style={{
              border: "none",
              background: "transparent",
              color: "#ff4ed0",
              padding: 0,
              font: "inherit",
              fontWeight: 1000,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {t("register.privacy")}
          </button>
          {t("aiConflictConsentPrompt.text3AfterPrivacy")}
        </p>
      </div>

      {aiConsentMessage && (
        <div
          style={{
            marginTop: 14,
            color: "#ff6b7d",
            fontSize: 13,
            fontWeight: 900,
            lineHeight: 1.35,
          }}
        >
          {aiConsentMessage}
        </div>
      )}

      <div style={{ display: "grid", gap: 10, marginTop: 20 }}>
        <button
          type="button"
          onClick={() => saveAiConflictAnalysisConsent(true, { closePrompt: true })}
          disabled={savingAiConsent}
          style={{
            width: "100%",
            minHeight: 50,
            border: "2px solid rgba(255,255,255,0.34)",
            borderRadius: 16,
            background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
            color: "white",
            fontSize: 15,
            fontWeight: 1000,
            letterSpacing: 1,
            textTransform: "uppercase",
            cursor: savingAiConsent ? "default" : "pointer",
            opacity: savingAiConsent ? 0.7 : 1,
          }}
        >
          {savingAiConsent ? "..." : t("aiConflictConsentPrompt.agree")}
        </button>
        <button
          type="button"
          onClick={() => saveAiConflictAnalysisConsent(false, { closePrompt: true })}
          disabled={savingAiConsent}
          style={{
            width: "100%",
            minHeight: 48,
            border: "2px solid rgba(199,103,255,0.42)",
            borderRadius: 16,
            background: "rgba(255,255,255,0.06)",
            color: "rgba(255,255,255,0.86)",
            fontSize: 15,
            fontWeight: 1000,
            letterSpacing: 1,
            textTransform: "uppercase",
            cursor: savingAiConsent ? "default" : "pointer",
            opacity: savingAiConsent ? 0.7 : 1,
          }}
        >
          {t("aiConflictConsentPrompt.notNow")}
        </button>
      </div>
    </div>
  </div>
)}

{questRewardPromptOpen && (
  <div className="vq-quest-reward-backdrop" onClick={() => setQuestRewardPromptOpen(false)}>
    <div className="vq-quest-reward-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
      <button type="button" className="vq-quest-reward-close" onClick={() => setQuestRewardPromptOpen(false)} aria-label={t("common.close")}>×</button>
      <div className="vq-quest-reward-content">
        <div className="vq-quest-reward-main-icon"><QuestsIcon size={92} /></div>
        <div className="vq-quest-reward-eyebrow">{t("rewardPrompt.eyebrow")}</div>
        <div className="vq-quest-reward-title">
          {questRewardPromptRewards.length > 1
            ? t("rewardPrompt.titleMany").replace("{{count}}", String(questRewardPromptRewards.length))
            : t("rewardPrompt.titleOne")}
        </div>
        <div className="vq-quest-reward-description">
          {questRewardPromptRewards.length > 1
            ? t("rewardPrompt.textMany")
            : t("rewardPrompt.textOne")}
        </div>
        <div className="vq-quest-reward-list">
          {questRewardPromptDisplay.visibleRewards.map((reward) => (
            <div key={reward.claimId || reward.id} className="vq-quest-reward-row">
              <span className="vq-quest-reward-row-icon">{renderQuestRewardPromptIcon(reward.iconType)}</span>
              <span className="vq-quest-reward-row-copy"><strong>{getQuestRewardPromptName(reward, t)}</strong></span>
              <span className="vq-quest-reward-coins"><CoinIcon size={30} /><strong>+{reward.coins}</strong></span>
            </div>
          ))}
          {questRewardPromptDisplay.remainingCount > 0 && (
            <div className="vq-quest-reward-more">
              {t("rewardPrompt.more").replace("{{count}}", String(questRewardPromptDisplay.remainingCount))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="vq-quest-reward-primary"
          onClick={() => {
            setQuestRewardPromptOpen(false);
            setQuestRewardPromptRewards([]);
            setView("LIST");
            setHomeSection("COMMUNITY_REWARDS");
          }}
        >
          {t("rewardPrompt.cta")}
        </button>
        <button
          type="button"
          className="vq-quest-reward-secondary"
          onClick={() => {
            setQuestRewardPromptOpen(false);
            setQuestRewardPromptRewards([]);
          }}
        >
          {t("rewardPrompt.later")}
        </button>
      </div>
    </div>
  </div>
)}


{passwordResetModalOpen && (
  <div
    onClick={() => {
      if (!sendingPasswordReset) setPasswordResetModalOpen(false);
    }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 21800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: "380px",
        borderRadius: "18px",
        overflow: "hidden",
        background: "linear-gradient(180deg, #f2e9ff 0%, #e7d6ff 100%)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      }}
    >
      <div style={{ padding: "18px 18px 14px" }}>
        <div
          style={{
            fontWeight: 1000,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            fontSize: 18,
            color: "#000",
            marginBottom: 10,
          }}
        >
          {t("dashboard.passwordReset.modalTitle")}
        </div>

        <div
          style={{
            fontWeight: 900,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            fontSize: 12,
            lineHeight: 1.4,
            color: "#000",
            opacity: 0.9,
            marginBottom: 14,
          }}
        >
          {t("dashboard.passwordReset.modalText")}
        </div>

        <div
          style={{
            fontWeight: 1000,
            fontSize: 14,
            color: "#000",
            marginBottom: 16,
            wordBreak: "break-word",
          }}
        >
          {profile?.email || auth.currentUser?.email || "-"}
        </div>

        <div style={{ display: "flex", gap: 14 }}>
          <button
            type="button"
            disabled={sendingPasswordReset}
            onClick={() => setPasswordResetModalOpen(false)}
            style={{
              flex: 1,
              border: "none",
              borderRadius: 12,
              padding: "12px 10px",
              fontWeight: 1000,
              letterSpacing: 1,
              background: "#ff3b30",
              color: "white",
              cursor: sendingPasswordReset ? "not-allowed" : "pointer",
              opacity: sendingPasswordReset ? 0.6 : 1,
            }}
          >
            {t("common.cancel")}
          </button>

          <button
            type="button"
            disabled={sendingPasswordReset}
            onClick={handleSendPasswordReset}
            style={{
              flex: 1,
              border: "none",
              borderRadius: 12,
              padding: "12px 10px",
              fontWeight: 1000,
              letterSpacing: 1,
              background: "#18c964",
              color: "white",
              cursor: sendingPasswordReset ? "not-allowed" : "pointer",
              opacity: sendingPasswordReset ? 0.6 : 1,
            }}
          >
            {sendingPasswordReset ? t("dashboard.passwordReset.sending") : t("common.send")}
          </button>
        </div>
      </div>
    </div>
  </div>
)}



{/* KONTO LÖSCHEN */}
  {deleteAccountModalOpen && (
  <div
    onClick={() => {
      if (!deletingAccount) closeDeleteAccountModal();
    }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.74)",
      zIndex: 22000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "max(16px, env(safe-area-inset-top)) max(14px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(14px, env(safe-area-inset-left))",
      boxSizing: "border-box",
    }}
  >
    {!deleteAccountUsesApple && !appleLinkedDeleteUnavailable ? (
	      <div
	        onClick={(e) => e.stopPropagation()}
	        role="dialog"
	        aria-modal="true"
	        aria-labelledby="delete-account-modal-title"
	        className="vq-delete-account-modal"
	        style={{
          width: "min(100%, 740px)",
          maxHeight: "calc(100dvh - 28px)",
          overflowY: "auto",
          borderRadius: "clamp(24px, 4vw, 34px)",
          background:
            "radial-gradient(circle at 50% -8%, rgba(255, 68, 99, 0.22), transparent 34%), linear-gradient(145deg, rgba(34, 16, 72, 0.98) 0%, rgba(12, 10, 48, 0.98) 58%, rgba(8, 8, 35, 0.98) 100%)",
          border: "1.5px solid rgba(167, 82, 255, 0.42)",
          boxShadow: "0 28px 90px rgba(0,0,0,0.64), 0 0 42px rgba(155, 71, 255, 0.18)",
          color: "#fff",
          position: "relative",
          boxSizing: "border-box",
          fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        }}
      >
	        <button
	          type="button"
	          aria-label={t("common.close")}
	          disabled={deletingAccount}
	          onClick={closeDeleteAccountModal}
	          className="vq-delete-account-modal-close"
	          style={{
            position: "absolute",
            top: 20,
            right: 20,
            width: 48,
            height: 48,
            borderRadius: 14,
            border: "1px solid rgba(167, 116, 255, 0.28)",
            background: "rgba(39, 21, 82, 0.72)",
            color: "#b87cff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            cursor: deletingAccount ? "not-allowed" : "pointer",
            opacity: deletingAccount ? 0.55 : 1,
            zIndex: 2,
          }}
        >
          <FaTimes aria-hidden="true" />
        </button>

	        <div
	          className="vq-delete-account-modal-body"
	          style={{
	            padding: "clamp(22px, 4vw, 34px) clamp(16px, 4vw, 38px) clamp(22px, 3.5vw, 34px)",
	          }}
	        >
	          <div className="vq-delete-account-modal-header" style={{ textAlign: "center", paddingRight: 44 }}>
	            <div
	              aria-hidden="true"
	              className="vq-delete-account-modal-icon"
	              style={{
                width: 88,
                height: 88,
                margin: "0 auto 10px",
                borderRadius: 24,
                background: "radial-gradient(circle, rgba(255, 92, 116, 0.42), rgba(110, 19, 42, 0.24) 62%, transparent 70%)",
                color: "#ff7086",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                filter: "drop-shadow(0 0 24px rgba(255, 68, 99, 0.36))",
                fontSize: 42,
              }}
            >
              <FaTrashAlt />
            </div>
	            <h2
	              id="delete-account-modal-title"
	              className="vq-delete-account-modal-title"
	              style={{
                margin: "0 0 10px",
                fontSize: "clamp(26px, 4.2vw, 36px)",
                lineHeight: 1.05,
                fontWeight: 1000,
                letterSpacing: 1.6,
                textTransform: "uppercase",
              }}
            >
              {t("dashboard.deleteAccount.title")}
            </h2>
	            <p
	              className="vq-delete-account-modal-subtitle"
	              style={{
                margin: "0 0 22px",
                color: "rgba(255,255,255,0.72)",
                fontSize: "clamp(15px, 2.1vw, 18px)",
                lineHeight: 1.35,
                fontWeight: 700,
              }}
            >
              {renderInlineHighlight(
                t("dashboard.deleteAccount.text"),
                t("dashboard.deleteAccount.irreversibleHighlight"),
                { color: "#ff667e", fontWeight: 1000 }
              )}
            </p>
          </div>

	          <div
	            className="vq-delete-account-modal-warning"
	            style={{
              borderRadius: 16,
              border: "1px solid rgba(255, 69, 93, 0.32)",
              background: "linear-gradient(145deg, rgba(93, 24, 55, 0.52), rgba(58, 21, 68, 0.44))",
              boxShadow: "inset 0 0 32px rgba(255, 38, 92, 0.08)",
              padding: "clamp(16px, 2.5vw, 22px)",
              marginBottom: 20,
            }}
          >
	            <div
	              className="vq-delete-account-modal-warning-title"
	              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                color: "#ff687d",
                fontSize: 16,
                fontWeight: 1000,
                letterSpacing: 0.9,
                textTransform: "uppercase",
                marginBottom: 14,
              }}
            >
              <FaExclamationTriangle aria-hidden="true" />
              <span>{t("dashboard.deleteAccount.warningTitle")}</span>
            </div>
            {[
              [<FaRegUser />, t("dashboard.deleteAccount.warningProfile")],
              [<FaUserFriends />, t("dashboard.deleteAccount.warningFriends")],
              [<FaTrophy />, t("dashboard.deleteAccount.warningProgress")],
              [<FaCloudUploadAlt />, t("dashboard.deleteAccount.warningEvidence")],
              [<FaHistory />, t("dashboard.deleteAccount.warningIrreversible")],
            ].map(([icon, text]) => (
	              <div
	                key={text}
	                className="vq-delete-account-modal-warning-item"
	                style={{
                  display: "grid",
                  gridTemplateColumns: "28px 1fr",
                  gap: 14,
                  alignItems: "start",
                  color: "rgba(255,255,255,0.78)",
                  fontSize: "clamp(14px, 1.8vw, 17px)",
                  lineHeight: 1.38,
                  fontWeight: 750,
                  marginTop: 10,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    color: "#a66bff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    paddingTop: 2,
                  }}
                >
                  {icon}
                </span>
                <span>{text}</span>
              </div>
            ))}
          </div>

	          <label
	            htmlFor="delete-account-password"
	            className="vq-delete-account-modal-password-label"
	            style={{
              display: "block",
              color: "rgba(209, 198, 255, 0.78)",
              fontWeight: 1000,
              fontSize: 14,
              letterSpacing: 0.9,
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            {t("dashboard.deleteAccount.passwordLabel")}
          </label>
	          <div className="vq-delete-account-modal-password-wrap" style={{ position: "relative", marginBottom: 14 }}>
            <FaLock
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 20,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#a66bff",
                fontSize: 20,
                pointerEvents: "none",
              }}
            />
	            <input
	              id="delete-account-password"
              type={deleteAccountPasswordVisible ? "text" : "password"}
              value={deleteAccountPassword}
              onFocus={handleAccountKeyboardAwareFocus}
              onChange={(e) => setDeleteAccountPassword(e.target.value)}
              placeholder={t("dashboard.deleteAccount.passwordPlaceholder")}
              autoComplete="current-password"
	              disabled={deletingAccount}
	              className="vq-delete-account-modal-password-input"
	              style={{
                width: "100%",
                border: "1.5px solid rgba(139, 84, 255, 0.42)",
                borderRadius: "14px",
                background: "rgba(4, 6, 31, 0.76)",
                color: "#fff",
                padding: "16px 56px 16px 56px",
                fontWeight: 900,
                fontSize: 16,
                outline: "none",
                boxSizing: "border-box",
                boxShadow: "inset 0 0 22px rgba(52, 27, 112, 0.34)",
              }}
            />
            <button
              type="button"
              aria-label={
                deleteAccountPasswordVisible
                  ? t("dashboard.deleteAccount.hidePassword")
                  : t("dashboard.deleteAccount.showPassword")
              }
              disabled={deletingAccount}
              onClick={() => setDeleteAccountPasswordVisible((visible) => !visible)}
              style={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                color: "#a66bff",
                fontSize: 22,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: deletingAccount ? "not-allowed" : "pointer",
                opacity: deletingAccount ? 0.55 : 1,
              }}
            >
              {deleteAccountPasswordVisible ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
            </button>
          </div>

	          <label
	            className="vq-delete-account-modal-confirmation"
	            style={{
              display: "grid",
              gridTemplateColumns: "28px 1fr",
              gap: 14,
              alignItems: "start",
              color: "rgba(255,255,255,0.74)",
              fontSize: "clamp(14px, 2vw, 17px)",
              lineHeight: 1.45,
              fontWeight: 700,
              marginBottom: 18,
              cursor: deletingAccount ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={deleteAccountConfirmed}
              disabled={deletingAccount}
              onChange={(e) => setDeleteAccountConfirmed(e.target.checked)}
              aria-label={t("dashboard.deleteAccount.confirmation")}
              style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
            />
            <span
              aria-hidden="true"
              style={{
                width: 24,
                height: 24,
                borderRadius: 7,
                border: `2px solid ${deleteAccountConfirmed ? "#ff5f78" : "rgba(166, 107, 255, 0.48)"}`,
                background: deleteAccountConfirmed ? "linear-gradient(135deg, #ff2f6f, #8b28ff)" : "rgba(4, 6, 31, 0.45)",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 17,
                fontWeight: 1000,
                boxShadow: deleteAccountConfirmed ? "0 0 18px rgba(255, 47, 111, 0.28)" : "none",
              }}
            >
              {deleteAccountConfirmed ? "✓" : ""}
            </span>
            <span>
              {renderInlineHighlight(
                t("dashboard.deleteAccount.confirmation"),
                t("dashboard.deleteAccount.confirmationHighlight"),
                { color: "#ff667e", fontWeight: 1000 }
              )}
            </span>
          </label>

          {deleteAccountMessage && (
	            <div
	              role="alert"
	              className="vq-delete-account-modal-message"
	              style={{
                color: "#ff7588",
                fontSize: 14,
                fontWeight: 900,
                lineHeight: 1.35,
                marginBottom: 18,
                padding: "12px 14px",
                borderRadius: 12,
                background: "rgba(255, 56, 96, 0.12)",
                border: "1px solid rgba(255, 86, 118, 0.24)",
              }}
            >
              {deleteAccountMessage}
            </div>
          )}

	          <div
	            className="vq-delete-account-modal-actions"
	            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 14,
            }}
          >
	            <button
	              type="button"
	              disabled={deletingAccount}
	              onClick={closeDeleteAccountModal}
	              className="vq-delete-account-modal-action"
	              style={{
                minHeight: 62,
                border: "1.5px solid rgba(166, 107, 255, 0.5)",
                borderRadius: 14,
                padding: "14px 12px",
                fontWeight: 1000,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                background: "rgba(36, 20, 78, 0.66)",
                color: "#b995ff",
                cursor: deletingAccount ? "not-allowed" : "pointer",
                opacity: deletingAccount ? 0.6 : 1,
              }}
            >
              {t("dashboard.deleteAccount.cancel")}
            </button>
	            <button
	              type="button"
	              disabled={passwordDeleteButtonDisabled}
	              onClick={handleDeleteAccount}
	              className="vq-delete-account-modal-action"
	              style={{
                minHeight: 62,
                border: "1.5px solid rgba(255, 91, 109, 0.72)",
                borderRadius: 14,
                padding: "14px 12px",
                fontWeight: 1000,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                background: "linear-gradient(135deg, #8e1b32 0%, #d7334e 100%)",
                color: "#fff",
                cursor: passwordDeleteButtonDisabled ? "not-allowed" : "pointer",
                opacity: passwordDeleteButtonDisabled ? 0.5 : 1,
                boxShadow: passwordDeleteButtonDisabled ? "none" : "0 0 24px rgba(255, 58, 87, 0.28)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
              }}
            >
              <FaTrashAlt aria-hidden="true" />
              {deletingAccount
                ? t("dashboard.deleteAccount.deleting")
                : t("dashboard.deleteAccount.confirmDelete")}
            </button>
          </div>
        </div>
      </div>
    ) : (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "380px",
          borderRadius: "18px",
          overflow: "hidden",
          background: "linear-gradient(180deg, #f2e9ff 0%, #e7d6ff 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
          fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        }}
      >
        <div style={{ padding: "18px 18px 14px" }}>
          <div
            style={{
              fontWeight: 1000,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              fontSize: 18,
              color: "#000",
              marginBottom: 10,
            }}
          >
            {t("dashboard.deleteAccount.title")}
          </div>

          <div
            style={{
              fontWeight: 900,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              fontSize: 12,
              lineHeight: 1.4,
              color: "#000",
              opacity: 0.9,
              marginBottom: 14,
            }}
          >
            {t("dashboard.deleteAccount.text")}
          </div>

          <div
            style={{
              borderRadius: 14,
              background: "rgba(255,255,255,0.72)",
              color: "#000",
              padding: "13px 14px",
              fontWeight: 900,
              fontSize: 13,
              lineHeight: 1.4,
              marginBottom: 14,
              textAlign: "center",
            }}
          >
            {deleteAccountUsesApple
              ? t("dashboard.deleteAccount.appleConfirmationHint")
              : t("dashboard.deleteAccount.iosRequired")}
          </div>

          {deleteAccountMessage && (
            <div
              style={{
                color: "#9f0712",
                fontSize: 13,
                fontWeight: 900,
                lineHeight: 1.35,
                marginBottom: 12,
              }}
            >
              {deleteAccountMessage}
            </div>
          )}

          <div style={{ display: "flex", gap: 14 }}>
          <button
            type="button"
            disabled={deletingAccount || appleLinkedDeleteUnavailable}
            onClick={handleDeleteAccount}
            style={{
              flex: 1,
              border: "none",
              borderRadius: 12,
              padding: "12px 10px",
              fontWeight: 1000,
              letterSpacing: 1,
              background: "#ff3b30",
              color: "white",
              cursor: deletingAccount || appleLinkedDeleteUnavailable ? "not-allowed" : "pointer",
              opacity: deletingAccount || appleLinkedDeleteUnavailable ? 0.6 : 1,
            }}
          >
            {deletingAccount
              ? t("dashboard.deleteAccount.deleting")
              : deleteAccountUsesApple
                ? t("dashboard.deleteAccount.confirmWithApple")
                : t("common.yes")}
          </button>

          <button
            type="button"
            disabled={deletingAccount}
            onClick={closeDeleteAccountModal}
            style={{
              flex: 1,
              border: "none",
              borderRadius: 12,
              padding: "12px 10px",
              fontWeight: 1000,
              letterSpacing: 1,
              background: "#18c964",
              color: "white",
              cursor: deletingAccount ? "not-allowed" : "pointer",
              opacity: deletingAccount ? 0.6 : 1,
            }}
          >
           {t("common.no")}
          </button>
        </div>
      </div>
    </div>
    )}
  </div>
)}


{dailyBonusOpen && dailyBonusData && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.72)",
      zIndex: 23000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: "390px",
        borderRadius: "26px",
        overflow: "hidden",
        background: "linear-gradient(180deg, #070b16 0%, #0b1020 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 24px 70px rgba(0,0,0,0.55)",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
          opacity: 0.22,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "24px 22px 22px",
          textAlign: "center",
          color: "white",
          fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
        }}
      >
        <img
          src="/vaultquest-logo.png"
          alt="VaultQuest Logo"
          style={{
            width: "74px",
            height: "74px",
            objectFit: "contain",
            marginBottom: "18px",
          }}
        />

        <div
          style={{
            fontSize: "14px",
            fontWeight: 800,
            letterSpacing: "1.4px",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.72)",
            marginBottom: "14px",
          }}
        >
          {t("dashboard.dailyBonus.title")}
        </div>

        <div
          style={{
            fontSize: "46px",
            lineHeight: 1,
            fontWeight: 1000,
            letterSpacing: "0.5px",
            marginBottom: "18px",
          }}
        >
          {dailyBonusData?.coinsAdded || 100} {t("dashboard.dailyBonus.coins")}
        </div>

       <div
  style={{
    fontSize: "14px",
    lineHeight: 1.2,
    fontWeight: 800,
    color: "rgba(255,255,255,0.68)",
    marginTop: "-6px",
    marginBottom: "22px",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  }}
>
  {t("dashboard.dailyBonus.dayLabel", { day: dailyBonusData?.streakDays || 1 })}
</div>

       <button
  type="button"
  onClick={handleClaimDailyBonus}
  disabled={dailyBonusLoading}
  style={{
    width: "100%",
    height: "58px",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: "16px",
    background: "linear-gradient(90deg, #ff007f 0%, #7a2bff 100%)",
    color: "white",
    fontWeight: 1000,
    fontSize: "16px",
    letterSpacing: "0.8px",
    cursor: dailyBonusLoading ? "not-allowed" : "pointer",
    marginBottom: "16px",
    boxShadow: "0 10px 24px rgba(122,43,255,0.32)",
    opacity: dailyBonusLoading ? 0.7 : 1,
  }}
>
  {dailyBonusLoading
    ? t("dashboard.dailyBonus.collecting")
    : t("dashboard.dailyBonus.collect")}
</button>

        <div
          style={{
            fontSize: "13px",
            lineHeight: 1.45,
            color: "rgba(255,255,255,0.72)",
          }}
        >
          {t("dashboard.dailyBonus.disclaimer")}
        </div>
      </div>
    </div>
  </div>
)}






















  </React.Suspense>
</>
);
};

export default DashboardPage;
